/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
'use client';

import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { 
  BarChart3, 
  Users, 
  Calculator, 
  FileCheck, 
  Lock, 
  ChevronRight, 
  Info,
  Settings,
  Edit3,
  RefreshCw,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Calendar, 
  Bell,
  UserPlus, 
  Network,   
  GripVertical, 
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  UserCheck,
  MessageSquare,
  ExternalLink,
  Layout, 
  User,   
  UserCog,
  Sparkles, 
  Loader2,
  Target,
  TrendingUp,
  PieChart,
  Save,
  HelpCircle,
  Building2,
  Table,
  Upload,
  RefreshCcw,
  Eye,
  EyeOff,
  MoreHorizontal,
  Bot
} from 'lucide-react';
import { createStore, deleteStore as deleteStoreApi, fetchStoreList, type StoreItem } from '@/app/api-client/store-client';
import {
  createAccount,
  createPositionMaster,
  deletePositionMaster,
  deleteAccount,
  fetchAccountList,
  fetchAccountMasters,
  updatePositionMaster,
  updateAccount as updateAccountApi,
  type AccountItem,
  type PositionMasterItem,
  type RoleMasterItem,
} from '@/app/api-client/account-client';
import {
  createEvaluationEvaluator,
  deleteEvaluationEvaluator,
  fetchEvaluationLineBoard,
  removeEvaluationLineByEvaluatee,
  upsertEvaluationLine,
  type EvaluationLineBoardUserItem,
} from '@/app/api-client/evaluation-line-client';
import {
  fetchPerformanceItemList,
  replacePerformanceItemList,
  type PerformanceItem as PerformanceConfigItem,
} from '@/app/api-client/performance-item-client';
import {
  fetchBehaviorItemList,
  replaceBehaviorItemList,
  type BehaviorItem as BehaviorConfigItem,
  type BehaviorItemPositionCategory,
} from '@/app/api-client/behavior-item-client';
import {
  fetchBehaviorEvaluationByTarget,
  fetchBehaviorEvaluationOverview,
  saveEvaluatorBehaviorEvaluation,
  saveSelfBehaviorEvaluation,
} from '@/app/api-client/behavior-evaluation-client';
import {
  assignUserToStore,
  fetchStoreAssignmentBoard,
  type StoreAssignmentItem,
  type StoreAssignmentBoardUserItem,
  type StoreAssignmentBoardStoreItem,
  unassignUserFromStore,
} from '@/app/api-client/store-assignment-client';
import {
  fetchAdjustmentItemList,
  replaceAdjustmentItemList,
  type AdjustmentItem as AdjustmentConfigItem,
} from '@/app/api-client/adjustment-item-client';
import {
  fetchAdjustmentEvaluation,
  fetchStoreAdjustmentEvaluation,
  saveStoreAdjustmentEvaluation,
} from '@/app/api-client/adjustment-evaluation-client';
import {
  fetchFinalEvaluation,
  fetchFinalEvaluationMissingRequirementsBatch,
  fetchFinalEvaluationMissingRequirements,
  fetchFinalEvaluationSubmissions,
  generateFinalEvaluationAiComment,
  saveFinalEvaluationAiComment,
  saveFinalEvaluation,
} from '@/app/api-client/final-evaluation-client';
import { pushLineMessage } from '@/app/api-client/line-messaging-client';
import {
  fetchAdminDailyReports,
  fetchDailyReports,
  upsertDailyReport,
  type DailyReportItem,
} from '@/app/api-client/daily-report-client';
import {
  fetchStoreCrewEsScoreRows,
  fetchStoreQscRateRows,
  fetchStoreQscScoreRows,
  fetchStoreTargetRows,
  importStoreCrewEsPdfWithPeriod,
  importStoreQscPdfWithPeriod,
  importStoreQscRatePdfWithPeriod,
  importStoreTargetPdf,
  previewStoreCrewEsPdf,
  previewStoreQscRatePdf,
  previewStoreQscPdf,
  previewStoreTargetPdf,
  saveStoreTargetRows,
  type StoreHalfScoreRow,
  type StoreTargetRow,
} from '@/app/api-client/store-target-client';
import {
  fetchScheduleSettings,
  saveScheduleSettings,
  type AlertSetting,
  type ScheduleRow,
  type ScheduleSettings,
  type ScheduleQuarter,
} from '@/app/api-client/schedule-setting-client';
import {
  fetchRankMaster,
  saveRankMaster,
  type RankThresholds as RankThresholdItem,
} from '@/app/api-client/rank-master-client';
import { createSafrieSsoRedirectUrl } from '@/app/api-client/safrie-sso-client';
import StoreAssignmentBoardModal from '@/components/store-assignment-board-modal';
import AppToast, { type AppToastNotification } from '@/components/ui/app-toast';
import AppConfirmDialog from '@/components/ui/app-confirm-dialog';
import { supabaseClient } from '@/lib/supabase/client';

/**
 * デザイン定数
 */
const COLORS = {
  primary: '#6366f1',
  danger: '#ef4444',
  accent: '#f59e0b',
  bg: '#f8fafc',
  text: '#1e293b',
  border: '#e2e8f0',
  white: '#FFFFFF',
  adminHeader: '#2c3e50',
};

const ENABLE_APPROVAL_FINALIZE_TEST = process.env.NEXT_PUBLIC_ENABLE_APPROVAL_FINALIZE_TEST === 'true';
type PerformanceFixedKey =
  | 'sales_growth'
  | 'profit_growth'
  | 'monitor_check'
  | 'qsc_score'
  | 'crew_es';

const PERFORMANCE_FIXED_ITEM_KEYS = new Set<PerformanceFixedKey>(
  ['sales_growth', 'profit_growth', 'monitor_check', 'qsc_score', 'crew_es'],
);
const PERFORMANCE_RANGE_FIXED_POINTS = [4, 3, 2, 1, 0] as const;

function isFixedPerformanceKey(fixedKey: string | null | undefined): fixedKey is PerformanceFixedKey {
  if (!fixedKey) return false;
  return PERFORMANCE_FIXED_ITEM_KEYS.has(fixedKey as PerformanceFixedKey);
}

function resolvePerformanceFixedKey(
  fixedKey: string | null | undefined,
  itemName?: string,
): PerformanceFixedKey | null {
  if (isFixedPerformanceKey(fixedKey)) return fixedKey;
  if (!itemName) return null;
  if (itemName === '売上高 伸長率') return 'sales_growth';
  if (itemName === '経常利益 伸長率') return 'profit_growth';
  if (itemName === 'モニターチェック') return 'monitor_check';
  if (itemName === 'QSCスコア') return 'qsc_score';
  if (itemName === 'クルーESスコア') return 'crew_es';
  return null;
}

function getMaxTermNo<T extends { termNo: number }>(terms: T[]): number | null {
  if (terms.length === 0) return null;
  return terms.reduce((max, term) => (term.termNo > max ? term.termNo : max), terms[0].termNo);
}

function roundToOneDecimal(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

function formatOneDecimal(value: number): string {
  return roundToOneDecimal(value).toFixed(1);
}

function normalizePerformanceRangesToFixedPoints(
  ranges: { min: number; point: number }[],
) {
  const minByPoint = new Map<number, number>();
  ranges.forEach((range) => {
    const point = Number(range.point);
    const min = Number(range.min);
    if (!Number.isFinite(point) || !Number.isFinite(min)) return;
    if (!minByPoint.has(point)) {
      minByPoint.set(point, min);
    }
  });

  return PERFORMANCE_RANGE_FIXED_POINTS.map((point) => ({
    point,
    min: Number(minByPoint.get(point) ?? 0),
  }));
}

// Gemini API Key
const apiKey = "";
const TARGET_TERM_NO = 1;
const parsedUploadFileSizeMb = Number(process.env.NEXT_PUBLIC_UPLOAD_FILE_SIZE_MB ?? 50);
const uploadFileSizeMb =
  Number.isFinite(parsedUploadFileSizeMb) && parsedUploadFileSizeMb > 0
    ? parsedUploadFileSizeMb
    : 50;
const uploadFileSizeLimitBytes = Math.floor(uploadFileSizeMb * 1024 * 1024);

function getCurrentScheduleQuarter(baseDate = new Date()): ScheduleQuarter {
  const month = baseDate.getMonth() + 1;
  return month >= 11 || month <= 4 ? 'first_half' : 'second_half';
}

function formatDateYmdLocal(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addMonths(dateString: string, months: number) {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1 + months, day);
  return formatDateYmdLocal(date);
}

function addDays(dateString: string, days: number) {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return formatDateYmdLocal(date);
}

type PersonnelEvaluationSystemProps = {
  initialIsAdmin?: boolean;
  initialTab?: string;
  initialAdminUsersTab?: AdminUsersTabId;
  lockTab?: boolean;
  showRoleToggle?: boolean;
  displayUserName?: string;
  displayRoleName?: string;
  currentUserIdOverride?: string;
  authenticatedUserId?: string;
  onLogout?: () => Promise<void> | void;
};

type LegacyScheduleSettings = {
  evaluationInputPeriods?: Partial<Record<'q1' | 'q2', ScheduleSettings['evaluationInputPeriods']['first_half']>>;
  scheduleData?: Partial<Record<'q1' | 'q2', ScheduleRow[]>>;
  alertSettings?: Partial<Record<'q1' | 'q2', AlertSetting[]>>;
};

type AdminTargetDataTab = 'actual' | 'qsc' | 'qsc_rate' | 'crew_es';
type AdminUsersTabId =
  | 'store_assignment'
  | 'accounts'
  | 'evaluation_lines'
  | 'performance'
  | 'behavior'
  | 'adjustment'
  | 'rank_master';

type SubmittedEvaluationSnapshot = {
  submissionKey: string;
  userId: string;
  userName: string;
  department: string;
  role: string;
  termNo: number;
  period: ScheduleQuarter;
  isFinalized: boolean;
  performanceScore: number;
  behaviorScore: number;
  adjustmentScore: number;
  executiveAdjustmentScore: number;
  totalScore: number;
  rank?: string | null;
  submittedAt: string;
  hasSnapshot: boolean;
};
type SubmittedEvaluationScoreBreakdown = Pick<
  SubmittedEvaluationSnapshot,
  'performanceScore' | 'behaviorScore' | 'adjustmentScore' | 'executiveAdjustmentScore' | 'totalScore'
>;

type ApprovalDetailSection = 'summary' | 'performance' | 'behavior' | 'adjustment' | 'executive';
type ApprovalPerformanceDetailItem = {
  id: number;
  item: string;
  actual: string;
  weight: number;
  points: number;
  score: number;
};

type ApprovalBehaviorDetailItem = {
  id: number;
  item: string;
  def: string;
  weight: number;
  selfScore: number;
  evaluatorScore: number;
};

type ApprovalAdjustmentDetailItem = {
  id: number;
  category: 'plus' | 'minus';
  item: string;
  unitPoint: number;
  count: number;
  score: number;
};

// タブ定義
const EMPLOYEE_TABS = [
  { id: 'dashboard', label: 'ダッシュボード', icon: BarChart3 },
  { id: 'daily_report', label: '日報入力', icon: Edit3 },
  { id: 'performance', label: '店舗業績評価', icon: BarChart3 },
  { id: 'behavior', label: '行動・技能評価', icon: Users },
  { id: 'adjustment', label: '加点・減点', icon: Calculator },
];

const ADMIN_TABS = [
  { id: 'admin_reports', label: '日報管理', icon: MessageSquare },
  { id: 'admin_behavior_eval', label: '行動技能評価', icon: UserCheck },
  { id: 'admin_adjustment', label: '加点・減点入力', icon: Calculator },
  { id: 'admin_approvals', label: '評価承認', icon: FileCheck },
  { id: 'admin_targets', label: '店舗目標管理', icon: Table }, 
  { id: 'admin_schedule', label: 'スケジュール設定', icon: Calendar }, 
  { id: 'admin_users', label: 'マスタ管理', icon: Settings }, 
];

const TAB_ROUTE_MAP: Record<string, string> = {
  dashboard: '/dashboard',
  daily_report: '/daily-report',
  performance: '/performance',
  behavior: '/behavior',
  adjustment: '/adjustment',
  final: '/final',
  admin_schedule: '/admin/schedule',
  admin_users: '/admin/masters/store-assignment',
  admin_targets: '/admin/targets',
  admin_performance: '/admin/performance',
  admin_behavior_eval: '/admin/behavior-evaluation',
  admin_behavior: '/admin/behavior',
  admin_adjustment: '/admin/adjustment',
  admin_approvals: '/admin/approvals',
  admin_reports: '/admin/reports',
};

const ADMIN_USERS_TAB_ROUTE_MAP: Record<AdminUsersTabId, string> = {
  store_assignment: '/admin/masters/store-assignment',
  accounts: '/admin/masters/accounts',
  evaluation_lines: '/admin/masters/evaluation-lines',
  performance: '/admin/masters/performance',
  behavior: '/admin/masters/behavior',
  adjustment: '/admin/masters/adjustment',
  rank_master: '/admin/masters/rank-master',
};

const PERIOD_LIMITED_EMPLOYEE_TAB_IDS = new Set([
  'performance',
  'behavior',
  'adjustment',
]);

const PERFORMANCE_TYPE_OPTIONS = [
  { value: 'budget_ratio', label: '予算比' },
  { value: 'yoy_growth', label: '前年比' },
  { value: 'absolute', label: '絶対値' },
  { value: 'manual', label: '手動' },
];

const ROLE_OPTIONS = [
  { value: '一般社員', label: '一般社員' },
  { value: '店長', label: '店長' },
  { value: 'エリアマネージャー', label: 'エリアマネージャー' },
  { value: '人事管理者', label: '人事管理者' },
];

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined) {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

const resolveValidTabId = (tabId: string, adminMode: boolean) => {
  const allowedTabs = adminMode ? ADMIN_TABS : EMPLOYEE_TABS;
  if (allowedTabs.some((tab) => tab.id === tabId)) return tabId;
  return adminMode ? 'admin_reports' : 'dashboard';
};

const resolveTabIdByPathname = (pathname: string) => {
  const matchedTab = Object.entries(TAB_ROUTE_MAP).find(([, route]) => route === pathname)?.[0];
  if (matchedTab) return { tabId: matchedTab, adminUsersTab: null as AdminUsersTabId | null };

  const matchedAdminUsersTab = (
    Object.entries(ADMIN_USERS_TAB_ROUTE_MAP).find(([, route]) => route === pathname)?.[0] ??
    null
  ) as AdminUsersTabId | null;

  if (matchedAdminUsersTab) {
    return { tabId: 'admin_users', adminUsersTab: matchedAdminUsersTab };
  }

  return null;
};

type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

function MobileSelect({
  value,
  onChange,
  options,
  placeholder = '選択してください',
  disabled = false,
  selectClassName = '',
  buttonClassName = '',
  sheetTitle = '選択してください',
}: {
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  selectClassName?: string;
  buttonClassName?: string;
  sheetTitle?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const normalizedValue = value === undefined || value === null ? '' : String(value);
  const currentOption = options.find((opt) => String(opt.value) === normalizedValue);
  const displayLabel = currentOption ? currentOption.label : placeholder;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
        className={`md:hidden w-full flex items-center justify-between border rounded p-2 text-sm font-bold bg-white shadow-sm ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${buttonClassName}`}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown size={16} className="text-gray-400" />
      </button>
      <select
        value={normalizedValue}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`hidden md:block w-full border rounded p-2 text-sm font-bold bg-white shadow-sm ${selectClassName}`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl border-t border-gray-200 p-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-700">{sheetTitle}</span>
              <button type="button" onClick={() => setIsOpen(false)} className="p-2 rounded-md hover:bg-gray-100 text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2">
              {options.map((opt) => {
                const isActive = String(opt.value) === normalizedValue;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => {
                      if (!opt.disabled) onChange(String(opt.value));
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-lg border text-sm font-bold ${
                      isActive ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    } ${opt.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span>{opt.label}</span>
                    {isActive && <span className="text-indigo-600 text-base">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SectionTitle({
  children,
}: {
  children: React.ReactNode;
  isAdminMode?: boolean;
}) {
  return (
    <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 pb-2 border-b-2 flex items-center justify-between border-gray-400 text-gray-800">
      <span>{children}</span>
    </h2>
  );
}

function Card({
  children,
  highlight = false,
  className = '',
}: {
  children: React.ReactNode;
  highlight?: boolean;
  className?: string;
}) {
  return (
    <div className={`bg-white shadow-sm border p-4 sm:p-6 mb-4 sm:mb-6 rounded-lg ${highlight ? 'ring-2 ring-blue-100' : 'border-gray-200'} ${className}`}>
      {children}
    </div>
  );
}

function StatusBadge({ status, detail }: { status: string; detail?: string }) {
  const statusClassName =
    status === '確定済' || status === '承認済み'
      ? 'bg-indigo-500 text-white border-indigo-500'
      : status === '入力中'
        ? 'bg-white text-indigo-500 border-indigo-500'
        : status === '未開始'
          ? 'bg-amber-50 text-amber-700 border-amber-300'
          : 'bg-slate-100 text-slate-600 border-slate-300';
  return (
    <div className="flex flex-col items-end gap-1">
      <span className={`px-3 py-1 text-sm font-bold rounded-full border ${statusClassName}`}>
        {status}
      </span>
      {detail ? <span className="text-xs font-medium text-gray-500">{detail}</span> : null}
    </div>
  );
}

// ヘルパー関数: ポイント計算ロジック
const calculatePerformancePoint = (item) => {
  if (item.type === 'manual') return item.points;

  // 数値パース ("102.5%" -> 102.5)
  const parseValue = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    return parseFloat(val.toString().replace(/[%pt,]/g, ''));
  };

  const actualVal = parseValue(item.actual);
  
  // 評価レンジ判定
  if (item.ranges && item.ranges.length > 0) {
    // 基準値(min)の大きい順にソートして判定
    const sortedRanges = [...item.ranges].sort((a, b) => b.min - a.min);
    for (const range of sortedRanges) {
      if (actualVal >= range.min) {
        return range.point;
      }
    }
    return 0; // どのレンジにも当てはまらない場合
  }

  // 評価レンジがない場合のフォールバック（簡易）
  if (item.type === 'budget_ratio' || item.type === 'yoy_growth') {
      if (actualVal >= 105) return 4;
      if (actualVal >= 100) return 3;
      if (actualVal >= 95) return 2;
      if (actualVal >= 90) return 1;
      return 0;
  }
  
  return item.points; 
};

const calculateBehaviorWeightedScore = (score: number, weight: number) => {
  const normalizedScore = Number(score) / 4;
  return normalizedScore * Number(weight);
};

const calculateWeightedScoreByMax = (
  score: number,
  weight: number,
  maxScore: number,
) => {
  const safeMax = Number(maxScore) > 0 ? Number(maxScore) : 4;
  const normalizedScore = Number(score) / safeMax;
  return normalizedScore * Number(weight);
};

export default function PersonnelEvaluationSystem({
  initialIsAdmin = false,
  initialTab = 'dashboard',
  initialAdminUsersTab = 'store_assignment',
  lockTab = false,
  showRoleToggle = false,
  displayUserName,
  displayRoleName,
  currentUserIdOverride,
  authenticatedUserId,
  onLogout,
}: PersonnelEvaluationSystemProps) {
  const [isAdmin, setIsAdmin] = useState(initialIsAdmin);
  const [activeTab, setActiveTab] = useState(() => resolveValidTabId(initialTab, initialIsAdmin));
  const [isFinalized, setIsFinalized] = useState(false);
  const [finalizedScoreSnapshot, setFinalizedScoreSnapshot] = useState<{
    performanceScore: number;
    behaviorScore: number;
    adjustmentScore: number;
    executiveAdjustmentScore: number;
    totalScore: number;
  } | null>(null);
  const [finalizedRank, setFinalizedRank] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUserActionMenu, setShowUserActionMenu] = useState(false);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);
  const [isSafrieSsoLoading, setIsSafrieSsoLoading] = useState(false);
  const [behaviorViewMode, setBehaviorViewMode] = useState('both');

  // --- データ定義 ---
  const [departments, setDepartments] = useState<string[]>([]);

  const currentUserId = authenticatedUserId || currentUserIdOverride || '';
  const activeDisplayUserName = displayUserName || 'ログインユーザー';
  const activeDisplayRoleName = displayRoleName || '';
  const activeDisplayRoleBadgeName = activeDisplayRoleName || '権限未設定';
  const normalizedDisplayRoleName = String(activeDisplayRoleName ?? '').trim().toLowerCase();
  const isSystemAdminMode =
    showRoleToggle &&
    (activeDisplayRoleName === 'システム管理者' ||
      normalizedDisplayRoleName === 'system_admin' ||
      normalizedDisplayRoleName === 'super_admin');
  const canOpenSafrieSso = isAdmin || isSystemAdminMode;
  const activeDisplayRoleBadgeClass = isSystemAdminMode
    ? 'border-purple-100 bg-purple-50 text-purple-700'
    : isAdmin
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-indigo-100 bg-indigo-50 text-indigo-700';
  const [evaluatorIds, setEvaluatorIds] = useState<string[]>([]);
  const [evalRelations, setEvalRelations] = useState<Record<string, string[]>>({});

  const activeTargetYear = 2025;
  const [activeTargetTermNo, setActiveTargetTermNo] = useState(1); 
  const [targetTermOptions, setTargetTermOptions] = useState<{ termNo: number }[]>([]); 
  const [adminTargetDataTab, setAdminTargetDataTab] = useState<AdminTargetDataTab>('actual');
  const [storeQscScores, setStoreQscScores] = useState<StoreHalfScoreRow[]>([]);
  const [storeQscRateScores, setStoreQscRateScores] = useState<StoreHalfScoreRow[]>([]);
  const [storeCrewEsScores, setStoreCrewEsScores] = useState<StoreHalfScoreRow[]>([]);
  const [adminApprovalPeriod, setAdminApprovalPeriod] = useState<ScheduleQuarter>('first_half');
  const [adminBehaviorEvalTermNo, setAdminBehaviorEvalTermNo] = useState(TARGET_TERM_NO);
  const [adminBehaviorEvalPeriod, setAdminBehaviorEvalPeriod] =
    useState<ScheduleQuarter>(getCurrentScheduleQuarter());
  const [isAdminBehaviorEvalPeriodInitialized, setIsAdminBehaviorEvalPeriodInitialized] =
    useState(false);
  const [targetViewType, setTargetViewType] = useState('budgetSales');
  const [approvalActionLoadingKey, setApprovalActionLoadingKey] = useState<string | null>(null);
  const [finalizedYears, setFinalizedYears] = useState({});
  const [submittedEvaluations, setSubmittedEvaluations] = useState<SubmittedEvaluationSnapshot[]>([]);
  const [computedApprovalScoreMap, setComputedApprovalScoreMap] = useState<
    Record<string, SubmittedEvaluationScoreBreakdown>
  >({});
  const [approvalReadinessMap, setApprovalReadinessMap] = useState<Record<string, boolean>>({});
  const [approvalMissingItemsMap, setApprovalMissingItemsMap] = useState<Record<string, string[]>>(
    {},
  );
  const [isAdminApprovalLoading, setIsAdminApprovalLoading] = useState(false);
  const [selectedApprovalDetail, setSelectedApprovalDetail] =
    useState<SubmittedEvaluationSnapshot | null>(null);
  const [approvalDetailSection, setApprovalDetailSection] =
    useState<ApprovalDetailSection>('summary');
  const [isApprovalSectionLoading, setIsApprovalSectionLoading] = useState(false);
  const [approvalPerformanceDetail, setApprovalPerformanceDetail] =
    useState<ApprovalPerformanceDetailItem[] | null>(null);
  const [approvalBehaviorDetail, setApprovalBehaviorDetail] =
    useState<ApprovalBehaviorDetailItem[] | null>(null);
  const [approvalAdjustmentDetail, setApprovalAdjustmentDetail] =
    useState<ApprovalAdjustmentDetailItem[] | null>(null);
  const [adminStoreAdjustmentTermNo, setAdminStoreAdjustmentTermNo] = useState(TARGET_TERM_NO);
  const [adminStoreAdjustmentPeriod, setAdminStoreAdjustmentPeriod] =
    useState<ScheduleQuarter>(getCurrentScheduleQuarter());
  const [isAdminStoreAdjustmentPeriodInitialized, setIsAdminStoreAdjustmentPeriodInitialized] =
    useState(false);
  const [selectedAdminAdjustmentStoreId, setSelectedAdminAdjustmentStoreId] = useState<number | ''>('');
  const [adminStoreAdjustmentDetail, setAdminStoreAdjustmentDetail] =
    useState<ApprovalAdjustmentDetailItem[]>([]);
  const [isAdminStoreAdjustmentLoading, setIsAdminStoreAdjustmentLoading] = useState(false);
  const [isAdminStoreAdjustmentSaving, setIsAdminStoreAdjustmentSaving] = useState(false);
  const [selectedApprovalAiTarget, setSelectedApprovalAiTarget] =
    useState<SubmittedEvaluationSnapshot | null>(null);
  const [isApprovalAiCommentLoading, setIsApprovalAiCommentLoading] = useState(false);
  const [approvalAiComment, setApprovalAiComment] = useState({
    summary: '',
    strength: '',
    improvement: '',
  });
  const [approvalMissingRequirementModal, setApprovalMissingRequirementModal] = useState<{
    isOpen: boolean;
    userName: string;
    items: string[];
    isLoading: boolean;
    errorMessage: string;
  }>({
    isOpen: false,
    userName: '',
    items: [],
    isLoading: false,
    errorMessage: '',
  });
  const [dailyReports, setDailyReports] = useState<DailyReportItem[]>([]);
  const [dailyReportDraft, setDailyReportDraft] = useState({
    reportDate: '',
    todayWork: '',
    nextTasks: '',
  });
  const [dailyReportCalendarMonth, setDailyReportCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isDailyReportCalendarOpen, setIsDailyReportCalendarOpen] = useState(false);
  const [isAdminReportCalendarOpen, setIsAdminReportCalendarOpen] = useState(false);
  const [adminReportCalendarMonth, setAdminReportCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dailyReportHistoryVisibleCount, setDailyReportHistoryVisibleCount] = useState(5);
  const [reportFilterUserId, setReportFilterUserId] = useState('all');
  const [reportFilterDate, setReportFilterDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [selectedAdminReport, setSelectedAdminReport] = useState<DailyReportItem | null>(null);
  const [isDailyReportLoading, setIsDailyReportLoading] = useState(false);
  const [isDailyReportSaving, setIsDailyReportSaving] = useState(false);
  const [dailyReportErrorMessage, setDailyReportErrorMessage] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [storeErrorMessage, setStoreErrorMessage] = useState('');
  const [isStoreLoading, setIsStoreLoading] = useState(false);
  const [storeList, setStoreList] = useState<StoreItem[]>([]);
  const [showStoreAssignmentModal, setShowStoreAssignmentModal] = useState(false);
  const [storeAssignmentStoreList, setStoreAssignmentStoreList] = useState<
    StoreAssignmentBoardStoreItem[]
  >([]);
  const [selectedStoreForAssignment, setSelectedStoreForAssignment] =
    useState<StoreAssignmentBoardStoreItem | null>(null);
  const [storeAssignmentList, setStoreAssignmentList] = useState<StoreAssignmentItem[]>([]);
  const [selectedPerformanceStoreId, setSelectedPerformanceStoreId] = useState<number | 'average'>(
    'average',
  );
  const [selectedAdjustmentStoreId, setSelectedAdjustmentStoreId] = useState<number | 'average'>(
    'average',
  );
  const [storeAssignmentUserList, setStoreAssignmentUserList] = useState<
    StoreAssignmentBoardUserItem[]
  >([]);
  const [storeAssignmentErrorMessage, setStoreAssignmentErrorMessage] = useState('');
  const [isStoreAssignmentSaving, setIsStoreAssignmentSaving] = useState(false);
  const [evaluationInputPeriods, setEvaluationInputPeriods] = useState({
    first_half: { fromDate: '2025-11-01', toDate: '2026-04-30' },
    second_half: { fromDate: '2026-05-01', toDate: '2026-10-31' },
  });
  const showNavigation = !lockTab;

  // 画面入力中に親コンポーネントの再レンダーでタブ状態が再初期化されるのを防ぐ。
  // ルート遷移時はコンポーネント自体が再マウントされるため、ここでの同期は不要。

  const syncUrlByTab = (tabId: string) => {
    const nextRoute = TAB_ROUTE_MAP[tabId];
    if (!nextRoute || typeof window === 'undefined') return;
    if (window.location.pathname === nextRoute) return;
    window.history.pushState({}, '', nextRoute);
  };

  const syncUrlByAdminUsersTab = (tabId: AdminUsersTabId) => {
    const nextRoute = ADMIN_USERS_TAB_ROUTE_MAP[tabId];
    if (!nextRoute || typeof window === 'undefined') return;
    if (window.location.pathname === nextRoute) return;
    window.history.pushState({}, '', nextRoute);
  };

  const formatDateToLocalYmd = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const quarterMonthMap: Record<ScheduleQuarter, Set<number>> = {
    first_half: new Set([11, 12, 1, 2, 3, 4]),
    second_half: new Set([5, 6, 7, 8, 9, 10]),
  };

  const quarterMonthOrderMap: Record<ScheduleQuarter, number[]> = {
    first_half: [11, 12, 1, 2, 3, 4],
    second_half: [5, 6, 7, 8, 9, 10],
  };

  const quarterLabelMap: Record<ScheduleQuarter, string> = {
    first_half: '上期',
    second_half: '下期',
  };
  const quarterOptions = ['first_half', 'second_half'] as ScheduleQuarter[];
  const resolvePerformanceActualByMetrics = (
    fixedKey: PerformanceFixedKey | null,
    metrics: {
      salesRatio: number | null;
      profitRatio: number | null;
      qscScore: number | null;
      qscRateScore: number | null;
      crewEsScore: number | null;
    },
  ) => {
    if (fixedKey === 'sales_growth') return metrics.salesRatio !== null ? `${metrics.salesRatio}%` : '-';
    if (fixedKey === 'profit_growth') return metrics.profitRatio !== null ? `${metrics.profitRatio}%` : '-';
    if (fixedKey === 'monitor_check') return metrics.qscScore !== null ? `${metrics.qscScore}点` : '-';
    if (fixedKey === 'qsc_score') return metrics.qscRateScore !== null ? `${metrics.qscRateScore}%` : '-';
    if (fixedKey === 'crew_es') return metrics.crewEsScore !== null ? `${metrics.crewEsScore}点` : '-';
    return null;
  };
  const calculatePerformanceAveragePointByStores = (input: {
    item: {
      actual?: string | number | null;
      [key: string]: unknown;
    };
    fixedKey: PerformanceFixedKey | null;
    storeIds: number[];
    termNo: number;
    period: ScheduleQuarter;
  }) => {
    if (input.storeIds.length === 0) {
      return calculatePerformancePoint(input.item);
    }
    const totalPoint = input.storeIds.reduce((sum, storeId) => {
      const metrics = calculatePerformanceActualMetricsByStores({
        storeIds: [storeId],
        termNo: input.termNo,
        period: input.period,
      });
      const resolvedActual = resolvePerformanceActualByMetrics(input.fixedKey, metrics);
      const storeItem = {
        ...input.item,
        actual: resolvedActual ?? input.item.actual,
      };
      return sum + calculatePerformancePoint(storeItem);
    }, 0);
    return Number((totalPoint / input.storeIds.length).toFixed(1));
  };
  const calculatePerformanceActualMetricsByStores = (input: {
    storeIds: number[];
    termNo: number;
    period: ScheduleQuarter;
  }) => {
    if (input.storeIds.length === 0) {
      return {
        salesRatio: null as number | null,
        profitRatio: null as number | null,
        qscScore: null as number | null,
        qscRateScore: null as number | null,
        crewEsScore: null as number | null,
      };
    }
    const averageByStores = (resolver: (storeId: number) => number | null) => {
      const total = input.storeIds.reduce((sum, storeId) => sum + Number(resolver(storeId) ?? 0), 0);
      return Number((total / input.storeIds.length).toFixed(1));
    };
    const qscScore = averageByStores(
      (storeId) =>
        storeQscScores.find(
          (row) =>
            row.storeId === storeId && row.termNo === input.termNo && row.period === input.period,
        )?.score ?? null,
    );
    const qscRateScore = averageByStores(
      (storeId) =>
        storeQscRateScores.find(
          (row) =>
            row.storeId === storeId && row.termNo === input.termNo && row.period === input.period,
        )?.score ?? null,
    );
    const crewEsScore = averageByStores(
      (storeId) =>
        storeCrewEsScores.find(
          (row) =>
            row.storeId === storeId && row.termNo === input.termNo && row.period === input.period,
        )?.score ?? null,
    );
    const targetMonths = quarterMonthOrderMap[input.period];
    const sumValueByTermAndStore = (storeId: number, termNo: number, field: 'sales' | 'profit') =>
      targetMonths.reduce((sum, month) => {
        const row = employeePerformanceStoreTargets.find(
          (targetRow) =>
            targetRow.storeId === storeId &&
            targetRow.termNo === termNo &&
            targetRow.month === month,
        );
        return sum + Number(row?.[field] ?? 0);
      }, 0);
    const averageGrowthRatio = (field: 'sales' | 'profit') => {
      const totalRatio = input.storeIds.reduce((sum, storeId) => {
        const currentTotal = sumValueByTermAndStore(storeId, input.termNo, field);
        const previousTotal =
          input.termNo > 1 ? sumValueByTermAndStore(storeId, input.termNo - 1, field) : 0;
        const ratio = previousTotal > 0 ? (currentTotal / previousTotal) * 100 : 0;
        return sum + ratio;
      }, 0);
      return Number((totalRatio / input.storeIds.length).toFixed(1));
    };
    return {
      salesRatio: averageGrowthRatio('sales'),
      profitRatio: averageGrowthRatio('profit'),
      qscScore,
      qscRateScore,
      crewEsScore,
    };
  };

  const renderTermNavigator = ({
    termLabel,
    onPrev,
    onNext,
    disablePrev,
    disableNext,
    size = 'regular',
  }: {
    termLabel: string;
    onPrev: () => void;
    onNext: () => void;
    disablePrev?: boolean;
    disableNext?: boolean;
    size?: 'compact' | 'regular';
  }) => (
    <div className="inline-flex items-center rounded-xl border border-slate-400 bg-white p-1">
      <button
        type="button"
        onClick={onPrev}
        disabled={disablePrev}
        className={`inline-flex items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-35 disabled:cursor-not-allowed ${
          size === 'compact' ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'
        }`}
        aria-label="前の期"
      >
        <ChevronLeft size={size === 'compact' ? 13 : 14} />
      </button>
      <div
        className={`px-2 text-center font-extrabold text-slate-700 ${
          size === 'compact'
            ? 'min-w-[78px] text-sm'
            : 'min-w-[84px] md:min-w-[96px] text-base md:text-lg'
        }`}
      >
        {termLabel}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={disableNext}
        className={`inline-flex items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-35 disabled:cursor-not-allowed ${
          size === 'compact' ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'
        }`}
        aria-label="次の期"
      >
        <ChevronRight size={size === 'compact' ? 13 : 14} />
      </button>
    </div>
  );

  const renderQuarterToggle = ({
    activePeriod,
    onChange,
    size = 'regular',
  }: {
    activePeriod: ScheduleQuarter;
    onChange: (period: ScheduleQuarter) => void;
    size?: 'compact' | 'regular';
  }) => (
    <div className="inline-flex items-center rounded-xl border border-slate-400 bg-slate-100 p-1">
      {quarterOptions.map((period) => {
        const isActive = activePeriod === period;
        return (
          <button
            key={period}
            type="button"
            onClick={() => onChange(period)}
            className={`rounded-lg font-bold transition-colors ${
              size === 'compact'
                ? 'px-2.5 py-1 text-[11px]'
                : 'px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm'
            } ${
              isActive ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:bg-slate-200'
            }`}
          >
            {quarterLabelMap[period]}
          </button>
        );
      })}
    </div>
  );

  const isDateWithinQuarterWindow = (quarter: ScheduleQuarter, dateString: string) => {
    if (!dateString) return false;
    const date = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(date.getTime())) return false;
    const month = date.getMonth() + 1;
    return quarterMonthMap[quarter].has(month);
  };

  const todayYmd = formatDateToLocalYmd(new Date());
  const isEvaluationInputWindowOpen = Object.values(evaluationInputPeriods).some((period) => {
    if (!period.fromDate || !period.toDate) return false;
    if (period.fromDate > period.toDate) return false;
    return period.fromDate <= todayYmd && todayYmd <= period.toDate;
  });
  const isEvaluationInputWindowOpenForPeriod = (period: ScheduleQuarter) => {
    const targetPeriod = evaluationInputPeriods[period];
    if (!targetPeriod?.fromDate || !targetPeriod?.toDate) return false;
    if (targetPeriod.fromDate > targetPeriod.toDate) return false;
    return targetPeriod.fromDate <= todayYmd && todayYmd <= targetPeriod.toDate;
  };
  const getDefaultEmployeeTab = () => (isEvaluationInputWindowOpen ? 'dashboard' : 'daily_report');

  const isEmployeeTabDisabledByRule = (tabId: string) => {
    if (isAdmin) return false;
    const isLockedByInputPeriod =
      PERIOD_LIMITED_EMPLOYEE_TAB_IDS.has(tabId) && !isEvaluationInputWindowOpen;
    return isLockedByInputPeriod;
  };

  const handleTabChange = (tabId) => {
    if (lockTab) return;
    if (isEmployeeTabDisabledByRule(tabId)) {
      if (PERIOD_LIMITED_EMPLOYEE_TAB_IDS.has(tabId) && !isEvaluationInputWindowOpen) {
        showNotification('このメニューは評価入力期間のみ利用できます。', 'error');
      }
      return;
    }
    setShowTargetImportModal(false);
    setShowStoreScorePreviewModal(false);
    setShowUserActionMenu(false);
    setShowMobileMenu(false);
    setActiveTab(tabId);
    syncUrlByTab(tabId);
  };

  const loadStoreMasterList = async () => {
    setIsStoreLoading(true);
    setStoreErrorMessage('');
    try {
      const storeList = await fetchStoreList();
      setStoreList(storeList);
      setDepartments(storeList.map((item) => item.storeName));
    } catch (error) {
      setStoreErrorMessage(
        error instanceof Error ? error.message : '店舗一覧の取得に失敗しました',
      );
    } finally {
      setIsStoreLoading(false);
    }
  };

  const isSystemAdminUser = (roleId?: number | string | null, roleName?: string | null) =>
    Number(roleId) === 3 || roleName === 'システム管理者';

  const loadAccountManagementData = async () => {
    setIsAccountLoading(true);
    setAccountErrorMessage('');
    try {
      const [accountData, accountMasterData] = await Promise.all([
        fetchAccountList(),
        fetchAccountMasters(),
      ]);
      setAccountList(
        accountData.filter((account) => !isSystemAdminUser(account.roleId, account.roleName)),
      );
      setRoleMasterList(
        accountMasterData.roles.filter((role) => !isSystemAdminUser(role.id, role.name)),
      );
      setPositionMasterList(accountMasterData.positions);
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : 'アカウント情報の取得に失敗しました',
      );
    } finally {
      setIsAccountLoading(false);
    }
  };

  const loadEvaluationLineData = async () => {
    setIsEvaluationLineLoading(true);
    setEvaluationLineErrorMessage('');
    try {
      const boardData = await fetchEvaluationLineBoard();
      const filteredUsers = boardData.users.filter(
        (user) => !isSystemAdminUser(user.roleId, user.roleName),
      );
      const allowedUserIdSet = new Set(filteredUsers.map((user) => user.id));
      setEvaluationLineUserList(filteredUsers);
      setEvaluatorIds(
        boardData.evaluators
          .map((item) => item.userId)
          .filter((userId) => allowedUserIdSet.has(userId)),
      );
      const nextRelations = boardData.relations.reduce<Record<string, string[]>>((acc, item) => {
        if (!allowedUserIdSet.has(item.evaluatorUserId)) return acc;
        if (!allowedUserIdSet.has(item.evaluateeUserId)) return acc;
        const current = acc[item.evaluatorUserId] ?? [];
        acc[item.evaluatorUserId] = [...current, item.evaluateeUserId];
        return acc;
      }, {});
      setEvalRelations(nextRelations);
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '評価ライン情報の取得に失敗しました',
      );
    } finally {
      setIsEvaluationLineLoading(false);
    }
  };

  const mapPerformanceConfigToUiData = (
    configItems: PerformanceConfigItem[],
    currentItems: typeof performanceData = [],
  ) => {
    const currentByFixedKey = new Map(
      currentItems
        .map((item) => [resolvePerformanceFixedKey(item.fixedKey, item.item), item] as const)
        .filter(([fixedKey]) => fixedKey !== null),
    );
    return configItems
      .filter((item) => item.item !== '営業利益 伸長率')
      .map((item) => {
        const resolvedFixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
        const currentItem = resolvedFixedKey ? currentByFixedKey.get(resolvedFixedKey) : undefined;
        return {
        ...currentItem,
        id: item.id,
        item: item.item,
        fixedKey: resolvedFixedKey,
        type: item.type,
        actual: currentItem?.actual ?? '',
        weight: item.weight,
        max: item.max,
        points: currentItem?.points ?? 0,
        ranges: normalizePerformanceRangesToFixedPoints(
          item.ranges.map((range) => ({
            min: Number(range.min),
            point: range.point,
          })),
        ),
      };
    });
  };

  const loadPerformanceConfigData = async () => {
    setIsPerformanceConfigLoading(true);
    setPerformanceConfigErrorMessage('');
    try {
      const configItems = await fetchPerformanceItemList();
      if (configItems.length > 0) {
        setPerformanceData((prev) => mapPerformanceConfigToUiData(configItems, prev));
      }
    } catch (error) {
      setPerformanceConfigErrorMessage(
        error instanceof Error ? error.message : '業績項目設定の取得に失敗しました',
      );
    } finally {
      setIsPerformanceConfigLoading(false);
    }
  };

  const mapBehaviorConfigToUiData = (configItems: BehaviorConfigItem[]) =>
    configItems.map((item) => ({
      id: item.id,
      item: item.item,
      def: item.def,
      weight: item.weight,
      max: item.max,
      criteria: {
        0: item.criteria['0'] ?? '',
        1: item.criteria['1'] ?? '',
        2: item.criteria['2'] ?? '',
        3: item.criteria['3'] ?? '',
        4: item.criteria['4'] ?? '',
      },
      selfScore: 0,
      selfComment: '',
      evaluatorScore: 0,
      evaluatorComment: '',
    }));

  const mergeBehaviorEvaluationToUiData = (
    configItems: BehaviorConfigItem[],
    selfEvaluationItems = [],
    evaluatorEvaluationItems = [],
  ) => {
    const selfMap = new Map(
      selfEvaluationItems.map((item) => [item.behaviorItemId, item]),
    );
    const evaluatorMap = new Map(
      evaluatorEvaluationItems.map((item) => [item.behaviorItemId, item]),
    );

    return configItems.map((item) => {
      const selfItem = selfMap.get(item.id);
      const evaluatorItem = evaluatorMap.get(item.id);

      return {
        id: item.id,
        item: item.item,
        def: item.def,
        weight: item.weight,
        max: item.max,
        criteria: {
          0: item.criteria['0'] ?? '',
          1: item.criteria['1'] ?? '',
          2: item.criteria['2'] ?? '',
          3: item.criteria['3'] ?? '',
          4: item.criteria['4'] ?? '',
        },
        selfScore: Number(selfItem?.score ?? 0),
        selfComment: String(selfItem?.comment ?? ''),
        evaluatorScore: Number(evaluatorItem?.score ?? 0),
        evaluatorComment: String(evaluatorItem?.comment ?? ''),
      };
    });
  };

  const loadBehaviorConfigData = async (
    resetToConfig = false,
    options?: { positionCategory?: BehaviorItemPositionCategory; targetUserId?: string },
  ) => {
    setIsBehaviorConfigLoading(true);
    setBehaviorConfigErrorMessage('');
    try {
      const configItems = await fetchBehaviorItemList(options);
      if (resetToConfig) {
        setBehaviorData(mapBehaviorConfigToUiData(configItems));
      }
      return configItems;
    } catch (error) {
      setBehaviorConfigErrorMessage(
        error instanceof Error ? error.message : '行動項目設定の取得に失敗しました',
      );
      return [] as BehaviorConfigItem[];
    } finally {
      setIsBehaviorConfigLoading(false);
    }
  };

  const loadBehaviorEvaluationData = async (options?: {
    configItems?: BehaviorConfigItem[];
    targetUserId?: string;
  }) => {
    if (!isAdmin && !employeePeriodReady) return;
    if (!currentUserId || !activeEmployeeTermNo) return;
    const scopedTermNo =
      isAdmin && activeTab === 'admin_behavior_eval'
        ? adminBehaviorEvalTermNo
        : activeEmployeeTermNo;
    const scopedPeriod =
      isAdmin && activeTab === 'admin_behavior_eval'
        ? adminBehaviorEvalPeriod
        : activeEmployeePeriod;

    const evaluationTargetUserId =
      options?.targetUserId ??
      ((activeTab === 'behavior' && behaviorViewMode === 'evaluator') ||
      (isAdmin && activeTab === 'admin_behavior_eval')
        ? selectedTargetUserId
        : currentUserId);
    const configItems =
      options?.configItems && options.configItems.length > 0
        ? options.configItems
        : await loadBehaviorConfigData(false, { targetUserId: evaluationTargetUserId });

    if (configItems.length === 0) return;

    try {
      const isEvaluatorOnlyContext =
        (activeTab === 'behavior' && behaviorViewMode === 'evaluator') ||
        (isAdmin && activeTab === 'admin_behavior_eval');
      console.log('[employee_score_debug] behavior load start', {
        activeTab,
        behaviorViewMode,
        isAdmin,
        currentUserId,
        scopedTermNo,
        scopedPeriod,
        selectedTargetUserId,
        targetUserIdFromOptions: options?.targetUserId,
        isEvaluatorOnlyContext,
      });
      if (isEvaluatorOnlyContext) {
        const targetUserId = options?.targetUserId ?? selectedTargetUserId;
        if (!targetUserId || !isUuid(targetUserId) || !isUuid(currentUserId)) {
          // 対象未選択時に既存スコアを0へ戻さない（タブ切替時の見た目崩れ防止）
          console.log('[employee_score_debug] behavior load skipped (missing evaluator target)', {
            targetUserId,
            currentUserId,
          });
          setSelectedTargetIsFinalized(false);
          return;
        }

        const [overview, evaluation, targetFinalEvaluation] = await Promise.all([
          fetchBehaviorEvaluationOverview({
            userId: targetUserId,
            termNo: scopedTermNo,
            period: scopedPeriod,
          }),
          fetchBehaviorEvaluationByTarget({
            evaluatorUserId: currentUserId,
            targetUserId,
            termNo: scopedTermNo,
            period: scopedPeriod,
          }),
          fetchFinalEvaluation({
            userId: targetUserId,
            termNo: scopedTermNo,
            period: scopedPeriod,
          }),
        ]);
        setSelectedTargetIsFinalized(Boolean(targetFinalEvaluation?.isFinalized));

        setBehaviorData(
          (() => {
            const merged = mergeBehaviorEvaluationToUiData(
              configItems,
              overview.selfEvaluation?.items ?? [],
              evaluation.evaluatorEvaluation?.items ?? [],
            );
            console.log('[employee_score_debug] behavior evaluator loaded', {
              targetUserId,
              itemCount: merged.length,
              selfScores: merged.map((item) => ({
                id: item.id,
                item: item.item,
                selfScore: item.selfScore,
                weight: item.weight,
              })),
              evaluatorScores: merged.map((item) => ({
                id: item.id,
                item: item.item,
                evaluatorScore: item.evaluatorScore,
                weight: item.weight,
              })),
            });
            return merged;
          })(),
        );
        setBehaviorSupervisorUserId(overview.supervisorUserId ?? null);
        setHasSelfBehaviorEvaluation((overview.selfEvaluation?.items?.length ?? 0) > 0);
        setHasSupervisorBehaviorEvaluation((overview.supervisorEvaluation?.items?.length ?? 0) > 0);
        setOverallFeedback(evaluation.evaluatorEvaluation?.overallComment ?? '');
        if (typeof evaluation.evaluatorEvaluation?.rolePoint === 'number') {
          setEvaluatorRolePoint(evaluation.evaluatorEvaluation.rolePoint);
          writeRolePointCache(evaluation.evaluatorEvaluation.rolePoint);
        }
        return;
      }

      const overview = await fetchBehaviorEvaluationOverview({
        userId: currentUserId,
        termNo: scopedTermNo,
        period: scopedPeriod,
      });
      setSelectedTargetIsFinalized(false);
      setBehaviorSupervisorUserId(overview.supervisorUserId ?? null);
      setHasSelfBehaviorEvaluation((overview.selfEvaluation?.items?.length ?? 0) > 0);
      setHasSupervisorBehaviorEvaluation((overview.supervisorEvaluation?.items?.length ?? 0) > 0);

      setBehaviorData(
        (() => {
          const merged = mergeBehaviorEvaluationToUiData(
            configItems,
            overview.selfEvaluation?.items ?? [],
            overview.supervisorEvaluation?.items ?? [],
          );
          console.log('[employee_score_debug] behavior overview loaded', {
            userId: currentUserId,
            supervisorUserId: overview.supervisorUserId ?? null,
            selfCount: overview.selfEvaluation?.items?.length ?? 0,
            evaluatorCount: overview.supervisorEvaluation?.items?.length ?? 0,
            mergedCount: merged.length,
            mergedScores: merged.map((item) => ({
              id: item.id,
              item: item.item,
              selfScore: item.selfScore,
              evaluatorScore: item.evaluatorScore,
              weight: item.weight,
            })),
          });
          return merged;
        })(),
      );
      setOverallFeedback(overview.supervisorEvaluation?.overallComment ?? '');
      if (typeof overview.supervisorEvaluation?.rolePoint === 'number') {
        setEvaluatorRolePoint(overview.supervisorEvaluation.rolePoint);
        writeRolePointCache(overview.supervisorEvaluation.rolePoint);
      }
    } catch (error) {
      console.log('[employee_score_debug] behavior load error', error);
      showNotification(
        error instanceof Error ? error.message : '行動評価データの取得に失敗しました',
        'error',
      );
    }
  };

  const mapAdjustmentConfigToUiData = (configItems: AdjustmentConfigItem[]) => {
    const plusItems = configItems
      .filter((item) => item.category === 'plus')
      .map((item) => ({
        id: String(item.id),
        item: item.item,
        unitPoint: item.unitPoint,
        count: '',
      }));

    const minusItems = configItems
      .filter((item) => item.category === 'minus')
      .map((item) => ({
        id: String(item.id),
        item: item.item,
        unitPoint: item.unitPoint,
        count: '',
      }));

    return {
      plus: plusItems,
      minus: minusItems,
    };
  };

  const mergeAdjustmentEntriesToUiData = (
    configItems: AdjustmentConfigItem[],
    entries: { adjustmentItemId: number; count: number }[] = [],
  ) => {
    const entryCountByItemId = new Map(entries.map((entry) => [entry.adjustmentItemId, entry.count]));

    const plusItems = configItems
      .filter((item) => item.category === 'plus')
      .map((item) => ({
        id: String(item.id),
        item: item.item,
        unitPoint: item.unitPoint,
        count: entryCountByItemId.has(item.id) ? Number(entryCountByItemId.get(item.id) ?? 0) : '',
      }));

    const minusItems = configItems
      .filter((item) => item.category === 'minus')
      .map((item) => ({
        id: String(item.id),
        item: item.item,
        unitPoint: item.unitPoint,
        count: entryCountByItemId.has(item.id) ? Number(entryCountByItemId.get(item.id) ?? 0) : '',
      }));

    return {
      plus: plusItems,
      minus: minusItems,
    };
  };

  const loadAdjustmentConfigData = async () => {
    setIsAdjustmentConfigLoading(true);
    setAdjustmentConfigErrorMessage('');
    try {
      const configItems = await fetchAdjustmentItemList();
      setAdjustmentConfigItems(configItems);
      if (configItems.length > 0) {
        setAdjustments(mapAdjustmentConfigToUiData(configItems));
      }
      return configItems;
    } catch (error) {
      setAdjustmentConfigErrorMessage(
        error instanceof Error ? error.message : '加減点設定の取得に失敗しました',
      );
      return [] as AdjustmentConfigItem[];
    } finally {
      setIsAdjustmentConfigLoading(false);
    }
  };

  const loadAdjustmentEvaluationData = async (configItems?: AdjustmentConfigItem[]) => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    if (!currentUserId || !activeEmployeeTermNo) return;

    const masterItems =
      configItems && configItems.length > 0
        ? configItems
        : adjustmentConfigItems.length > 0
          ? adjustmentConfigItems
          : await loadAdjustmentConfigData();

    if (masterItems.length === 0) return;

    try {
      const result = await fetchAdjustmentEvaluation({
        userId: currentUserId,
        termNo: activeEmployeeTermNo,
        period: activeEmployeePeriod,
      });
      setAdjustments(mergeAdjustmentEntriesToUiData(masterItems, result.entries));
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '加減点データの取得に失敗しました',
        'error',
      );
    }
  };

  const loadScheduleSettingData = async (termNo?: number) => {
    setIsScheduleLoading(true);
    setScheduleErrorMessage('');
    try {
      const response = await fetchScheduleSettings(termNo);
      const legacyResponse = response as unknown as LegacyScheduleSettings;
      const isLegacyQuarterMode =
        !response.evaluationInputPeriods?.first_half &&
        !response.evaluationInputPeriods?.second_half &&
        (!!legacyResponse.evaluationInputPeriods?.q1 || !!legacyResponse.evaluationInputPeriods?.q2);
      const normalizedEvaluationInputPeriods = {
        first_half:
          response.evaluationInputPeriods?.first_half ??
          legacyResponse.evaluationInputPeriods?.q1 ??
          { fromDate: '2025-11-01', toDate: '2026-04-30' },
        second_half:
          response.evaluationInputPeriods?.second_half ??
          legacyResponse.evaluationInputPeriods?.q2 ??
          { fromDate: '2026-05-01', toDate: '2026-10-31' },
      };
      const normalizedScheduleData = {
        first_half:
          response.scheduleData?.first_half ?? legacyResponse.scheduleData?.q1 ?? [],
        second_half:
          response.scheduleData?.second_half ?? legacyResponse.scheduleData?.q2 ?? [],
      };
      const normalizedAlertSettings = {
        first_half:
          response.alertSettings?.first_half ?? legacyResponse.alertSettings?.q1 ?? [],
        second_half:
          response.alertSettings?.second_half ?? legacyResponse.alertSettings?.q2 ?? [],
      };

      setEvaluationInputPeriods(normalizedEvaluationInputPeriods);
      setScheduleData(normalizedScheduleData);
      setAlertSettings(normalizedAlertSettings);
      setScheduleKeyMode(isLegacyQuarterMode ? 'quarter' : 'half');
      setScheduleTermNo(response.termNo ?? TARGET_TERM_NO);
      setScheduleTermOptions([...(response.terms ?? [])].sort((a, b) => a.termNo - b.termNo));
    } catch (error) {
      setScheduleErrorMessage(
        error instanceof Error ? error.message : 'スケジュール設定の取得に失敗しました',
      );
    } finally {
      setIsScheduleLoading(false);
    }
  };

  const loadStoreAssignmentBoard = async () => {
    setStoreAssignmentErrorMessage('');
    try {
      const boardData = await fetchStoreAssignmentBoard();
      const filteredUsers = boardData.users.filter(
        (user) => !isSystemAdminUser(user.roleId, user.roleName),
      );
      const allowedUserIdSet = new Set(filteredUsers.map((user) => user.id));
      setStoreAssignmentStoreList(boardData.stores);
      setStoreAssignmentUserList(filteredUsers);
      setStoreAssignmentList(
        boardData.assignments.filter((assignment) => allowedUserIdSet.has(assignment.userId)),
      );
      setDepartments(boardData.stores.map((item) => item.storeName));
    } catch (error) {
      setStoreAssignmentErrorMessage(
        error instanceof Error ? error.message : '配属データの取得に失敗しました',
      );
    }
  };

  const loadStoreTargetData = async (termNo?: number) => {
    setIsStoreTargetLoading(true);
    setStoreTargetErrorMessage('');
    try {
      const result = await fetchStoreTargetRows(termNo);
      const resolvedTermNo = result.termNo ?? result.activeTermNo ?? termNo ?? 1;
      setStoreTargets(result.rows);
      setTargetTermOptions([...(result.terms ?? [])].sort((a, b) => a.termNo - b.termNo));
      setActiveTargetTermNo(resolvedTermNo);
    } catch (error) {
      setStoreTargets([]);
      setStoreTargetErrorMessage(
        error instanceof Error ? error.message : '店舗目標データの取得に失敗しました',
      );
    } finally {
      setIsStoreTargetLoading(false);
    }
  };

  const loadEmployeePerformanceStoreTargetData = async (termNo: number) => {
    const requestSeq = ++employeeTargetLoadSeqRef.current;
    setIsEmployeePerformanceTargetLoading(true);
    setHasEmployeePerformanceTargetLoaded(false);
    try {
      const requestTermNos = termNo > 1 ? [termNo - 1, termNo] : [termNo];
      const results = await Promise.all(
        requestTermNos.map((requestTermNo) => fetchStoreTargetRows(requestTermNo)),
      );
      if (requestSeq !== employeeTargetLoadSeqRef.current) return;
      setEmployeePerformanceStoreTargets(results.flatMap((result) => result.rows));
      setHasEmployeePerformanceTargetLoaded(true);
    } catch (error) {
      if (requestSeq !== employeeTargetLoadSeqRef.current) return;
      setEmployeePerformanceStoreTargets([]);
      setHasEmployeePerformanceTargetLoaded(true);
      showNotification(
        error instanceof Error ? error.message : '店舗業績データの取得に失敗しました',
        'error',
      );
    } finally {
      if (requestSeq === employeeTargetLoadSeqRef.current) {
        setIsEmployeePerformanceTargetLoading(false);
      }
    }
  };

  const loadStoreScoreData = async (termNo?: number) => {
    const requestSeq = ++storeScoreLoadSeqRef.current;
    setIsStoreScoreLoading(true);
    setHasStoreScoreLoaded(false);
    try {
      const [monitorCheckResult, qscRateResult, crewEsResult] = await Promise.all([
        fetchStoreQscScoreRows(termNo),
        fetchStoreQscRateRows(termNo),
        fetchStoreCrewEsScoreRows(termNo),
      ]);
      if (requestSeq !== storeScoreLoadSeqRef.current) return;
      setStoreQscScores(monitorCheckResult.rows);
      setStoreQscRateScores(qscRateResult.rows);
      setStoreCrewEsScores(crewEsResult.rows);
      setHasStoreScoreLoaded(true);
    } catch (error) {
      if (requestSeq !== storeScoreLoadSeqRef.current) return;
      setStoreQscScores([]);
      setStoreQscRateScores([]);
      setStoreCrewEsScores([]);
      setHasStoreScoreLoaded(true);
      showNotification(
        error instanceof Error ? error.message : 'モニターチェック/QSC/クルーESデータの取得に失敗しました',
        'error',
      );
    } finally {
      if (requestSeq === storeScoreLoadSeqRef.current) {
        setIsStoreScoreLoading(false);
      }
    }
  };

  const [storeTargets, setStoreTargets] = useState<StoreTargetRow[]>([]);
  const [employeePerformanceStoreTargets, setEmployeePerformanceStoreTargets] = useState<StoreTargetRow[]>([]);
  const [isStoreTargetLoading, setIsStoreTargetLoading] = useState(false);
  const [isStoreScoreLoading, setIsStoreScoreLoading] = useState(false);
  const [isEmployeePerformanceTargetLoading, setIsEmployeePerformanceTargetLoading] = useState(false);
  const [hasStoreScoreLoaded, setHasStoreScoreLoaded] = useState(false);
  const [hasEmployeePerformanceTargetLoaded, setHasEmployeePerformanceTargetLoaded] = useState(false);
  const [isStoreTargetSaving, setIsStoreTargetSaving] = useState(false);
  const [isStoreTargetImporting, setIsStoreTargetImporting] = useState(false);
  const [isStoreScoreImporting, setIsStoreScoreImporting] = useState<null | 'qsc' | 'qsc_rate' | 'crew_es'>(null);
  const [storeTargetErrorMessage, setStoreTargetErrorMessage] = useState('');
  const [storeTargetNoticeMessage, setStoreTargetNoticeMessage] = useState('');
  const [appNotification, setAppNotification] = useState<AppToastNotification | null>(null);
  const [isAppNotificationVisible, setIsAppNotificationVisible] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const [showImportMonthPicker, setShowImportMonthPicker] = useState(false);
  const [showTargetImportModal, setShowTargetImportModal] = useState(false);
  const [targetImportModalType, setTargetImportModalType] = useState<AdminTargetDataTab>('actual');
  const [showStoreScorePreviewModal, setShowStoreScorePreviewModal] = useState(false);
  const [pendingStoreScoreFiles, setPendingStoreScoreFiles] = useState<File[]>([]);
  const [pendingStoreScorePreviews, setPendingStoreScorePreviews] = useState<Array<{
    storeId: number;
    storeName: string;
    termNo: number;
    period: ScheduleQuarter;
    score: number;
    scoreDetail?: Record<string, unknown>;
    reportDate: string;
    type: 'qsc' | 'qsc_rate' | 'crew_es';
    fileName: string;
  }>>([]);
  const [storeScoreImportOverrideTermNo, setStoreScoreImportOverrideTermNo] = useState<number | ''>('');
  const [storeScoreImportOverridePeriod, setStoreScoreImportOverridePeriod] = useState<ScheduleQuarter | ''>('');
  const [pdfReportRef, setPdfReportRef] = useState<{ termNo: number; month: number } | null>(
    null,
  );
  const [importMonthOptions, setImportMonthOptions] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
  ]);
  const [selectedImportMonths, setSelectedImportMonths] = useState<number[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const qscFileInputRef = useRef<HTMLInputElement | null>(null);
  const qscRateFileInputRef = useRef<HTMLInputElement | null>(null);
  const crewEsFileInputRef = useRef<HTMLInputElement | null>(null);
  const dailyReportCalendarRef = useRef<HTMLDivElement | null>(null);
  const adminReportCalendarRef = useRef<HTMLDivElement | null>(null);
  const dailyReportInputCardRef = useRef<HTMLDivElement | null>(null);
  const mainScrollContainerRef = useRef<HTMLElement | null>(null);
  const preservedMainScrollTopRef = useRef(0);
  const performanceAutoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dashboardAiCommentRef = useRef<HTMLDivElement | null>(null);
  const storeScoreLoadSeqRef = useRef(0);
  const employeeTargetLoadSeqRef = useRef(0);
  const adjustmentStoreViewLoadSeqRef = useRef(0);
  const adminApprovalLoadSeqRef = useRef(0);
  const adminApprovalScoreLoadSeqRef = useRef(0);
  const adminApprovalReadinessSeqRef = useRef(0);
  const autoSnapshotSaveKeyRef = useRef('');

  const [selectedTargetUserId, setSelectedTargetUserId] = useState('');
  const [overallFeedback, setOverallFeedback] = useState('');
  const [evaluatorRolePoint, setEvaluatorRolePoint] = useState(0);
  const [isDashboardPerfChartAnimated, setIsDashboardPerfChartAnimated] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showFinalAiCommentModal, setShowFinalAiCommentModal] = useState(false);
  const [isFinalAiCommentLoading, setIsFinalAiCommentLoading] = useState(false);
  const [finalAiComment, setFinalAiComment] = useState({
    summary: '',
    strength: '',
    improvement: '',
  });
  const renderAiCommentWithBold = (text: string, boldClassName: string) =>
    text.split('\n').map((line, lineIndex) => {
      const chunks = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={`ai-line-${lineIndex}`} className="text-sm leading-7 whitespace-pre-wrap">
          {chunks.map((chunk, chunkIndex) => {
            const match = chunk.match(/^\*\*([^*]+)\*\*$/);
            if (match) {
              return (
                <strong
                  key={`ai-bold-${lineIndex}-${chunkIndex}`}
                  className={`font-black tracking-tight ${boldClassName}`}
                >
                  {match[1]}
                </strong>
              );
            }
            return <React.Fragment key={`ai-text-${lineIndex}-${chunkIndex}`}>{chunk}</React.Fragment>;
          })}
        </p>
      );
    });
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditDeleteConfirmModal, setShowEditDeleteConfirmModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false); 
  const [showEditPositionModal, setShowEditPositionModal] = useState(false);
  const [showCreatePositionModal, setShowCreatePositionModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    roleId: 2,
    positionId: null as number | null,
  });
  const [newDepartmentName, setNewDepartmentName] = useState(''); 
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    roleId: number;
    positionId: number | null;
  } | null>(null);
  const [editingPosition, setEditingPosition] = useState<{
    id: number;
    positionName: string;
  } | null>(null);
  const [draggingUserId, setDraggingUserId] = useState(null);
  const [showAddEvaluatorModal, setShowAddEvaluatorModal] = useState(false);
  const [alertDeleteTargetId, setAlertDeleteTargetId] = useState<number | null>(null);
  const [evaluatorDeleteTargetId, setEvaluatorDeleteTargetId] = useState<string | null>(null);
  const [storeDeleteTarget, setStoreDeleteTarget] = useState<StoreAssignmentBoardStoreItem | null>(null);
  const [genericConfirmState, setGenericConfirmState] = useState<{
    title: string;
    message: string;
    confirmText: string;
    tone: 'primary' | 'danger';
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const [selectedNewEvaluator, setSelectedNewEvaluator] = useState('');
  const [selectedEvaluatorIdForLineModal, setSelectedEvaluatorIdForLineModal] = useState<
    string | null
  >(null);
  const [evaluationLineUserList, setEvaluationLineUserList] = useState<
    EvaluationLineBoardUserItem[]
  >([]);
  const [evaluationLineErrorMessage, setEvaluationLineErrorMessage] = useState('');
  const [isEvaluationLineLoading, setIsEvaluationLineLoading] = useState(false);
  const [accountList, setAccountList] = useState<AccountItem[]>([]);
  const [roleMasterList, setRoleMasterList] = useState<RoleMasterItem[]>([]);
  const [positionMasterList, setPositionMasterList] = useState<PositionMasterItem[]>([]);
  const [newPositionDraft, setNewPositionDraft] = useState({
    positionName: '',
  });
  const [adminUserManagementTab, setAdminUserManagementTab] = useState<AdminUsersTabId>(
    initialAdminUsersTab,
  );
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const [accountErrorMessage, setAccountErrorMessage] = useState('');
  const [isPerformanceConfigLoading, setIsPerformanceConfigLoading] = useState(false);
  const [isPerformanceConfigSaving, setIsPerformanceConfigSaving] = useState(false);
  const [performanceConfigErrorMessage, setPerformanceConfigErrorMessage] = useState('');
  const [isBehaviorConfigLoading, setIsBehaviorConfigLoading] = useState(false);
  const [isBehaviorConfigSaving, setIsBehaviorConfigSaving] = useState(false);
  const [behaviorConfigErrorMessage, setBehaviorConfigErrorMessage] = useState('');
  const [isAdjustmentConfigLoading, setIsAdjustmentConfigLoading] = useState(false);
  const [isAdjustmentConfigSaving, setIsAdjustmentConfigSaving] = useState(false);
  const [adjustmentConfigErrorMessage, setAdjustmentConfigErrorMessage] = useState('');
  const [rankThresholds, setRankThresholds] = useState<RankThresholdItem[]>([]);

  const preserveMainScrollPosition = () => {
    if (!mainScrollContainerRef.current) return;
    preservedMainScrollTopRef.current = mainScrollContainerRef.current.scrollTop;
  };

  const restoreMainScrollPosition = () => {
    if (!mainScrollContainerRef.current) return;
    mainScrollContainerRef.current.scrollTop = preservedMainScrollTopRef.current;
  };

  const showNotification = (
    message: string,
    type: AppToastNotification['type'] = 'success',
  ) => {
    setAppNotification({ message, type });
  };

  const handleOpenSafrieSso = async () => {
    if (isSafrieSsoLoading || !canOpenSafrieSso) return;
    setShowUserActionMenu(false);
    setIsSafrieSsoLoading(true);
    const redirectWindow = window.open('about:blank', '_blank');
    try {
      if (!redirectWindow) {
        throw new Error('別タブを開けませんでした。ブラウザのポップアップ設定を確認してください。');
      }
      redirectWindow.opener = null;
      const { data } = await supabaseClient.auth.getSession();
      const accessToken = data.session?.access_token;
      if (!accessToken) {
        throw new Error('ログイン状態を確認できませんでした。再ログインしてください。');
      }
      const redirectUrl = await createSafrieSsoRedirectUrl(accessToken);
      redirectWindow.location.href = redirectUrl;
      setIsSafrieSsoLoading(false);
    } catch (error) {
      redirectWindow?.close();
      showNotification(
        error instanceof Error ? error.message : '修正依頼システムへの遷移に失敗しました。',
        'error',
      );
      setIsSafrieSsoLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadRankMaster = async () => {
      try {
        const result = await fetchRankMaster();
        if (!isMounted) return;
        setRankThresholds(result);
      } catch {
        if (!isMounted) return;
        setRankThresholds([]);
      }
    };
    void loadRankMaster();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleRankThresholdChange = (rank: string, value: string) => {
    const numeric = Number(value || 0);
    setRankThresholds((prev) =>
      prev.map((item) => (
        item.rank === rank
          ? { ...item, minScore: Number.isFinite(numeric) ? Math.max(0, numeric) : 0 }
          : item
      )),
    );
  };

  const handleSaveRankMaster = async () => {
    const sortedAsc = [...rankThresholds].sort((a, b) => a.minScore - b.minScore);
    const isValid = sortedAsc.every((item, index) => (
      index === 0 ? true : sortedAsc[index - 1].minScore <= item.minScore
    ));
    if (!isValid) {
      showNotification('しきい値の設定が不正です。', 'error');
      return;
    }
    try {
      await saveRankMaster(rankThresholds);
      showNotification('ランクを保存しました。', 'success');
    } catch (error) {
      showNotification(error instanceof Error ? error.message : 'ランクの保存に失敗しました', 'error');
    }
  };
  const moveToDashboardAiComment = () => {
    setActiveTab('dashboard');
    syncUrlByTab('dashboard');
    requestAnimationFrame(() => {
      setTimeout(() => {
        dashboardAiCommentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 120);
    });
  };

  const openGenericConfirm = (params: {
    title: string;
    message: string;
    confirmText?: string;
    tone?: 'primary' | 'danger';
    onConfirm: () => void | Promise<void>;
  }) => {
    setGenericConfirmState({
      title: params.title,
      message: params.message,
      confirmText: params.confirmText ?? '実行する',
      tone: params.tone ?? 'danger',
      onConfirm: params.onConfirm,
    });
  };

  const handleOpenUserModal = () => {
    preserveMainScrollPosition();
    setShowUserModal(true);
  };

  const handleOpenAddDepartmentModal = () => {
    preserveMainScrollPosition();
    setShowAddDepartmentModal(true);
  };
  const handleOpenCreatePositionModal = () => {
    preserveMainScrollPosition();
    setShowCreatePositionModal(true);
  };

  const handleOpenAddEvaluatorModal = () => {
    preserveMainScrollPosition();
    setShowAddEvaluatorModal(true);
  };

  const handleOpenEditUserModal = (account: AccountItem) => {
    preserveMainScrollPosition();
      setEditingUser({
        id: account.id,
        name: account.name,
        email: account.email,
        roleId: account.roleId,
        positionId: account.positionId,
      });
    setShowEditUserModal(true);
  };
  const handleOpenEditPositionModal = (position: PositionMasterItem) => {
    preserveMainScrollPosition();
    setEditingPosition({
      id: position.id,
      positionName: position.positionName,
    });
    setShowEditPositionModal(true);
  };

  const [performanceData, setPerformanceData] = useState([]);

  const [expandedPerformanceId, setExpandedPerformanceId] = useState(null); 

  const [behaviorData, setBehaviorData] = useState<ReturnType<typeof mapBehaviorConfigToUiData>>(
    [],
  );
  const [behaviorItemPositionCategory, setBehaviorItemPositionCategory] =
    useState<BehaviorItemPositionCategory>('general');
  const [isBehaviorEvaluationSaving, setIsBehaviorEvaluationSaving] = useState(false);
  const [behaviorSupervisorUserId, setBehaviorSupervisorUserId] = useState<string | null>(null);
  const [selectedTargetIsFinalized, setSelectedTargetIsFinalized] = useState(false);
  const [hasSelfBehaviorEvaluation, setHasSelfBehaviorEvaluation] = useState(false);
  const [hasSupervisorBehaviorEvaluation, setHasSupervisorBehaviorEvaluation] = useState(false);

  const [adjustments, setAdjustments] = useState({
    plus: [
      { id: 'p1', item: 'シフトヘルプ（他店応援）', count: 0, unitPoint: 5 },
      { id: 'p2', item: '社員紹介（採用）', count: 0, unitPoint: 10 },
    ],
    minus: [
      { id: 'm1', item: '本社クレーム発生', count: 0, unitPoint: -10 },
      { id: 'm2', item: '遅刻・勤怠不備', count: 0, unitPoint: -5 },
      { id: 'm3', item: '公休取得不足', count: 0, unitPoint: -5 },
    ]
  });
  const [adjustmentStoreViewData, setAdjustmentStoreViewData] = useState<{
    plus: Array<{ id: string; item: string; unitPoint: number; count: number | '' }>;
    minus: Array<{ id: string; item: string; unitPoint: number; count: number | '' }>;
  } | null>(null);
  const [isAdjustmentStoreViewLoading, setIsAdjustmentStoreViewLoading] = useState(false);
  const [adjustmentConfigItems, setAdjustmentConfigItems] = useState<AdjustmentConfigItem[]>([]);
  const [isAdjustmentSaving, setIsAdjustmentSaving] = useState(false);

  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isScheduleSaving, setIsScheduleSaving] = useState(false);
  const [scheduleErrorMessage, setScheduleErrorMessage] = useState('');
  const [scheduleTermNo, setScheduleTermNo] = useState(TARGET_TERM_NO);
  const [showDashboardPeriodModal, setShowDashboardPeriodModal] = useState(false);
  const [dashboardTermDraft, setDashboardTermDraft] = useState<number | ''>('');
  const [dashboardPeriodDraft, setDashboardPeriodDraft] = useState<ScheduleQuarter>('first_half');
  const [isDashboardHistoryLoading, setIsDashboardHistoryLoading] = useState(false);
  const [dashboardHistoryErrorMessage, setDashboardHistoryErrorMessage] = useState('');
  const [dashboardHistoryPreview, setDashboardHistoryPreview] = useState<{
    termNo: number;
    period: ScheduleQuarter;
    performanceScore: number;
    behaviorScore: number;
    adjustmentScore: number;
    executiveAdjustmentScore: number;
    totalScore: number;
    rank: string | null;
    aiSummary: string;
    aiStrength: string;
    aiImprovement: string;
  } | null>(null);
  const [dashboardHistoryPerformanceRows, setDashboardHistoryPerformanceRows] = useState<
    Array<{ id: number; item: string; score: number }>
  >([]);
  const [dashboardHistoryBehaviorRows, setDashboardHistoryBehaviorRows] = useState<
    Array<{ id: number; item: string; selfScore: number; evaluatorScore: number; weight: number }>
  >([]);
  const [scheduleTermOptions, setScheduleTermOptions] = useState<
    { termNo: number; startDate: string; endDate: string }[]
  >([]);
  const [scheduleKeyMode, setScheduleKeyMode] = useState<'half' | 'quarter'>('half');
  const [showScheduleItemModal, setShowScheduleItemModal] = useState(false);
  const [scheduleItemModalMode, setScheduleItemModalMode] = useState<'create' | 'edit'>('create');
  const [editingScheduleItemId, setEditingScheduleItemId] = useState<number | null>(null);
  const [scheduleItemForm, setScheduleItemForm] = useState({
    id: 1,
    item: '',
    date: '',
    note: '',
    alertEnabled: false,
    daysBefore: 3,
    sendTime: '09:00',
    message: '',
  });
  const [showEvaluationPeriodModal, setShowEvaluationPeriodModal] = useState(false);
  const [evaluationPeriodForm, setEvaluationPeriodForm] = useState({
    fromDate: '',
    toDate: '',
  });
  const [activeQuarter, setActiveQuarter] = useState<ScheduleQuarter>(getCurrentScheduleQuarter());
  const activeScheduleTerm = scheduleTermOptions.find((term) => term.termNo === scheduleTermNo);
  const activeScheduleBasePeriod = activeScheduleTerm
    ? activeQuarter === 'first_half'
      ? {
          fromDate: activeScheduleTerm.startDate,
          toDate: addDays(addMonths(activeScheduleTerm.startDate, 6), -1),
        }
      : {
          fromDate: addMonths(activeScheduleTerm.startDate, 6),
          toDate: activeScheduleTerm.endDate,
        }
    : null;
  const activeEmployeeTerm =
    activeScheduleTerm ??
    scheduleTermOptions.find((term) => term.startDate <= todayYmd && todayYmd <= term.endDate) ??
    scheduleTermOptions.find((term) => term.termNo === getMaxTermNo(scheduleTermOptions)) ??
    null;
  const storeScorePreviewSavePeriods = useMemo(() => {
    const labels = new Set<string>();
    pendingStoreScorePreviews.forEach((preview) => {
      labels.add(`第${preview.termNo}期 ${quarterLabelMap[preview.period]}`);
    });
    return Array.from(labels);
  }, [pendingStoreScorePreviews]);
  const activeEmployeeTermNo = activeEmployeeTerm?.termNo ?? scheduleTermNo;
  const activeEmployeePeriod = getCurrentScheduleQuarter(new Date());
  const currentTermNo = useMemo(() => {
    const terms =
      scheduleTermOptions.length > 0
        ? scheduleTermOptions
        : activeEmployeeTerm
          ? [activeEmployeeTerm]
          : [];
    if (terms.length === 0) return scheduleTermNo;
    const matched = terms.find((term) => term.startDate <= todayYmd && todayYmd <= term.endDate);
    if (matched) return matched.termNo;
    return getMaxTermNo(terms) ?? scheduleTermNo;
  }, [activeEmployeeTerm, scheduleTermNo, scheduleTermOptions, todayYmd]);
  const currentPeriodNow = getCurrentScheduleQuarter(new Date());
  const pastTermOptions = (
    scheduleTermOptions.length > 0
      ? scheduleTermOptions
      : [{ termNo: currentTermNo, startDate: '', endDate: '' }]
  ).filter((term) => term.termNo <= currentTermNo);
  const getSelectablePastPeriodsForTerm = (termNo: number | ''): ScheduleQuarter[] => {
    if (termNo === '') return [];
    if (termNo < currentTermNo) return ['first_half', 'second_half'];
    if (termNo > currentTermNo) return [];
    if (currentPeriodNow === 'second_half') return ['first_half'];
    return [];
  };
  const selectablePastPeriods = (() => {
    if (dashboardTermDraft === '') return [] as ScheduleQuarter[];
    return getSelectablePastPeriodsForTerm(dashboardTermDraft);
  })();
  const employeePeriodReady = !isScheduleLoading && !!activeScheduleTerm;
  const activeInputPeriodBySchedule = useMemo<ScheduleQuarter | null>(() => {
    const first = evaluationInputPeriods.first_half;
    if (first?.fromDate && first?.toDate && first.fromDate <= todayYmd && todayYmd <= first.toDate) {
      return 'first_half';
    }
    const second = evaluationInputPeriods.second_half;
    if (
      second?.fromDate &&
      second?.toDate &&
      second.fromDate <= todayYmd &&
      todayYmd <= second.toDate
    ) {
      return 'second_half';
    }
    return null;
  }, [evaluationInputPeriods, todayYmd]);
  const adminBehaviorEvalLockedTermNo = adminBehaviorEvalTermNo;
  const adminBehaviorEvalLockedPeriod = adminBehaviorEvalPeriod;
  const adminBehaviorEvalLockedPeriodRange = evaluationInputPeriods[adminBehaviorEvalLockedPeriod];
  const rolePointCacheKey = useMemo(
    () => `role_point:${currentUserId}:${activeEmployeeTermNo}:${activeEmployeePeriod}`,
    [activeEmployeePeriod, activeEmployeeTermNo, currentUserId],
  );
  const rolePointRequestKeyRef = useRef<string>('');
  const rolePointLoadedKeysRef = useRef<Set<string>>(new Set());
  const rolePointInFlightKeyRef = useRef<string | null>(null);
  const adminApprovalsInitializedRef = useRef(false);
  const writeRolePointCache = (value: number) => {
    if (typeof window === 'undefined') return;
    if (!Number.isFinite(value)) return;
    window.localStorage.setItem(rolePointCacheKey, String(value));
  };
  const readRolePointCache = () => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(rolePointCacheKey);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const previousActiveTabRef = useRef(activeTab);

  const [scheduleData, setScheduleData] = useState({
    first_half: [
      { id: 1, item: '評価対象期間 開始', date: '2025-11-01', note: '上期スタート' },
      { id: 2, item: '評価対象期間 終了', date: '2026-04-30', note: '' },
      { id: 3, item: '自己評価入力 開始', date: '2025-11-01', note: 'システム入力解禁' },
      { id: 4, item: '自己評価入力 締切', date: '2026-04-30', note: '' },
      { id: 5, item: '一次評価 締切', date: '2026-04-20', note: '' },
      { id: 6, item: '最終確定日', date: '2026-04-30', note: '給与反映' },
    ],
    second_half: [
      { id: 1, item: '評価対象期間 開始', date: '2026-05-01', note: '下期スタート' },
      { id: 2, item: '評価対象期間 終了', date: '2026-10-31', note: '' },
      { id: 3, item: '自己評価入力 開始', date: '2026-05-01', note: 'システム入力解禁' },
      { id: 4, item: '自己評価入力 締切', date: '2026-10-31', note: '' },
      { id: 5, item: '一次評価 締切', date: '2026-10-20', note: '' },
      { id: 6, item: '最終確定日', date: '2026-10-31', note: '賞与査定反映' },
    ],
  });
  const [alertSettings, setAlertSettings] = useState({
    first_half: [{ id: 1, enabled: true, itemId: 3, daysBefore: 3, sendTime: '09:00', message: '上期の評価入力期間がまもなく開始されます。ご準備をお願いいたします。' }],
    second_half: [{ id: 1, enabled: true, itemId: 3, daysBefore: 3, sendTime: '09:00', message: '下期の評価入力期間がまもなく開始されます。ご準備をお願いいたします。' }],
  });

  // --- 計算ロジック ---
  useEffect(() => {
    if (isAdmin || activeTab !== 'dashboard') return;
    setIsDashboardPerfChartAnimated(false);
    const timer = window.setTimeout(() => {
      setIsDashboardPerfChartAnimated(true);
    }, 40);
    return () => window.clearTimeout(timer);
  }, [activeTab, isAdmin, activeEmployeeTermNo, activeEmployeePeriod, performanceData]);

  useEffect(() => {
    setPerformanceData(prev => prev.map(item => ({
      ...item,
      points: calculatePerformancePoint(item)
    })));
  }, [performanceData.map(p => p.actual).join(','), performanceData.map(p => JSON.stringify(p.ranges)).join(',')]);

  const perfScore = useMemo(() => {
    const storeIds = Array.from(
      new Set(
        storeAssignmentList
          .filter((assignment) => assignment.userId === currentUserId)
          .map((assignment) => assignment.storeId),
      ),
    );
    return performanceData.reduce((sum, item) => {
      const fixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
      if (!fixedKey) {
        return sum + calculateWeightedScoreByMax(
          calculatePerformancePoint(item),
          item.weight,
          item.max,
        );
      }
      const averagedPoint = calculatePerformanceAveragePointByStores({
        item,
        fixedKey,
        storeIds,
        termNo: activeEmployeeTermNo,
        period: activeEmployeePeriod,
      });
      return sum + calculateWeightedScoreByMax(averagedPoint, item.weight, item.max);
    }, 0);
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    calculatePerformanceAveragePointByStores,
    currentUserId,
    performanceData,
    storeAssignmentList,
  ]);
  const perfMaxScore = useMemo(
    () =>
      performanceData.reduce(
        (acc, curr) =>
          acc + calculateWeightedScoreByMax(curr.max, curr.weight, curr.max),
        0,
      ),
    [performanceData],
  );
  const behaviorScore = useMemo(
    () =>
      behaviorData.reduce(
        (acc, curr) =>
          acc + calculateBehaviorWeightedScore(Number(curr.evaluatorScore || 0), Number(curr.weight || 0)),
        0,
      ),
    [behaviorData],
  );
  const adjustScore = useMemo(
    () =>
      adjustments.plus.reduce((acc, curr) => acc + (Number(curr.count || 0) * curr.unitPoint), 0) +
      adjustments.minus.reduce((acc, curr) => acc + (Number(curr.count || 0) * curr.unitPoint), 0),
    [adjustments],
  );
  const effectiveRolePoint = useMemo(() => Number(evaluatorRolePoint || 0), [evaluatorRolePoint]);
  const totalScore = perfScore + behaviorScore + adjustScore + effectiveRolePoint;
  const hasPerformanceEvaluation = useMemo(() => {
    const storeIds = Array.from(
      new Set(
        storeAssignmentList
          .filter((assignment) => assignment.userId === currentUserId)
          .map((assignment) => assignment.storeId),
      ),
    );
    const metrics = calculatePerformanceActualMetricsByStores({
      storeIds,
      termNo: activeEmployeeTermNo,
      period: activeEmployeePeriod,
    });
    return (
      performanceData.length > 0 &&
      performanceData.every((item) => {
        const fixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
        const resolvedActual = resolvePerformanceActualByMetrics(fixedKey, metrics);
        const actualText = String(resolvedActual ?? item.actual ?? '').trim();
        if (actualText === '') return false;
        if (actualText === '-' || actualText === 'ー' || actualText === '—') return false;
        return true;
      })
    );
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    currentUserId,
    performanceData,
    storeAssignmentList,
  ]);
  const hasAdjustmentEvaluation = useMemo(
    () =>
      adjustmentConfigItems.length > 0 &&
      [...adjustments.plus, ...adjustments.minus].length > 0 &&
      [...adjustments.plus, ...adjustments.minus].every(
        (item) => item.count !== '' && item.count !== null && item.count !== undefined,
      ),
    [adjustmentConfigItems.length, adjustments],
  );
  const isFinalEvaluationDataLoading =
    !isAdmin &&
    (isPerformanceConfigLoading ||
      isBehaviorConfigLoading ||
      isAdjustmentConfigLoading ||
      !currentUserId ||
      !activeEmployeeTermNo ||
      performanceData.length === 0 ||
      behaviorData.length === 0 ||
      adjustmentConfigItems.length === 0);
  const canSaveApprovalSnapshot = useMemo(
    () =>
      !isAdmin &&
      Boolean(currentUserId) &&
      Boolean(activeEmployeeTermNo) &&
      !isFinalized &&
      !isStoreScoreLoading &&
      !isEmployeePerformanceTargetLoading &&
      hasStoreScoreLoaded &&
      hasEmployeePerformanceTargetLoaded &&
      hasPerformanceEvaluation &&
      hasSelfBehaviorEvaluation &&
      Boolean(behaviorSupervisorUserId) &&
      hasSupervisorBehaviorEvaluation &&
      hasAdjustmentEvaluation,
    [
      activeEmployeeTermNo,
      behaviorSupervisorUserId,
      currentUserId,
      hasAdjustmentEvaluation,
      hasEmployeePerformanceTargetLoaded,
      hasPerformanceEvaluation,
      hasSelfBehaviorEvaluation,
      hasStoreScoreLoaded,
      hasSupervisorBehaviorEvaluation,
      isAdmin,
      isEmployeePerformanceTargetLoading,
      isFinalized,
      isStoreScoreLoading,
    ],
  );

  useEffect(() => {
    if (!canSaveApprovalSnapshot) return;
    const currentAccount = accountList.find((account) => account.id === currentUserId);
    const snapshotKey = [
      currentUserId,
      activeEmployeeTermNo,
      activeEmployeePeriod,
      perfScore.toFixed(3),
      behaviorScore.toFixed(3),
      adjustScore.toFixed(3),
      effectiveRolePoint.toFixed(3),
      totalScore.toFixed(3),
    ].join(':');
    if (autoSnapshotSaveKeyRef.current === snapshotKey) return;
    autoSnapshotSaveKeyRef.current = snapshotKey;
    const submissionKey = getEvaluationSubmissionKey(
      currentUserId,
      activeEmployeeTermNo,
      activeEmployeePeriod,
    );

    void saveFinalEvaluation({
      userId: currentUserId,
      termNo: activeEmployeeTermNo,
      period: activeEmployeePeriod,
      isFinalized: false,
      userName: currentAccount?.name ?? activeDisplayUserName,
      department: currentAccount?.department ?? '',
      role: currentAccount?.roleName ?? activeDisplayRoleName,
      performanceScore: perfScore,
      behaviorScore,
      adjustmentScore: adjustScore,
      executiveAdjustmentScore: effectiveRolePoint,
      totalScore,
      rank: null,
    })
      .then((result) => {
        setSubmittedEvaluations((prev) => {
          const nextRow: SubmittedEvaluationSnapshot = {
            submissionKey,
            userId: currentUserId,
            userName: currentAccount?.name ?? activeDisplayUserName,
            department: currentAccount?.department ?? '',
            role: currentAccount?.roleName ?? activeDisplayRoleName,
            termNo: activeEmployeeTermNo,
            period: activeEmployeePeriod,
            isFinalized: Boolean(result.saved.isFinalized),
            performanceScore: perfScore,
            behaviorScore,
            adjustmentScore: adjustScore,
            executiveAdjustmentScore: effectiveRolePoint,
            totalScore,
            rank: result.saved.rank ?? null,
            submittedAt: result.saved.finalizedAt ?? '',
            hasSnapshot: true,
          };
          const existingIndex = prev.findIndex((row) => row.submissionKey === submissionKey);
          if (existingIndex >= 0) {
            const next = [...prev];
            next[existingIndex] = nextRow;
            return next;
          }
          return [nextRow, ...prev];
        });
      })
      .catch(() => {
        autoSnapshotSaveKeyRef.current = '';
      });
  }, [
    accountList,
    activeDisplayRoleName,
    activeDisplayUserName,
    activeEmployeePeriod,
    activeEmployeeTermNo,
    adjustScore,
    behaviorScore,
    canSaveApprovalSnapshot,
    currentUserId,
    effectiveRolePoint,
    perfScore,
    totalScore,
  ]);

  // --- ハンドラ ---
  useEffect(() => {
    const isEvaluatorContext =
      behaviorViewMode === 'evaluator' ||
      (isAdmin && activeTab === 'admin_behavior_eval');
    if (!isEvaluatorContext) return;

    const subordinates = evalRelations[currentUserId] || [];
    const fallbackTargets =
      isAdmin && activeTab === 'admin_behavior_eval' && isSystemAdminMode
        ? evaluationLineUserList
            .filter((user) => !isSystemAdminUser(user.roleId, user.roleName))
            .map((user) => user.id)
            .filter((id) => id !== currentUserId && isUuid(id))
        : subordinates.filter((id) => isUuid(id));

    if (fallbackTargets.length > 0 && !selectedTargetUserId) {
      setSelectedTargetUserId(fallbackTargets[0]);
    } else if (
      fallbackTargets.length === 0 ||
      (selectedTargetUserId && !fallbackTargets.includes(selectedTargetUserId))
    ) {
      setSelectedTargetUserId('');
    }
  }, [behaviorViewMode, currentUserId, evalRelations, isAdmin, selectedTargetUserId, activeTab, isSystemAdminMode, evaluationLineUserList]);

  useEffect(() => () => {
    if (performanceAutoSaveTimerRef.current) {
      clearTimeout(performanceAutoSaveTimerRef.current);
    }
  }, []);

  useLayoutEffect(() => {
    if (isAdmin && activeTab === 'admin_schedule' && previousActiveTabRef.current !== 'admin_schedule') {
      setActiveQuarter(getCurrentScheduleQuarter());
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab, isAdmin]);

  useEffect(() => {
    if (isAdmin && activeTab === 'admin_users') {
      loadStoreMasterList();
      loadAccountManagementData();
      loadStoreAssignmentBoard();
      loadEvaluationLineData();
    }
    if (isAdmin && activeTab === 'admin_approvals') {
      loadAccountManagementData();
      loadStoreAssignmentBoard();
    }
    if (isAdmin && activeTab === 'admin_schedule') {
      loadScheduleSettingData();
    }
    if (isAdmin && activeTab === 'admin_adjustment') {
      loadStoreAssignmentBoard();
      loadScheduleSettingData();
    }
    if (isAdmin && activeTab === 'admin_behavior_eval') {
      loadEvaluationLineData();
      loadScheduleSettingData();
    }
    if (isAdmin && activeTab === 'admin_reports') {
      loadAccountManagementData();
    }
    if (!isAdmin && ['dashboard', 'performance', 'adjustment', 'final'].includes(activeTab)) {
      loadAccountManagementData();
    }
    if (activeTab === 'behavior' || (isAdmin && activeTab === 'admin_behavior_eval')) {
      loadEvaluationLineData();
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin) return;
    if (!currentUserId) return;
    loadAccountManagementData();
  }, [isAdmin, currentUserId]);

  useEffect(() => {
    if (
      (isAdmin &&
        ((activeTab === 'admin_performance') ||
          (activeTab === 'admin_users' && adminUserManagementTab === 'performance'))) ||
      (!isAdmin && ['performance', 'dashboard', 'final'].includes(activeTab))
    ) {
      loadPerformanceConfigData();
    }
  }, [isAdmin, activeTab, adminUserManagementTab]);

  useEffect(() => {
    if (!['behavior', 'dashboard', 'final', 'admin_behavior_eval'].includes(activeTab)) return;
    if (!isAdmin && !employeePeriodReady) return;

    const load = async () => {
      const evaluationTargetUserId =
        (activeTab === 'behavior' && behaviorViewMode === 'evaluator') ||
        activeTab === 'admin_behavior_eval'
          ? selectedTargetUserId
          : currentUserId;
      const configItems = await loadBehaviorConfigData(false, {
        targetUserId: evaluationTargetUserId,
      });
      await loadBehaviorEvaluationData({
        configItems,
        targetUserId:
          (activeTab === 'behavior' && behaviorViewMode === 'evaluator') ||
          activeTab === 'admin_behavior_eval'
            ? selectedTargetUserId
            : undefined,
      });
    };

    load();
  }, [
    adminBehaviorEvalPeriod,
    adminBehaviorEvalTermNo,
    activeEmployeePeriod,
    activeEmployeeTermNo,
    activeTab,
    behaviorViewMode,
    currentUserId,
    employeePeriodReady,
    isAdmin,
    selectedTargetUserId,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    loadScheduleSettingData();
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    setIsFinalized(false);
    setFinalizedScoreSnapshot(null);
    setFinalizedRank(null);
    if (!currentUserId || !activeEmployeeTermNo) return;

    const load = async () => {
      try {
        const result = await fetchFinalEvaluation({
          userId: currentUserId,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
        });
        setIsFinalized(result.isFinalized);
        setFinalizedScoreSnapshot(
          result.isFinalized
            ? {
                performanceScore: Number(result.performanceScore ?? 0),
                behaviorScore: Number(result.behaviorScore ?? 0),
                adjustmentScore: Number(result.adjustmentScore ?? 0),
                executiveAdjustmentScore: Number(result.executiveAdjustmentScore ?? 0),
                totalScore: Number(result.totalScore ?? 0),
              }
            : null,
        );
        setFinalizedRank(result.isFinalized ? (result.rank ?? null) : null);
        setFinalAiComment({
          summary: result.aiSummary || '',
          strength: result.aiStrength || '',
          improvement: result.aiImprovement || '',
        });
      } catch (error) {
        setIsFinalized(false);
        setFinalizedScoreSnapshot(null);
        setFinalizedRank(null);
        setFinalAiComment({ summary: '', strength: '', improvement: '' });
        showNotification(
          error instanceof Error ? error.message : '最終確定状態の取得に失敗しました',
          'error',
        );
      }
    };

    load();
  }, [activeEmployeePeriod, activeEmployeeTermNo, currentUserId, employeePeriodReady, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_approvals') {
      return;
    }
    loadFinalEvaluationSubmissions();
  }, [activeTab, isAdmin, activeTargetTermNo, adminApprovalPeriod]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_approvals') return;
    void hydrateApprovalComputedScores();
  }, [
    activeTab,
    isAdmin,
    activeTargetTermNo,
    adminApprovalPeriod,
    accountList,
    submittedEvaluations,
    approvalReadinessMap,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_approvals') return;
    void hydrateApprovalReadiness();
  }, [
    activeTab,
    isAdmin,
    activeTargetTermNo,
    adminApprovalPeriod,
    accountList,
    submittedEvaluations,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_approvals') return;
    if (adminApprovalsInitializedRef.current) return;
    if (scheduleTermOptions.length === 0) return;

    const currentDateTermNo =
      scheduleTermOptions.find((term) => term.startDate <= todayYmd && todayYmd <= term.endDate)
        ?.termNo ??
      getMaxTermNo(scheduleTermOptions) ??
      activeTargetTermNo;

    setActiveTargetTermNo(currentDateTermNo);
    setAdminApprovalPeriod(getCurrentScheduleQuarter(new Date()));
    adminApprovalsInitializedRef.current = true;
  }, [activeTab, activeTargetTermNo, isAdmin, scheduleTermOptions, todayYmd]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_approvals') return;
    loadScheduleSettingData(activeTargetTermNo);
  }, [activeTab, activeTargetTermNo, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab !== 'admin_behavior_eval') return;
    if (isAdminBehaviorEvalPeriodInitialized) return;
    if (!activeEmployeeTermNo) return;
    setAdminBehaviorEvalTermNo(activeEmployeeTermNo);
    setAdminBehaviorEvalPeriod(activeEmployeePeriod);
    setIsAdminBehaviorEvalPeriodInitialized(true);
  }, [
    activeTab,
    isAdmin,
    activeEmployeeTermNo,
    activeEmployeePeriod,
    isAdminBehaviorEvalPeriodInitialized,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    if (!isEmployeeTabDisabledByRule(activeTab)) return;
    const nextTab = getDefaultEmployeeTab();
    setActiveTab(nextTab);
    syncUrlByTab(nextTab);
  }, [activeTab, isAdmin, isFinalized, isEvaluationInputWindowOpen]);

  useEffect(() => {
    const resolvedTab = resolveValidTabId(activeTab, isAdmin);
    if (resolvedTab === activeTab) return;
    setActiveTab(resolvedTab);
    syncUrlByTab(resolvedTab);
  }, [activeTab, isAdmin]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const resolvedByPath = resolveTabIdByPathname(window.location.pathname);
    if (!resolvedByPath) return;

    const nextTab = resolveValidTabId(resolvedByPath.tabId, isAdmin);
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }

    if (
      resolvedByPath.adminUsersTab &&
      resolvedByPath.adminUsersTab !== adminUserManagementTab
    ) {
      setAdminUserManagementTab(resolvedByPath.adminUsersTab);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (
      activeTab === 'admin_behavior' ||
      (activeTab === 'admin_users' && adminUserManagementTab === 'behavior')
    ) {
      loadBehaviorConfigData(true, { positionCategory: behaviorItemPositionCategory });
    }
  }, [activeTab, adminUserManagementTab, behaviorItemPositionCategory]);

  useEffect(() => {
    if (
      activeTab === 'admin_adjustment' ||
      (activeTab === 'admin_users' && adminUserManagementTab === 'adjustment')
    ) {
      if (isAdmin) {
        loadAdjustmentConfigData();
      }
      return;
    }

    if (activeTab === 'adjustment' && !isAdmin) {
      const load = async () => {
        const configItems =
          adjustmentConfigItems.length > 0
            ? adjustmentConfigItems
            : await loadAdjustmentConfigData();
        await loadAdjustmentEvaluationData(configItems);
      };
      load();
    }
  }, [
    activeTab,
    adminUserManagementTab,
    isAdmin,
    activeEmployeePeriod,
    activeEmployeeTermNo,
    currentUserId,
  ]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'admin_adjustment') return;
    if (isAdminStoreAdjustmentPeriodInitialized) {
      if (
        selectedAdminAdjustmentStoreId === '' &&
        storeAssignmentStoreList.length > 0
      ) {
        setSelectedAdminAdjustmentStoreId(storeAssignmentStoreList[0].id);
      }
      return;
    }
    const currentDateTermNo =
      scheduleTermOptions.find((term) => term.startDate <= todayYmd && todayYmd <= term.endDate)
        ?.termNo ??
      scheduleTermOptions.at(-1)?.termNo ??
      TARGET_TERM_NO;
    const currentPeriod = getCurrentScheduleQuarter(new Date());
    if (adminStoreAdjustmentTermNo !== currentDateTermNo) {
      setAdminStoreAdjustmentTermNo(currentDateTermNo);
    }
    if (adminStoreAdjustmentPeriod !== currentPeriod) {
      setAdminStoreAdjustmentPeriod(currentPeriod);
    }
    if (
      selectedAdminAdjustmentStoreId === '' &&
      storeAssignmentStoreList.length > 0
    ) {
      setSelectedAdminAdjustmentStoreId(storeAssignmentStoreList[0].id);
    }
    setIsAdminStoreAdjustmentPeriodInitialized(true);
  }, [
    activeTab,
    isAdmin,
    isAdminStoreAdjustmentPeriodInitialized,
    scheduleTermOptions,
    todayYmd,
    adminStoreAdjustmentTermNo,
    adminStoreAdjustmentPeriod,
    selectedAdminAdjustmentStoreId,
    storeAssignmentStoreList,
  ]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'admin_adjustment') return;
    if (selectedAdminAdjustmentStoreId === '') return;
    if (adjustmentConfigItems.length === 0) return;
    loadAdminStoreAdjustmentDetail(
      Number(selectedAdminAdjustmentStoreId),
      adminStoreAdjustmentTermNo,
      adminStoreAdjustmentPeriod,
    );
  }, [
    activeTab,
    isAdmin,
    selectedAdminAdjustmentStoreId,
    adminStoreAdjustmentTermNo,
    adminStoreAdjustmentPeriod,
    adjustmentConfigItems,
  ]);

  useEffect(() => {
    if (activeTab === 'daily_report') {
      loadMyDailyReports();
    }
    if (activeTab === 'admin_reports') {
      loadAdminDailyReports();
    }
  }, [activeTab, authenticatedUserId]);

  useEffect(() => {
    if (!isDailyReportCalendarOpen && !isAdminReportCalendarOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (dailyReportCalendarRef.current?.contains(target)) return;
      if (adminReportCalendarRef.current?.contains(target)) return;
      setIsDailyReportCalendarOpen(false);
      setIsAdminReportCalendarOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isAdminReportCalendarOpen, isDailyReportCalendarOpen]);

  useEffect(() => {
    if (!showUserActionMenu) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest('[data-user-action-menu="true"]')) return;
      setShowUserActionMenu(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showUserActionMenu]);

  useEffect(() => {
    if (activeTab === 'admin_targets') {
      loadStoreTargetData(activeTargetTermNo);
      loadStoreScoreData(activeTargetTermNo);
    }
  }, [activeTab, activeTargetTermNo]);

  useEffect(() => {
    setShowTargetImportModal(false);
    setShowStoreScorePreviewModal(false);
  }, [activeTab]);

  useEffect(() => {
    if (adminTargetDataTab === 'actual') {
      handleCloseStoreScorePreviewModal();
    }
  }, [adminTargetDataTab]);

  useEffect(() => {
    if (isAdmin) return;
    loadStoreAssignmentBoard();
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    if (!currentUserId || !activeEmployeeTermNo) return;

    const load = async () => {
      const configItems =
        adjustmentConfigItems.length > 0
          ? adjustmentConfigItems
          : await loadAdjustmentConfigData();
      await loadAdjustmentEvaluationData(configItems);
    };

    load();
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    adjustmentConfigItems,
    currentUserId,
    employeePeriodReady,
    isAdmin,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    if (!activeEmployeeTermNo) return;
    loadStoreScoreData(activeEmployeeTermNo);
  }, [isAdmin, activeEmployeeTermNo, employeePeriodReady]);

  useEffect(() => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    if (!activeEmployeeTermNo) return;
    loadEmployeePerformanceStoreTargetData(activeEmployeeTermNo);
  }, [activeEmployeeTermNo, employeePeriodReady, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    if (!employeePeriodReady) return;
    if (!currentUserId || !activeEmployeeTermNo) return;
    if (!['dashboard', 'behavior', 'final'].includes(activeTab)) return;
    const requestKey = `${currentUserId}:${activeEmployeeTermNo}:${activeEmployeePeriod}`;
    if (rolePointLoadedKeysRef.current.has(requestKey)) {
      const cached = readRolePointCache();
      if (cached !== null) {
        setEvaluatorRolePoint(cached);
      }
      return;
    }
    if (rolePointInFlightKeyRef.current === requestKey) return;
    rolePointRequestKeyRef.current = requestKey;
    rolePointInFlightKeyRef.current = requestKey;

    const loadSupervisorRolePoint = async () => {
      try {
        const overview = await fetchBehaviorEvaluationOverview({
          userId: currentUserId,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
        });
        if (rolePointRequestKeyRef.current !== requestKey) return;
        setBehaviorSupervisorUserId(overview.supervisorUserId ?? null);
        if (typeof overview.supervisorEvaluation?.rolePoint === 'number') {
          setEvaluatorRolePoint(overview.supervisorEvaluation.rolePoint);
          writeRolePointCache(overview.supervisorEvaluation.rolePoint);
        } else {
          const cached = readRolePointCache();
          if (cached !== null) {
            setEvaluatorRolePoint(cached);
          }
        }
        rolePointLoadedKeysRef.current.add(requestKey);
      } catch {
        if (rolePointRequestKeyRef.current !== requestKey) return;
        const cached = readRolePointCache();
        if (cached !== null) {
          setEvaluatorRolePoint(cached);
        }
      } finally {
        if (rolePointInFlightKeyRef.current === requestKey) {
          rolePointInFlightKeyRef.current = null;
        }
      }
    };

    loadSupervisorRolePoint();
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    activeTab,
    currentUserId,
    employeePeriodReady,
    isAdmin,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    if (!currentUserId) return;
    if (!['performance', 'dashboard', 'final'].includes(activeTab)) return;

    const assignedStoreIds = Array.from(
      new Set(
        storeAssignmentList
          .filter((assignment) => assignment.userId === currentUserId)
          .map((assignment) => assignment.storeId),
      ),
    );
    if (assignedStoreIds.length === 0) {
      console.log('[employee_score_debug] performance sync skipped (no assignment)', {
        currentUserId,
        activeTab,
        assignmentCount: 0,
      });
      return;
    }
    if (!hasStoreScoreLoaded || isStoreScoreLoading) {
      console.log('[employee_score_debug] performance sync skipped (store scores loading)', {
        assignedStoreIds,
        qscCount: storeQscScores.length,
        qscRateCount: storeQscRateScores.length,
        crewEsCount: storeCrewEsScores.length,
      });
      return;
    }
    if (!hasEmployeePerformanceTargetLoaded || isEmployeePerformanceTargetLoading) {
      console.log('[employee_score_debug] performance sync skipped (targets loading)', {
        assignedStoreIds,
      });
      return;
    }
    const { salesRatio, profitRatio, qscScore, qscRateScore, crewEsScore } =
      calculatePerformanceActualMetricsByStores({
        storeIds: assignedStoreIds,
        termNo: activeEmployeeTermNo,
        period: activeEmployeePeriod,
      });

    console.log('[employee_score_debug] performance sync source', {
      currentUserId,
      assignedStoreIds,
      activeEmployeeTermNo,
      activeEmployeePeriod,
      qscScore,
      qscRateScore,
      crewEsScore,
      salesRatio,
      profitRatio,
    });

    setPerformanceData((prev) =>
      {
        const next = prev.map((item) => {
          const fixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
          if (fixedKey === 'sales_growth') {
            const nextActual = `${salesRatio}%`;
            return item.actual === nextActual ? item : { ...item, actual: nextActual };
          }
          if (fixedKey === 'profit_growth') {
            const nextActual = `${profitRatio}%`;
            return item.actual === nextActual ? item : { ...item, actual: nextActual };
          }
          if (fixedKey === 'monitor_check') {
            const nextActual = `${qscScore}点`;
            return item.actual === nextActual ? item : { ...item, actual: nextActual };
          }
          if (fixedKey === 'qsc_score') {
            const nextActual = `${qscRateScore}%`;
            return item.actual === nextActual ? item : { ...item, actual: nextActual };
          }
          if (fixedKey === 'crew_es') {
            const nextActual = `${crewEsScore}点`;
            return item.actual === nextActual ? item : { ...item, actual: nextActual };
          }
          return item;
        });
        console.log('[employee_score_debug] performance sync applied', {
          rows: next.map((item) => ({
            id: item.id,
            item: item.item,
            fixedKey: resolvePerformanceFixedKey(item.fixedKey, item.item),
            actual: item.actual,
            point: calculatePerformancePoint(item),
            weight: item.weight,
          })),
        });
        return next;
      },
    );
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    currentUserId,
    employeePerformanceStoreTargets,
    isAdmin,
    activeTab,
    performanceData.map((item) => `${item.id}:${item.item}`).join('|'),
    hasEmployeePerformanceTargetLoaded,
    hasStoreScoreLoaded,
    isEmployeePerformanceTargetLoading,
    isStoreScoreLoading,
    storeAssignmentList,
    storeCrewEsScores,
    storeQscRateScores,
    storeQscScores,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    if (!['dashboard', 'behavior', 'performance', 'final'].includes(activeTab)) return;
    console.log('[employee_score_debug] score snapshot', {
      activeTab,
      behaviorViewMode,
      perfScore,
      behaviorScore,
      adjustScore,
      effectiveRolePoint,
      totalScore,
      behaviorRows: behaviorData.map((item) => ({
        id: item.id,
        item: item.item,
        selfScore: item.selfScore,
        evaluatorScore: item.evaluatorScore,
        weight: item.weight,
      })),
      performanceRows: performanceData.map((item) => ({
        id: item.id,
        item: item.item,
        actual: item.actual,
        point: calculatePerformancePoint(item),
        weight: item.weight,
      })),
    });
  }, [
    activeTab,
    adjustScore,
    behaviorData,
    behaviorScore,
    behaviorViewMode,
    effectiveRolePoint,
    isAdmin,
    perfScore,
    performanceData,
    totalScore,
  ]);

  useEffect(() => {
    if (storeTargetNoticeMessage) {
      showNotification(storeTargetNoticeMessage, 'success');
      setStoreTargetNoticeMessage('');
    }
  }, [storeTargetNoticeMessage]);

  const employeeAssignedStoreIds = useMemo(
    () =>
      Array.from(
        new Set(
          storeAssignmentList
            .filter((assignment) => assignment.userId === currentUserId)
            .map((assignment) => assignment.storeId),
        ),
      ),
    [currentUserId, storeAssignmentList],
  );
  const employeeAssignedStores = useMemo(
    () =>
      employeeAssignedStoreIds.map((storeId) => {
        const store = storeAssignmentStoreList.find((storeItem) => storeItem.id === storeId);
        return {
          storeId,
          storeName: store?.storeName ?? `店舗ID:${storeId}`,
        };
      }),
    [employeeAssignedStoreIds, storeAssignmentStoreList],
  );
  useEffect(() => {
    if (employeeAssignedStoreIds.length <= 1) {
      setSelectedPerformanceStoreId('average');
      return;
    }
    if (
      selectedPerformanceStoreId !== 'average' &&
      !employeeAssignedStoreIds.includes(selectedPerformanceStoreId)
    ) {
      setSelectedPerformanceStoreId('average');
    }
  }, [employeeAssignedStoreIds, selectedPerformanceStoreId]);
  useEffect(() => {
    if (employeeAssignedStoreIds.length <= 1) {
      setSelectedAdjustmentStoreId('average');
      return;
    }
    if (
      selectedAdjustmentStoreId !== 'average' &&
      !employeeAssignedStoreIds.includes(selectedAdjustmentStoreId)
    ) {
      setSelectedAdjustmentStoreId('average');
    }
  }, [employeeAssignedStoreIds, selectedAdjustmentStoreId]);
  useEffect(() => {
    if (isAdmin || activeTab !== 'adjustment') {
      setAdjustmentStoreViewData(null);
      setIsAdjustmentStoreViewLoading(false);
      return;
    }
    if (selectedAdjustmentStoreId === 'average') {
      setAdjustmentStoreViewData(null);
      setIsAdjustmentStoreViewLoading(false);
      return;
    }
    if (!employeePeriodReady || !activeEmployeeTermNo) {
      setAdjustmentStoreViewData(null);
      setIsAdjustmentStoreViewLoading(false);
      return;
    }
    if (!employeeAssignedStoreIds.includes(selectedAdjustmentStoreId)) {
      setAdjustmentStoreViewData(null);
      setIsAdjustmentStoreViewLoading(false);
      return;
    }
    if (adjustmentConfigItems.length === 0) {
      setAdjustmentStoreViewData(null);
      setIsAdjustmentStoreViewLoading(false);
      return;
    }

    const requestSeq = ++adjustmentStoreViewLoadSeqRef.current;
    setIsAdjustmentStoreViewLoading(true);
    setAdjustmentStoreViewData(null);
    const load = async () => {
      try {
        const result = await fetchStoreAdjustmentEvaluation({
          storeId: selectedAdjustmentStoreId,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
        });
        if (requestSeq !== adjustmentStoreViewLoadSeqRef.current) return;
        setAdjustmentStoreViewData(
          mergeAdjustmentEntriesToUiData(adjustmentConfigItems, result.entries),
        );
      } catch (error) {
        if (requestSeq !== adjustmentStoreViewLoadSeqRef.current) return;
        showNotification(
          error instanceof Error ? error.message : '店舗加減点データの取得に失敗しました',
          'error',
        );
        setAdjustmentStoreViewData(mergeAdjustmentEntriesToUiData(adjustmentConfigItems, []));
      } finally {
        if (requestSeq === adjustmentStoreViewLoadSeqRef.current) {
          setIsAdjustmentStoreViewLoading(false);
        }
      }
    };
    load();
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    activeTab,
    adjustmentConfigItems,
    employeeAssignedStoreIds,
    employeePeriodReady,
    isAdmin,
    selectedAdjustmentStoreId,
  ]);
  const performanceViewStoreIds = useMemo(() => {
    if (
      selectedPerformanceStoreId !== 'average' &&
      employeeAssignedStoreIds.includes(selectedPerformanceStoreId)
    ) {
      return [selectedPerformanceStoreId];
    }
    return employeeAssignedStoreIds;
  }, [employeeAssignedStoreIds, selectedPerformanceStoreId]);
  const performanceViewMetrics = useMemo(
    () =>
      calculatePerformanceActualMetricsByStores({
        storeIds: performanceViewStoreIds,
        termNo: activeEmployeeTermNo,
        period: activeEmployeePeriod,
      }),
    [
      activeEmployeePeriod,
      activeEmployeeTermNo,
      employeePerformanceStoreTargets,
      performanceViewStoreIds,
      storeCrewEsScores,
      storeQscRateScores,
      storeQscScores,
    ],
  );
  const performanceViewData = useMemo(() => {
    return performanceData.map((item) => {
      const fixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
      let actual = item.actual;
      const resolvedActual = resolvePerformanceActualByMetrics(fixedKey, performanceViewMetrics);
      if (resolvedActual !== null) actual = resolvedActual;
      const nextItem = { ...item, actual };
      const points =
        fixedKey && performanceViewStoreIds.length > 1
          ? calculatePerformanceAveragePointByStores({
              item,
              fixedKey,
              storeIds: performanceViewStoreIds,
              termNo: activeEmployeeTermNo,
              period: activeEmployeePeriod,
            })
          : calculatePerformancePoint(nextItem);
      return {
        ...nextItem,
        points,
      };
    });
  }, [
    activeEmployeePeriod,
    activeEmployeeTermNo,
    calculatePerformanceAveragePointByStores,
    performanceData,
    performanceViewMetrics,
    performanceViewStoreIds,
  ]);
  const performanceViewScore = useMemo(
    () =>
      performanceViewData.reduce(
        (sum, row) => sum + calculateWeightedScoreByMax(row.points, row.weight, row.max),
        0,
      ),
    [performanceViewData],
  );
  const adjustmentViewData = useMemo(() => {
    if (selectedAdjustmentStoreId === 'average') return adjustments;
    if (adjustmentStoreViewData) return adjustmentStoreViewData;
    return mapAdjustmentConfigToUiData(adjustmentConfigItems);
  }, [adjustmentConfigItems, adjustmentStoreViewData, adjustments, selectedAdjustmentStoreId]);
  const adjustmentViewScore = useMemo(
    () =>
      adjustmentViewData.plus.reduce(
        (acc, curr) => acc + Number(curr.count || 0) * curr.unitPoint,
        0,
      ) +
      adjustmentViewData.minus.reduce(
        (acc, curr) => acc + Number(curr.count || 0) * curr.unitPoint,
        0,
      ),
    [adjustmentViewData],
  );

  useEffect(() => {
    const errorMessage =
      storeTargetErrorMessage ||
      storeErrorMessage ||
      storeAssignmentErrorMessage ||
      accountErrorMessage ||
      evaluationLineErrorMessage ||
      performanceConfigErrorMessage ||
      behaviorConfigErrorMessage ||
      adjustmentConfigErrorMessage ||
      dailyReportErrorMessage;
    if (errorMessage) {
      showNotification(errorMessage, 'error');
    }
  }, [
    storeTargetErrorMessage,
    storeErrorMessage,
    storeAssignmentErrorMessage,
    accountErrorMessage,
    evaluationLineErrorMessage,
    performanceConfigErrorMessage,
    behaviorConfigErrorMessage,
    adjustmentConfigErrorMessage,
    dailyReportErrorMessage,
  ]);

  useEffect(() => {
    if (!appNotification) return;
    setIsAppNotificationVisible(true);
    const hideTimer = window.setTimeout(() => {
      setIsAppNotificationVisible(false);
    }, 2400);
    const clearTimer = window.setTimeout(() => {
      setAppNotification(null);
    }, 3000);
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(clearTimer);
    };
  }, [appNotification]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      restoreMainScrollPosition();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [
    showUserModal,
    showEditUserModal,
    showEditPositionModal,
    showCreatePositionModal,
    showAddDepartmentModal,
    showAddEvaluatorModal,
    showStoreAssignmentModal,
    selectedEvaluatorIdForLineModal,
  ]);

  const toggleRole = () => {
    const newRole = !isAdmin;
    setIsAdmin(newRole);
    if (!lockTab) {
      const nextTab = newRole ? 'admin_reports' : getDefaultEmployeeTab();
      setActiveTab(nextTab);
      syncUrlByTab(nextTab);
    }
    setBehaviorViewMode(newRole ? 'evaluator' : 'self');
    if (newRole) { 
      const myId = currentUserId; 
      const mySubordinatesIds = evalRelations[myId] || [];
      if (mySubordinatesIds.length > 0) setSelectedTargetUserId(mySubordinatesIds[0]);
      else setSelectedTargetUserId('');
    } else {
      setSelectedTargetUserId(currentUserId);
    }
  };

  const handleStoreTargetChange = (
    storeId: number,
    month: number,
    field: 'sales' | 'profit',
    value: string,
  ) => {
    const nextValue = Number(value || 0);
    const normalizedValue = Number.isFinite(nextValue) ? nextValue : 0;
    setStoreTargets((prev) =>
      prev.map((row) =>
        row.termNo === activeTargetTermNo && row.storeId === storeId && row.month === month
          ? { ...row, [field]: normalizedValue }
          : row
      ),
    );
  };

  const handleSaveStoreTargets = async () => {
    try {
      setIsStoreTargetSaving(true);
      setStoreTargetErrorMessage('');
      setStoreTargetNoticeMessage('');
      const rows = storeTargets
        .filter((row) => row.termNo === activeTargetTermNo)
        .map((row) => ({
          storeId: row.storeId,
          termNo: row.termNo,
          month: row.month,
          sales: row.sales,
          profit: row.profit,
        }));
      await saveStoreTargetRows({ rows });
      await loadStoreTargetData(activeTargetTermNo);
      setStoreTargetNoticeMessage('数値を保存しました。');
    } catch (error) {
      setStoreTargetErrorMessage(error instanceof Error ? error.message : '保存に失敗しました');
    } finally {
      setIsStoreTargetSaving(false);
    }
  };

  const handleCloseTargetImportModal = () => {
    setShowTargetImportModal(false);
    setPendingImportFile(null);
    setShowImportMonthPicker(false);
    setPdfReportRef(null);
    setImportMonthOptions([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    setSelectedImportMonths([]);
  };

  const handleCloseStoreScorePreviewModal = () => {
    setShowStoreScorePreviewModal(false);
    setPendingStoreScoreFiles([]);
    setPendingStoreScorePreviews([]);
    setStoreScoreImportOverrideTermNo('');
    setStoreScoreImportOverridePeriod('');
  };

  const handleOpenTargetPdfSelect = () => {
    setTargetImportModalType('actual');
    fileInputRef.current?.click();
  };

  const handleOpenStoreScorePdfSelect = (type: 'qsc' | 'qsc_rate' | 'crew_es') => {
    if (type === 'qsc') {
      qscFileInputRef.current?.click();
      return;
    }
    if (type === 'qsc_rate') {
      qscRateFileInputRef.current?.click();
      return;
    }
    crewEsFileInputRef.current?.click();
  };

  const handleOpenTargetImportFileSelect = () => {
    if (targetImportModalType === 'actual') {
      fileInputRef.current?.click();
      return;
    }
    if (targetImportModalType === 'qsc') {
      qscFileInputRef.current?.click();
      return;
    }
    if (targetImportModalType === 'qsc_rate') {
      qscRateFileInputRef.current?.click();
      return;
    }
    crewEsFileInputRef.current?.click();
  };

  const findOversizedPdfFile = (files: File[]) =>
    files.find((file) => file.size > uploadFileSizeLimitBytes);

  const handleImportStoreTargetPdf = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    const oversizedFile = findOversizedPdfFile([file]);
    if (oversizedFile) {
      const actualMb = (oversizedFile.size / (1024 * 1024)).toFixed(1);
      setStoreTargetErrorMessage(
        `リクエストファイルが大きすぎます（上限 ${uploadFileSizeMb}MB / 選択 ${actualMb}MB）`,
      );
      return;
    }
    try {
      setIsStoreTargetImporting(true);
      setStoreTargetErrorMessage('');
      setStoreTargetNoticeMessage('');
      const preview = await previewStoreTargetPdf(file);
      setPendingImportFile(file);
      setPdfReportRef({ termNo: preview.termNo, month: preview.month });
      setImportMonthOptions(
        Array.isArray(preview.availableMonths) && preview.availableMonths.length > 0
          ? preview.availableMonths
          : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      );
      setSelectedImportMonths([preview.month]);
      setShowImportMonthPicker(true);
      setShowTargetImportModal(true);
      setStoreTargetNoticeMessage(
        `PDF確認OK: 第${preview.termNo}期 / ${preview.month}月現在（${preview.storeCount}店舗）`,
      );
    } catch (error) {
      setStoreTargetErrorMessage(
        error instanceof Error ? error.message : 'PDFインポートに失敗しました',
      );
    } finally {
      setIsStoreTargetImporting(false);
    }
  };

  const toggleImportMonth = (month: number) => {
    setSelectedImportMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((value) => value !== month);
      }
      return [...prev, month].sort((a, b) => a - b);
    });
  };

  const handleExecuteStoreTargetPdfImport = async () => {
    if (!pendingImportFile || selectedImportMonths.length === 0) return;
    try {
      setIsStoreTargetImporting(true);
      setStoreTargetErrorMessage('');
      setStoreTargetNoticeMessage('');
      const result = await importStoreTargetPdf(pendingImportFile, selectedImportMonths);
      setShowTargetImportModal(false);
      setShowImportMonthPicker(false);
      await loadStoreTargetData(result.termNo);
      const unmatchedSuffix =
        result.unmatchedStoreNames.length > 0
          ? `（未一致: ${result.unmatchedStoreNames.join('、')}）`
          : '';
      setStoreTargetNoticeMessage(
        `第${result.termNo}期 ${result.month}月PDFから${result.importedMonths.join('・')}月へ反映しました（${result.importedCount}件、保存済み）${unmatchedSuffix}`,
      );
      setPendingImportFile(null);
      setPdfReportRef(null);
      setImportMonthOptions([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
      setSelectedImportMonths([]);
    } catch (error) {
      setStoreTargetErrorMessage(
        error instanceof Error ? error.message : 'PDFインポートに失敗しました',
      );
    } finally {
      setIsStoreTargetImporting(false);
    }
  };

  const handleImportStoreScorePdf = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'qsc' | 'qsc_rate' | 'crew_es',
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    event.target.value = '';
    const oversizedFile = findOversizedPdfFile(files);
    if (oversizedFile) {
      const actualMb = (oversizedFile.size / (1024 * 1024)).toFixed(1);
      showNotification(
        `リクエストファイルが大きすぎます（上限 ${uploadFileSizeMb}MB / 選択 ${actualMb}MB）`,
        'error',
      );
      return;
    }

    try {
      setIsStoreScoreImporting(type);
      const previewPromises = files.map(async (file) => {
        const preview = type === 'qsc'
          ? await previewStoreQscPdf(file)
          : type === 'qsc_rate'
            ? await previewStoreQscRatePdf(file)
            : await previewStoreCrewEsPdf(file);
        return {
          ...preview,
          type,
          fileName: file.name,
        };
      });
      const previews = await Promise.all(previewPromises);
      setPendingStoreScoreFiles(files);
      setPendingStoreScorePreviews(previews);
      setStoreScoreImportOverrideTermNo(previews[0]?.termNo ?? '');
      setStoreScoreImportOverridePeriod(previews[0]?.period ?? '');
      setShowStoreScorePreviewModal(true);
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : 'PDFインポートに失敗しました',
        'error',
      );
    } finally {
      setIsStoreScoreImporting(null);
    }
  };

  const handleSaveStoreScorePreview = async () => {
    if (pendingStoreScorePreviews.length === 0) return;

    try {
      const importType = pendingStoreScorePreviews[0].type;
      setIsStoreScoreImporting(importType);
      if (
        typeof storeScoreImportOverrideTermNo !== 'number' ||
        (storeScoreImportOverridePeriod !== 'first_half' &&
          storeScoreImportOverridePeriod !== 'second_half')
      ) {
        throw new Error('保存先の期・半期を選択してください');
      }
      const importedRows =
        importType === 'qsc'
          ? await Promise.all(
              pendingStoreScoreFiles.map((file) =>
                importStoreQscPdfWithPeriod(
                  file,
                  storeScoreImportOverrideTermNo,
                  storeScoreImportOverridePeriod,
                ),
              ),
            )
          : importType === 'qsc_rate'
            ? await Promise.all(
                pendingStoreScoreFiles.map((file) =>
                  importStoreQscRatePdfWithPeriod(
                    file,
                    storeScoreImportOverrideTermNo,
                    storeScoreImportOverridePeriod,
                  ),
                ),
              )
            : await Promise.all(
                pendingStoreScoreFiles.map((file) =>
                  importStoreCrewEsPdfWithPeriod(
                    file,
                    storeScoreImportOverrideTermNo,
                    storeScoreImportOverridePeriod,
                  ),
                ),
              );

      const latestImportedTermNo = importedRows.at(-1)?.termNo ?? activeTargetTermNo;
      handleCloseStoreScorePreviewModal();
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
      if (latestImportedTermNo !== activeTargetTermNo) {
        setActiveTargetTermNo(latestImportedTermNo);
      } else {
        await loadStoreScoreData(latestImportedTermNo);
      }
      showNotification(
        `${importType === 'qsc'
          ? 'モニターチェック'
          : importType === 'qsc_rate'
            ? 'QSCスコア'
            : 'クルーESスコア'}を${importedRows.length}件保存しました（${
          `第${storeScoreImportOverrideTermNo}期 ${quarterLabelMap[storeScoreImportOverridePeriod]}`
        }）`,
        'success',
      );
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '保存に失敗しました',
        'error',
      );
    } finally {
      setIsStoreScoreImporting(null);
    }
  };

  const handleBehaviorChange = (id, field, value) => {
    setBehaviorData(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };
  const handleBehaviorCriteriaChange = (id, level, value) => {
    setBehaviorData(prev => prev.map(item => item.id === id ? { ...item, criteria: { ...item.criteria, [level]: value } } : item));
  };
  const handleSelectBehaviorLevel = (
    itemId: number,
    level: number,
    target: 'self' | 'evaluator',
  ) => {
    handleBehaviorChange(
      itemId,
      target === 'self' ? 'selfScore' : 'evaluatorScore',
      level,
    );
  };
  const handleSaveSelfBehaviorEvaluation = async () => {
    try {
      setIsBehaviorEvaluationSaving(true);
      await saveSelfBehaviorEvaluation({
        userId: currentUserId,
        termNo: activeEmployeeTermNo,
        period: activeEmployeePeriod,
        items: behaviorData.map((item) => ({
          behaviorItemId: item.id,
          score: Number(item.selfScore || 0),
          comment: String(item.selfComment ?? ''),
        })),
      });
      await loadBehaviorEvaluationData();
      showNotification('本人評価を保存しました。', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '本人評価の保存に失敗しました',
        'error',
      );
    } finally {
      setIsBehaviorEvaluationSaving(false);
    }
  };
  const handleSaveEvaluatorBehaviorEvaluation = async () => {
    if (!selectedTargetUserId) {
      showNotification('評価対象者を選択してください。', 'error');
      return;
    }

    try {
      setIsBehaviorEvaluationSaving(true);
      const scopedTermNo =
        isAdmin && activeTab === 'admin_behavior_eval'
          ? adminBehaviorEvalTermNo
          : activeEmployeeTermNo;
      const scopedPeriod =
        isAdmin && activeTab === 'admin_behavior_eval'
          ? adminBehaviorEvalPeriod
          : activeEmployeePeriod;
      await saveEvaluatorBehaviorEvaluation({
        evaluatorUserId: currentUserId,
        targetUserId: selectedTargetUserId,
        termNo: scopedTermNo,
        period: scopedPeriod,
        overallComment: overallFeedback,
        rolePoint: Number(evaluatorRolePoint || 0),
        items: behaviorData.map((item) => ({
          behaviorItemId: item.id,
          score: Number(item.evaluatorScore || 0),
          comment: String(item.evaluatorComment ?? ''),
        })),
      });
      await loadBehaviorEvaluationData({ targetUserId: selectedTargetUserId });
      showNotification('上長評価を保存しました。', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '上長評価の保存に失敗しました',
        'error',
      );
    } finally {
      setIsBehaviorEvaluationSaving(false);
    }
  };
  
  const handlePerformanceChange = (id, field, value) => {
    setPerformanceData(prev => prev.map(item => {
      if (item.id !== id) return item;
      if (
        resolvePerformanceFixedKey(item.fixedKey, item.item) !== null &&
        ['item', 'type', 'max', 'ranges', 'weight'].includes(field)
      ) {
        return item;
      }
      return { ...item, [field]: value };
    }));
  };

  const persistPerformanceConfig = async (
    sourceItems,
    options?: { showSuccess?: boolean; enforceWeight?: boolean },
  ) => {
    const nextPerformanceData = sourceItems.filter((item) => item.item !== '営業利益 伸長率');
    const normalizedPerformanceData = nextPerformanceData.map((item) => ({
      ...item,
      ranges: normalizePerformanceRangesToFixedPoints(
        item.ranges ?? [],
      ),
    }));

    for (const item of normalizedPerformanceData) {
      if (item.type === 'manual') continue;
      for (let index = 0; index < item.ranges.length - 1; index += 1) {
        const current = item.ranges[index];
        const next = item.ranges[index + 1];
        if (Number(current.min) <= Number(next.min)) {
          const message = `${item.item} の基準値は、${current.point}pt が ${next.point}pt より大きい値になるよう設定してください。`;
          setPerformanceConfigErrorMessage(message);
          if (options?.showSuccess || options?.enforceWeight) {
            showNotification(message, 'error');
          }
          return false;
        }
      }
    }

    const totalWeight = normalizedPerformanceData.reduce((sum, item) => sum + item.weight, 0);
    if (options?.enforceWeight && totalWeight !== 100) {
      showNotification(`ウェイト合計が100%ではありません（現在: ${totalWeight}%）`, 'error');
      return false;
    }

    try {
      setIsPerformanceConfigSaving(true);
      setPerformanceConfigErrorMessage('');
      const savedItems = await replacePerformanceItemList({
        items: normalizedPerformanceData.map((item) => ({
          id: item.id,
          item: item.item,
          type: item.type,
          weight: Number(item.weight || 0),
          max: Number(item.max || 4),
          ranges: normalizePerformanceRangesToFixedPoints(item.ranges ?? []).map((range) => ({
            min: Number(range.min ?? 0),
            point: Number(range.point ?? 0),
          })),
        })),
      });
      setPerformanceData((prev) => mapPerformanceConfigToUiData(savedItems, prev));
      if (options?.showSuccess) {
        showNotification('業績評価設定を更新しました。', 'success');
      }
      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '業績評価設定の更新に失敗しました';
      setPerformanceConfigErrorMessage(message);
      showNotification(message, 'error');
      return false;
    } finally {
      setIsPerformanceConfigSaving(false);
    }
  };

  const schedulePerformanceRangeAutoSave = (nextItems) => {
    if (!isAdmin) return;
    if (
      activeTab !== 'admin_performance' &&
      !(activeTab === 'admin_users' && adminUserManagementTab === 'performance')
    ) {
      return;
    }
    if (performanceAutoSaveTimerRef.current) {
      clearTimeout(performanceAutoSaveTimerRef.current);
    }
    performanceAutoSaveTimerRef.current = setTimeout(() => {
      void persistPerformanceConfig(nextItems, { enforceWeight: false, showSuccess: false });
    }, 500);
  };

  // 評価レンジ変更ハンドラ
  const handleRangeChange = (itemId, rangeIdx, value) => {
    setPerformanceData((prev) => {
      const nextItems = prev.map((item) => {
        if (item.id !== itemId) return item;
        const newRanges = normalizePerformanceRangesToFixedPoints(item.ranges ?? []);
        newRanges[rangeIdx] = { ...newRanges[rangeIdx], min: Number(value) };
        return { ...item, ranges: newRanges };
      });
      schedulePerformanceRangeAutoSave(nextItems);
      return nextItems;
    });
  };

  const saveCurrentScheduleSettings = async (
    successMessage?: string,
    overrides?: {
      evaluationInputPeriods?: typeof evaluationInputPeriods;
      scheduleData?: typeof scheduleData;
      alertSettings?: typeof alertSettings;
    },
  ) => {
    setIsScheduleSaving(true);
    setScheduleErrorMessage('');
    try {
      const nextEvaluationInputPeriods = overrides?.evaluationInputPeriods ?? evaluationInputPeriods;
      const nextScheduleData = overrides?.scheduleData ?? scheduleData;
      const nextAlertSettings = overrides?.alertSettings ?? alertSettings;
      const payload =
        scheduleKeyMode === 'quarter'
          ? ({
              evaluationInputPeriods: {
                q1: nextEvaluationInputPeriods.first_half,
                q2: nextEvaluationInputPeriods.second_half,
              },
              scheduleData: {
                q1: nextScheduleData.first_half,
                q2: nextScheduleData.second_half,
              },
              alertSettings: {
                q1: nextAlertSettings.first_half,
                q2: nextAlertSettings.second_half,
              },
            } as unknown as Parameters<typeof saveScheduleSettings>[0])
          : ({
              evaluationInputPeriods: nextEvaluationInputPeriods,
              scheduleData: nextScheduleData,
              alertSettings: nextAlertSettings,
            } as Parameters<typeof saveScheduleSettings>[0]);

      await saveScheduleSettings(payload, scheduleTermNo);
      await loadScheduleSettingData(scheduleTermNo);
      if (successMessage) {
        showNotification(successMessage, 'success');
      }
      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '評価スケジュールの更新に失敗しました';
      setScheduleErrorMessage(message);
      showNotification(message, 'error');
      return false;
    } finally {
      setIsScheduleSaving(false);
    }
  };

  const handleSaveAdjustment = async () => {
    try {
      setIsAdjustmentConfigSaving(true);
      setAdjustmentConfigErrorMessage('');
      const savedItems = await replaceAdjustmentItemList({
        items: [
          ...adjustments.plus.map((item) => ({
            id: Number.isFinite(Number(item.id)) ? Number(item.id) : undefined,
            category: 'plus' as const,
            item: item.item,
            unitPoint: Number(item.unitPoint || 0),
          })),
          ...adjustments.minus.map((item) => ({
            id: Number.isFinite(Number(item.id)) ? Number(item.id) : undefined,
            category: 'minus' as const,
            item: item.item,
            unitPoint: Number(item.unitPoint || 0),
          })),
        ],
      });
      setAdjustments(mapAdjustmentConfigToUiData(savedItems));
      showNotification('加減点設定を更新しました。', 'success');
    } catch (error) {
      setAdjustmentConfigErrorMessage(
        error instanceof Error ? error.message : '加減点設定の更新に失敗しました',
      );
    } finally {
      setIsAdjustmentConfigSaving(false);
    }
  };

  const handleSavePerformance = async () => {
    await persistPerformanceConfig(performanceData, { enforceWeight: true, showSuccess: true });
  };

  const handleAddPerformanceItem = () => {
    const newId = Math.max(...performanceData.map(p => p.id), 0) + 1;
    setPerformanceData([...performanceData, { 
      id: newId, item: '新規KPI項目', fixedKey: null, type: 'absolute', actual: '0', points: 0, weight: 0, max: 4, ranges: []
    }]);
  };

  const handleAdjustCountChange = (type, id, val) => {
    if (!isAdmin && isFinalized) return;
    setAdjustments((prev) => ({
      ...prev,
      [type]: prev[type].map((item) => {
        if (item.id !== id) return item;
        if (val === '') return { ...item, count: '' };
        const numeric = Number(val);
        return { ...item, count: Number.isFinite(numeric) ? Math.max(0, numeric) : '' };
      }),
    }));
  };
  const handleAdjustMasterChange = (type, id, field, val) => setAdjustments(prev => ({ ...prev, [type]: prev[type].map(item => item.id === id ? { ...item, [field]: field === 'unitPoint' ? Number(val) : val } : item) }));
  const handleAddAdjustmentItem = (type) => setAdjustments(prev => ({ ...prev, [type]: [...prev[type], { id: `${type}_${Date.now()}`, item: '新規項目', unitPoint: 5, count: '' }] }));
  const handleDeleteAdjustmentItem = (type, id) => {
    openGenericConfirm({
      title: '加減点項目削除の確認',
      message: 'この項目を削除します。よろしいですか？',
      confirmText: '削除する',
      tone: 'danger',
      onConfirm: () => {
        setAdjustments((prev) => ({
          ...prev,
          [type]: prev[type].filter((item) => item.id !== id),
        }));
        showNotification('加減点項目を削除しました。', 'success');
      },
    });
  };
  const handleFinalize = async () => {
    const finalizeBlockReasons: string[] = [];
    if (
      isStoreScoreLoading ||
      isEmployeePerformanceTargetLoading ||
      !hasStoreScoreLoaded ||
      !hasEmployeePerformanceTargetLoaded
    ) {
      finalizeBlockReasons.push('店舗業績データを読み込み中です。しばらく待ってから再実行してください。');
    }
    if (!hasPerformanceEvaluation) {
      finalizeBlockReasons.push('店舗業績評価が未入力のため評価を確定できません。');
    }
    if (!hasSelfBehaviorEvaluation) {
      finalizeBlockReasons.push('自己の行動評価が未入力のため評価を確定できません。');
    }
    if (!behaviorSupervisorUserId) {
      finalizeBlockReasons.push('上長が未設定のため評価を確定できません。');
    } else if (!hasSupervisorBehaviorEvaluation) {
      finalizeBlockReasons.push('上長の行動評価が未入力のため評価を確定できません。');
    }
    if (!hasAdjustmentEvaluation) {
      finalizeBlockReasons.push('加点・減点の件数が未入力のため評価を確定できません。');
    }
    if (finalizeBlockReasons.length > 0) {
      showNotification(finalizeBlockReasons[0], 'error');
      setShowConfirmModal(false);
      return;
    }

    const currentAccount = accountList.find((account) => account.id === currentUserId);
    const submissionKey = getEvaluationSubmissionKey(
      currentUserId,
      activeEmployeeTermNo,
      activeEmployeePeriod,
    );
    const finalizePayload = {
      userId: currentUserId,
      termNo: activeEmployeeTermNo,
      period: activeEmployeePeriod,
      isFinalized: true,
      userName: currentAccount?.name ?? activeDisplayUserName,
      department: currentAccount?.department ?? '',
      role: currentAccount?.roleName ?? activeDisplayRoleName,
      performanceScore: perfScore,
      behaviorScore,
      adjustmentScore: adjustScore,
      executiveAdjustmentScore: effectiveRolePoint,
      totalScore,
      rank: resolveRankByScore(totalScore),
    };

    try {
      const result = await saveFinalEvaluation(finalizePayload);
      const submittedAt = result.saved.finalizedAt ?? new Date().toISOString();

      setSubmittedEvaluations((prev) => {
        const nextRow: SubmittedEvaluationSnapshot = {
          submissionKey,
          userId: currentUserId,
          userName: currentAccount?.name ?? activeDisplayUserName,
          department: currentAccount?.department ?? '',
          role: currentAccount?.roleName ?? activeDisplayRoleName,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
          isFinalized: true,
          performanceScore: perfScore,
          behaviorScore,
          adjustmentScore: adjustScore,
          executiveAdjustmentScore: effectiveRolePoint,
          totalScore,
          rank: result.saved.rank ?? null,
          submittedAt,
          hasSnapshot: true,
        };

        return [
          nextRow,
          ...prev.filter((item) => item.submissionKey !== submissionKey),
        ];
      });
      setIsFinalized(true);
      setFinalizedScoreSnapshot({
        performanceScore: perfScore,
        behaviorScore,
        adjustmentScore: adjustScore,
        executiveAdjustmentScore: effectiveRolePoint,
        totalScore,
      });
      setFinalizedRank(result.saved.rank ?? null);
      setShowConfirmModal(false);
      showNotification('評価を確定しました。', 'success');

      setShowFinalAiCommentModal(true);
      setIsFinalAiCommentLoading(true);
      setFinalAiComment({ summary: '', strength: '', improvement: '' });
      try {
        const aiResult = await generateFinalEvaluationAiComment({
          userId: currentUserId,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
          userName: currentAccount?.name ?? activeDisplayUserName,
          performanceScore: perfScore,
          behaviorScore,
          adjustmentScore: adjustScore,
          executiveAdjustmentScore: effectiveRolePoint,
          totalScore,
        });
        const saveResult = await saveFinalEvaluationAiComment({
          userId: currentUserId,
          termNo: activeEmployeeTermNo,
          period: activeEmployeePeriod,
          summary: aiResult.result.summary,
          strength: aiResult.result.strength,
          improvement: aiResult.result.improvement,
        });
        setFinalAiComment({
          summary: saveResult.saved.summary,
          strength: saveResult.saved.strength,
          improvement: saveResult.saved.improvement,
        });
        showNotification('AIコメントの生成が完了しました。', 'success');
      } catch (aiError) {
        const message =
          aiError instanceof Error ? aiError.message : 'AIコメントの生成に失敗しました';
        setFinalAiComment({
          summary: `AIコメントを生成できませんでした。${message}`,
          strength: '',
          improvement: '',
        });
        showNotification(
          aiError instanceof Error ? aiError.message : 'AIコメントの生成に失敗しました',
          'error',
        );
      } finally {
        setIsFinalAiCommentLoading(false);
        setShowFinalAiCommentModal(false);
        moveToDashboardAiComment();
      }
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '評価の確定に失敗しました',
        'error',
      );
    }
  };
  const handleOpenEvaluationPeriodModal = () => {
    setEvaluationPeriodForm({
      fromDate: evaluationInputPeriods[activeQuarter]?.fromDate ?? '',
      toDate: evaluationInputPeriods[activeQuarter]?.toDate ?? '',
    });
    setShowEvaluationPeriodModal(true);
  };

  const handleSaveEvaluationInputPeriod = async () => {
    if (!evaluationPeriodForm.fromDate || !evaluationPeriodForm.toDate) {
      showNotification('From / To を入力してください。', 'error');
      return;
    }
    if (evaluationPeriodForm.fromDate > evaluationPeriodForm.toDate) {
      showNotification('To は From 以降の日付を入力してください。', 'error');
      return;
    }
    const nextEvaluationInputPeriods = {
      ...evaluationInputPeriods,
      [activeQuarter]: {
        fromDate: evaluationPeriodForm.fromDate,
        toDate: evaluationPeriodForm.toDate,
      },
    };

    const rangesOverlap = (
      left: { fromDate: string; toDate: string },
      right: { fromDate: string; toDate: string },
    ) => left.fromDate <= right.toDate && right.fromDate <= left.toDate;
    const firstHalfRange = nextEvaluationInputPeriods.first_half;
    const secondHalfRange = nextEvaluationInputPeriods.second_half;
    if (
      firstHalfRange?.fromDate &&
      firstHalfRange?.toDate &&
      secondHalfRange?.fromDate &&
      secondHalfRange?.toDate &&
      rangesOverlap(firstHalfRange, secondHalfRange)
    ) {
      showNotification('上期と下期の評価入力期間が重複しています。期間を見直してください。', 'error');
      return;
    }

    const sortedTerms = [...scheduleTermOptions].sort((a, b) => a.termNo - b.termNo);
    const currentTerm = sortedTerms.find((term) => term.termNo === scheduleTermNo);
    const nextTerm = sortedTerms.find((term) => term.termNo > scheduleTermNo);
    if (
      currentTerm &&
      nextTerm &&
      secondHalfRange?.fromDate &&
      secondHalfRange?.toDate &&
      secondHalfRange.toDate >= nextTerm.startDate
    ) {
      showNotification(
        `第${currentTerm.termNo}期 下期の評価入力期間が、第${nextTerm.termNo}期 上期開始日（${nextTerm.startDate}）に重複しています。`,
        'error',
      );
      return;
    }

    setEvaluationInputPeriods(nextEvaluationInputPeriods);
    setShowEvaluationPeriodModal(false);
    await saveCurrentScheduleSettings('評価入力期間を更新しました。', {
      evaluationInputPeriods: nextEvaluationInputPeriods,
    });
  };
  const handleScheduleChange = (id, field, value) => {
    if (field === 'date' && !isDateWithinQuarterWindow(activeQuarter, value)) {
      showNotification(`${quarterLabelMap[activeQuarter]} の期間内の日付を入力してください。`, 'error');
      return;
    }
    setScheduleData((prev) => ({
      ...prev,
      [activeQuarter]: (prev[activeQuarter] ?? []).map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    }));
  };
  const handleAddScheduleItem = () => {
    const currentRows = scheduleData[activeQuarter] ?? [];
    const nextTempId = currentRows.reduce((min, row) => (row.id < min ? row.id : min), 0) - 1;
    const defaultDate =
      evaluationInputPeriods[activeQuarter]?.fromDate ?? formatDateToLocalYmd(new Date());
    setScheduleItemModalMode('create');
    setEditingScheduleItemId(null);
    setScheduleItemForm({
      id: nextTempId,
      item: '新規スケジュール',
      date: defaultDate,
      note: '',
      alertEnabled: false,
      daysBefore: 3,
      sendTime: '09:00',
      message: '',
    });
    setShowScheduleItemModal(true);
  };
  const handleOpenScheduleEditModal = (rowId: number) => {
    const row = (scheduleData[activeQuarter] ?? []).find((item) => item.id === rowId);
    if (!row) return;
    const alert = (alertSettings[activeQuarter] ?? []).find((item) => item.itemId === rowId);
    setScheduleItemModalMode('edit');
    setEditingScheduleItemId(rowId);
    setScheduleItemForm({
      id: row.id,
      item: row.item,
      date: row.date,
      note: row.note ?? '',
      alertEnabled: Boolean(alert),
      daysBefore: alert?.daysBefore ?? 3,
      sendTime: alert?.sendTime ?? '09:00',
      message: alert?.message ?? '',
    });
    setShowScheduleItemModal(true);
  };
  const handleSaveScheduleItemModal = async () => {
    if (!scheduleItemForm.item.trim() || !scheduleItemForm.date) {
      showNotification('項目名と日付を入力してください。', 'error');
      return;
    }
    if (scheduleItemForm.alertEnabled && !scheduleItemForm.message.trim()) {
      showNotification('アラートを有効にする場合はメッセージを入力してください。', 'error');
      return;
    }
    if (scheduleItemForm.alertEnabled && !scheduleItemForm.sendTime) {
      showNotification('アラート送信時刻を入力してください。', 'error');
      return;
    }

    const nextScheduleData = (() => {
      const current = scheduleData[activeQuarter] ?? [];
      if (scheduleItemModalMode === 'create') {
        return {
          ...scheduleData,
          [activeQuarter]: [
            ...current,
            {
              id: scheduleItemForm.id,
              item: scheduleItemForm.item.trim(),
              date: scheduleItemForm.date,
              note: scheduleItemForm.note,
            },
          ],
        };
      }
      return {
        ...scheduleData,
        [activeQuarter]: current.map((row) =>
          row.id === editingScheduleItemId
            ? {
                ...row,
                item: scheduleItemForm.item.trim(),
                date: scheduleItemForm.date,
                note: scheduleItemForm.note,
              }
            : row,
        ),
      };
    })();

    const nextAlertSettings = (() => {
      const current = alertSettings[activeQuarter] ?? [];
      const targetItemId = scheduleItemModalMode === 'create' ? scheduleItemForm.id : editingScheduleItemId;
      if (!targetItemId) return alertSettings;

      const existing = current.find((item) => item.itemId === targetItemId);
      if (!scheduleItemForm.alertEnabled) {
        return {
          ...alertSettings,
          [activeQuarter]: current.filter((item) => item.itemId !== targetItemId),
        };
      }

      if (existing) {
        return {
          ...alertSettings,
          [activeQuarter]: current.map((item) =>
            item.itemId === targetItemId
              ? {
                  ...item,
                  enabled: true,
                  daysBefore: Number(scheduleItemForm.daysBefore || 0),
                  sendTime: scheduleItemForm.sendTime,
                  message: scheduleItemForm.message.trim(),
                }
              : item,
          ),
        };
      }

      const nextId = current.reduce((max, item) => Math.max(max, item.id), 0) + 1;
      return {
        ...alertSettings,
        [activeQuarter]: [
          ...current,
          {
            id: nextId,
            enabled: true,
            itemId: targetItemId,
            daysBefore: Number(scheduleItemForm.daysBefore || 0),
            sendTime: scheduleItemForm.sendTime,
            message: scheduleItemForm.message.trim(),
          },
        ],
      };
    })();

    setScheduleData(nextScheduleData);
    setAlertSettings(nextAlertSettings);
    setShowScheduleItemModal(false);
    await saveCurrentScheduleSettings(
      scheduleItemModalMode === 'create' ? 'スケジュール項目を追加しました。' : 'スケジュール項目を更新しました。',
      {
        scheduleData: nextScheduleData,
        alertSettings: nextAlertSettings,
      },
    );
  };
  const handleDeleteScheduleItem = () => {
    if (editingScheduleItemId === null) return;
    openGenericConfirm({
      title: 'スケジュール項目削除の確認',
      message: 'このスケジュール項目を削除します。よろしいですか？',
      confirmText: '削除する',
      tone: 'danger',
      onConfirm: async () => {
        const nextScheduleData = {
          ...scheduleData,
          [activeQuarter]: (scheduleData[activeQuarter] ?? []).filter((row) => row.id !== editingScheduleItemId),
        };
        const nextAlertSettings = {
          ...alertSettings,
          [activeQuarter]: (alertSettings[activeQuarter] ?? []).filter((alert) => alert.itemId !== editingScheduleItemId),
        };
        setScheduleData(nextScheduleData);
        setAlertSettings(nextAlertSettings);
        setShowScheduleItemModal(false);
        setEditingScheduleItemId(null);
        await saveCurrentScheduleSettings('スケジュール項目を削除しました。', {
          scheduleData: nextScheduleData,
          alertSettings: nextAlertSettings,
        });
      },
    });
    setShowScheduleItemModal(false);
  };
  const handleAlertSettingChange = (
    alertId: number,
    field: 'enabled' | 'itemId' | 'daysBefore' | 'sendTime' | 'message',
    value: boolean | string | number,
  ) =>
    setAlertSettings((prev) => ({
      ...prev,
      [activeQuarter]: (prev[activeQuarter] ?? []).map((alert) =>
        alert.id === alertId
          ? {
              ...alert,
              [field]:
                field === 'daysBefore' || field === 'itemId' ? Number(value) : value,
            }
          : alert,
      ),
    }));
  const handleAddAlertSetting = () => {
    const quarterItems = scheduleData[activeQuarter] ?? [];
    const defaultItemId = quarterItems[0]?.id ?? 1;
    const nextId =
      (alertSettings[activeQuarter] ?? []).reduce((max, item) => Math.max(max, item.id), 0) + 1;
    setAlertSettings((prev) => ({
      ...prev,
      [activeQuarter]: [
        ...(prev[activeQuarter] ?? []),
        {
          id: nextId,
          enabled: true,
          itemId: defaultItemId,
          daysBefore: 3,
          sendTime: '09:00',
          message: '',
        },
      ],
    }));
  };
  const handleDeleteAlertSetting = (alertId: number) =>
    setAlertSettings((prev) => ({
      ...prev,
      [activeQuarter]: prev[activeQuarter].filter((alert) => alert.id !== alertId),
    }));
  const handleRequestDeleteAlertSetting = (alertId: number) => {
    setAlertDeleteTargetId(alertId);
  };
  const handleConfirmDeleteAlertSetting = () => {
    if (alertDeleteTargetId === null) return;
    handleDeleteAlertSetting(alertDeleteTargetId);
    setAlertDeleteTargetId(null);
    showNotification('アラートを削除しました。', 'success');
  };
  const handleCancelDeleteAlertSetting = () => {
    setAlertDeleteTargetId(null);
  };
  const handleAddBehaviorItem = () => { const newId = Math.max(...behaviorData.map(b => b.id), 0) + 1; setBehaviorData([...behaviorData, { id: newId, item: '新規評価項目', def: '評価基準の定義を入力してください', criteria: { 0: '', 1: '', 2: '', 3: '', 4: '' }, weight: 0, selfScore: 0, selfComment: '', evaluatorScore: 0, evaluatorComment: '' }]); };
  const handleDeleteBehaviorItem = (id) => {
    openGenericConfirm({
      title: '行動項目削除の確認',
      message: 'この行動項目を削除します。よろしいですか？',
      confirmText: '削除する',
      tone: 'danger',
      onConfirm: () => {
        setBehaviorData((prev) => prev.filter((item) => item.id !== id));
        showNotification('行動項目を削除しました。', 'success');
      },
    });
  };
  const handleSaveBehavior = async () => {
    try {
      setIsBehaviorConfigSaving(true);
      setBehaviorConfigErrorMessage('');
      const totalWeight = behaviorData.reduce((sum, item) => sum + Number(item.weight || 0), 0);
      if (totalWeight !== 100) {
        showNotification(`行動評価ウェイト合計が100%ではありません（現在: ${totalWeight}%）`, 'error');
        return;
      }
      const savedItems = await replaceBehaviorItemList({
        positionCategory: behaviorItemPositionCategory,
        items: behaviorData.map((item) => ({
          id: item.id,
          item: item.item,
          def: item.def,
          weight: Number(item.weight || 0),
          max: Number(item.max || 4),
          criteria: {
            '0': String(item.criteria?.[0] ?? ''),
            '1': String(item.criteria?.[1] ?? ''),
            '2': String(item.criteria?.[2] ?? ''),
            '3': String(item.criteria?.[3] ?? ''),
            '4': String(item.criteria?.[4] ?? ''),
          },
        })),
      });
      setBehaviorData(mapBehaviorConfigToUiData(savedItems));
      showNotification('行動項目設定を更新しました。', 'success');
    } catch (error) {
      setBehaviorConfigErrorMessage(
        error instanceof Error ? error.message : '行動項目設定の更新に失敗しました',
      );
    } finally {
      setIsBehaviorConfigSaving(false);
    }
  };
  
  const handleAddUser = async () => { 
    if (!newUser.name || !newUser.email) return;
    try {
      setAccountErrorMessage('');
      await createAccount({
        email: newUser.email,
        name: newUser.name,
        roleId: newUser.roleId,
        positionId: newUser.positionId,
        department: null,
      });
      setNewUser({
        name: '',
        email: '',
        roleId: 2,
        positionId: null,
      });
      setShowUserModal(false);
      await loadAccountManagementData();
      await loadStoreAssignmentBoard();
      showNotification('ユーザーを作成し、初期パスワードをメール送信しました。', 'success');
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : 'ユーザー作成に失敗しました',
      );
    }
  };
  const handleDeleteUser = async (id: string) => {
    try {
      setAccountErrorMessage('');
      await deleteAccount(id);
      await loadAccountManagementData();
      await loadStoreAssignmentBoard();
      showNotification('ユーザーを削除しました。', 'success');
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : 'ユーザー削除に失敗しました',
      );
    }
  };
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    if (!editingUser.name || !editingUser.email) {
      showNotification('氏名とメールアドレスは必須です。', 'error');
      return;
    }

    try {
      setAccountErrorMessage('');
      await updateAccountApi(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        roleId: editingUser.roleId,
        positionId: editingUser.positionId,
        department: null,
      });
      await loadAccountManagementData();
      await loadStoreAssignmentBoard();
      setShowEditUserModal(false);
      setEditingUser(null);
      showNotification('アカウント情報を更新しました。', 'success');
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : 'アカウント更新に失敗しました',
      );
    }
  };
  const handleRequestDeleteEditingUser = () => {
    setShowEditDeleteConfirmModal(true);
  };
  const handleCancelDeleteEditingUser = () => {
    setShowEditDeleteConfirmModal(false);
  };
  const handleConfirmDeleteEditingUser = async () => {
    if (!editingUser) return;
    await handleDeleteUser(editingUser.id);
    setShowEditDeleteConfirmModal(false);
    setShowEditUserModal(false);
    setEditingUser(null);
  };
  const handleCreatePositionMaster = async () => {
    if (!newPositionDraft.positionName.trim()) {
      showNotification('役職名を入力してください。', 'error');
      return;
    }
    try {
      setAccountErrorMessage('');
      await createPositionMaster({
        positionName: newPositionDraft.positionName.trim(),
      });
      setNewPositionDraft({
        positionName: '',
      });
      setShowCreatePositionModal(false);
      await loadAccountManagementData();
      showNotification('役職を追加しました。', 'success');
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : '役職の追加に失敗しました',
      );
    }
  };
  const handleUpdatePositionMaster = async (input: {
    id: number;
    positionName: string;
  }) => {
    if (!input.positionName.trim()) {
      showNotification('役職名を入力してください。', 'error');
      return;
    }
    try {
      setAccountErrorMessage('');
      await updatePositionMaster(input.id, {
        positionName: input.positionName.trim(),
      });
      await loadAccountManagementData();
      showNotification('役職を更新しました。', 'success');
    } catch (error) {
      setAccountErrorMessage(
        error instanceof Error ? error.message : '役職の更新に失敗しました',
      );
    }
  };
  const handleDeletePositionMaster = (positionId: number) => {
    openGenericConfirm({
      title: '役職削除の確認',
      message: 'この役職を削除します。よろしいですか？',
      confirmText: '削除する',
      tone: 'danger',
      onConfirm: async () => {
        try {
          setAccountErrorMessage('');
          await deletePositionMaster(positionId);
          await loadAccountManagementData();
          showNotification('役職を削除しました。', 'success');
        } catch (error) {
          setAccountErrorMessage(
            error instanceof Error ? error.message : '役職の削除に失敗しました',
          );
        }
      },
    });
  };
  const handleSaveEditingPosition = async () => {
    if (!editingPosition) return;
    await handleUpdatePositionMaster(editingPosition);
    setShowEditPositionModal(false);
    setEditingPosition(null);
  };
  const handleRequestDeleteEditingPosition = () => {
    if (!editingPosition) return;
    openGenericConfirm({
      title: '役職削除の確認',
      message: 'この役職を削除します。よろしいですか？',
      confirmText: '削除する',
      tone: 'danger',
      onConfirm: async () => {
        try {
          setAccountErrorMessage('');
          await deletePositionMaster(editingPosition.id);
          await loadAccountManagementData();
          showNotification('役職を削除しました。', 'success');
          setShowEditPositionModal(false);
          setEditingPosition(null);
        } catch (error) {
          setAccountErrorMessage(
            error instanceof Error ? error.message : '役職の削除に失敗しました',
          );
        }
      },
    });
  };
  const handleAssignUserToStoreBoard = async ({
    userId,
  }: {
    userId: string;
  }) => {
    if (!selectedStoreForAssignment) return;
    try {
      setStoreAssignmentErrorMessage('');
      setIsStoreAssignmentSaving(true);
      await assignUserToStore({
        userId,
        storeId: selectedStoreForAssignment.id,
      });
      await loadStoreAssignmentBoard();
      await loadAccountManagementData();
    } catch (error) {
      setStoreAssignmentErrorMessage(
        error instanceof Error ? error.message : '配属更新に失敗しました',
      );
    } finally {
      setIsStoreAssignmentSaving(false);
    }
  };
  const handleUnassignUserFromStoreBoard = async (userId: string) => {
    if (!selectedStoreForAssignment) return;
    try {
      setStoreAssignmentErrorMessage('');
      setIsStoreAssignmentSaving(true);
      await unassignUserFromStore(userId, selectedStoreForAssignment.id);
      await loadStoreAssignmentBoard();
      await loadAccountManagementData();
    } catch (error) {
      setStoreAssignmentErrorMessage(
        error instanceof Error ? error.message : '配属解除に失敗しました',
      );
    } finally {
      setIsStoreAssignmentSaving(false);
    }
  };
  const handleOpenStoreAssignmentModal = async (storeId: number) => {
    preserveMainScrollPosition();
    if (storeAssignmentStoreList.length === 0) {
      try {
        const boardData = await fetchStoreAssignmentBoard();
        setStoreAssignmentStoreList(boardData.stores);
        setStoreAssignmentUserList(boardData.users);
        setStoreAssignmentList(boardData.assignments);
        const loadedStore = boardData.stores.find((storeItem) => storeItem.id === storeId);
        if (!loadedStore) return;
        setSelectedStoreForAssignment(loadedStore);
        setShowStoreAssignmentModal(true);
        return;
      } catch (error) {
        setStoreAssignmentErrorMessage(
          error instanceof Error ? error.message : '配属データの取得に失敗しました',
        );
        return;
      }
    }
    const targetStore = storeAssignmentStoreList.find((storeItem) => storeItem.id === storeId);
    if (!targetStore) return;
    setSelectedStoreForAssignment(targetStore);
    setShowStoreAssignmentModal(true);
  };
  const handleRequestDeleteStore = () => {
    if (!selectedStoreForAssignment) return;
    const hasAssignments = storeAssignmentList.some(
      (assignment) => assignment.storeId === selectedStoreForAssignment.id,
    );
    if (hasAssignments) {
      showNotification('配属ユーザーがいるため削除できません。', 'error');
      return;
    }
    setStoreDeleteTarget(selectedStoreForAssignment);
  };
  const handleCancelDeleteStore = () => {
    setStoreDeleteTarget(null);
  };
  const handleConfirmDeleteStore = async () => {
    if (!storeDeleteTarget) return;
    try {
      setStoreErrorMessage('');
      await deleteStoreApi(storeDeleteTarget.id);
      await loadStoreMasterList();
      await loadStoreAssignmentBoard();
      if (activeTab === 'admin_targets') {
        await loadStoreTargetData(activeTargetTermNo);
      }
      setShowStoreAssignmentModal(false);
      setSelectedStoreForAssignment(null);
      setStoreDeleteTarget(null);
      showNotification('店舗を削除しました。', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : '店舗削除に失敗しました';
      setStoreErrorMessage(message);
      showNotification(message, 'error');
    }
  };
  const handleAddEvaluator = async () => {
    if (!selectedNewEvaluator) return;
    try {
      setEvaluationLineErrorMessage('');
      await createEvaluationEvaluator({ userId: selectedNewEvaluator });
      await loadEvaluationLineData();
      setShowAddEvaluatorModal(false);
      setSelectedNewEvaluator('');
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '評価者追加に失敗しました',
      );
    }
  };
  
  const executeAddDepartment = async () => {
    if (!newDepartmentName) return;
    if (departments.includes(newDepartmentName)) {
      showNotification('既に存在する店舗名です。', 'error');
      return;
    }
    try {
      setStoreErrorMessage('');
      await createStore({ storeName: newDepartmentName });
      await loadStoreMasterList();
      await loadStoreAssignmentBoard();
      if (activeTab === 'admin_targets') {
        await loadStoreTargetData(activeTargetTermNo);
      }
      setNewDepartmentName('');
      setShowAddDepartmentModal(false);
    } catch (error) {
      setStoreErrorMessage(
        error instanceof Error ? error.message : '店舗追加に失敗しました',
      );
    }
  };

  const onDragStart = (e, userId) => { e.dataTransfer.setData('userId', userId); setDraggingUserId(userId); };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (e, evaluatorId) => {
    e.preventDefault();
    e.stopPropagation();
    const userId = e.dataTransfer.getData('userId');
    if (!userId || userId === evaluatorId) return;
    try {
      setEvaluationLineErrorMessage('');
      await upsertEvaluationLine({
        evaluatorUserId: evaluatorId,
        evaluateeUserId: userId,
      });
      await loadEvaluationLineData();
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '評価ライン更新に失敗しました',
      );
    } finally {
      setDraggingUserId(null);
    }
  };
  const onDropToCreateEvaluator = async (e) => {
    e.preventDefault();
    const userId = e.dataTransfer.getData('userId');
    if (!userId) return;
    try {
      setEvaluationLineErrorMessage('');
      await createEvaluationEvaluator({ userId });
      await loadEvaluationLineData();
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '評価者追加に失敗しました',
      );
    } finally {
      setDraggingUserId(null);
    }
  };
  const onRemoveFromEvaluator = async (evaluatorId, userId) => {
    if (!evalRelations[evaluatorId]?.includes(userId)) return;
    try {
      setEvaluationLineErrorMessage('');
      await removeEvaluationLineByEvaluatee(userId);
      await loadEvaluationLineData();
      showNotification('被評価者を解除しました。', 'success');
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '被評価者解除に失敗しました',
      );
    }
  };
  const handleRequestRemoveEvaluatee = (evaluatorId: string, userId: string) => {
    openGenericConfirm({
      title: '被評価者解除の確認',
      message: 'この被評価者を解除します。よろしいですか？',
      confirmText: '解除する',
      tone: 'danger',
      onConfirm: async () => {
        await onRemoveFromEvaluator(evaluatorId, userId);
      },
    });
  };
  const onRemoveEvaluator = async (evaluatorId) => {
    try {
      setEvaluationLineErrorMessage('');
      await deleteEvaluationEvaluator(evaluatorId);
      await loadEvaluationLineData();
      if (selectedEvaluatorIdForLineModal === evaluatorId) {
        setSelectedEvaluatorIdForLineModal(null);
      }
      showNotification('評価者設定を解除しました。', 'success');
    } catch (error) {
      setEvaluationLineErrorMessage(
        error instanceof Error ? error.message : '評価者解除に失敗しました',
      );
    }
  };
  const handleRequestRemoveEvaluator = (evaluatorId: string) => {
    setEvaluatorDeleteTargetId(evaluatorId);
  };
  const handleCancelRemoveEvaluator = () => {
    setEvaluatorDeleteTargetId(null);
  };
  const handleConfirmRemoveEvaluator = async () => {
    if (!evaluatorDeleteTargetId) return;
    await onRemoveEvaluator(evaluatorDeleteTargetId);
    setEvaluatorDeleteTargetId(null);
  };
  const onOpenEvaluatorLineModal = (evaluatorId) => {
    preserveMainScrollPosition();
    setSelectedEvaluatorIdForLineModal(evaluatorId);
  };
  const onCloseEvaluatorLineModal = () => {
    setSelectedEvaluatorIdForLineModal(null);
    setDraggingUserId(null);
  };
  const onDropToSelectedEvaluator = async (e) => {
    if (!selectedEvaluatorIdForLineModal) return;
    await onDrop(e, selectedEvaluatorIdForLineModal);
  };
  const onDropToUnassignedFromSelectedEvaluator = async (e) => {
    if (!selectedEvaluatorIdForLineModal) return;
    e.preventDefault();
    const userId = e.dataTransfer.getData('userId');
    if (!userId) return;
    await onRemoveFromEvaluator(selectedEvaluatorIdForLineModal, userId);
    setDraggingUserId(null);
  };

  const getEvaluationSubmissionKey = (userId: string, termNo: number, period: ScheduleQuarter) =>
    `${userId}:${termNo}:${period}`;
  const getAssignedStoreId = (userId: string): number | null => {
    const assignment = storeAssignmentList.find((item) => item.userId === userId);
    return assignment?.storeId ?? null;
  };
  const resolveRankByScore = (score: number): string | null => {
    if (!Number.isFinite(score)) return null;
    const sorted = [...rankThresholds].sort((a, b) => b.minScore - a.minScore);
    const matched = sorted.find((item) => score >= Number(item.minScore));
    return matched?.rank ?? null;
  };
  const isSubmissionApproved = (submission: SubmittedEvaluationSnapshot) => Boolean(submission.isFinalized);
  const handleSubmissionApprovalAction = async (submission: SubmittedEvaluationSnapshot) => {
    const currentlyApproved = isSubmissionApproved(submission);
    if (currentlyApproved) {
      showNotification('承認済みの評価は解除できません。', 'error');
      return;
    }
    const hasAnyScore =
      Number(submission.performanceScore ?? 0) !== 0 ||
      Number(submission.behaviorScore ?? 0) !== 0 ||
      Number(submission.adjustmentScore ?? 0) !== 0 ||
      Number(submission.executiveAdjustmentScore ?? 0) !== 0 ||
      Number(submission.totalScore ?? 0) !== 0;
    if (!submission.hasSnapshot && !hasAnyScore) return;

    setApprovalActionLoadingKey(submission.submissionKey);
    try {
      const rank = resolveRankByScore(Number(submission.totalScore ?? 0));
      const saveResult = await saveFinalEvaluation({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
        isFinalized: true,
        userName: submission.userName,
        department: submission.department,
        role: submission.role,
        performanceScore: submission.performanceScore,
        behaviorScore: submission.behaviorScore,
        adjustmentScore: submission.adjustmentScore,
        executiveAdjustmentScore: submission.executiveAdjustmentScore,
        totalScore: submission.totalScore,
        rank,
      });

      const currentEvaluation = await fetchFinalEvaluation({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
      });
      const hasAiComment =
        currentEvaluation.aiSummary.trim().length > 0 ||
        currentEvaluation.aiStrength.trim().length > 0 ||
        currentEvaluation.aiImprovement.trim().length > 0;

      if (!hasAiComment) {
        const aiResult = await generateFinalEvaluationAiComment({
          userId: submission.userId,
          termNo: submission.termNo,
          period: submission.period,
          userName: submission.userName,
          performanceScore: submission.performanceScore,
          behaviorScore: submission.behaviorScore,
          adjustmentScore: submission.adjustmentScore,
          executiveAdjustmentScore: submission.executiveAdjustmentScore,
          totalScore: submission.totalScore,
        });
        await saveFinalEvaluationAiComment({
          userId: submission.userId,
          termNo: submission.termNo,
          period: submission.period,
          summary: aiResult.result.summary,
          strength: aiResult.result.strength,
          improvement: aiResult.result.improvement,
        });
      }

      setSubmittedEvaluations((prev) =>
        prev.map((row) =>
          row.submissionKey === submission.submissionKey
            ? {
              ...row,
              hasSnapshot: true,
              isFinalized: true,
              submittedAt: saveResult.saved.finalizedAt ?? new Date().toISOString(),
              rank: saveResult.saved.rank ?? rank,
            }
            : row,
        ),
      );
      await loadFinalEvaluationSubmissions();
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : 'AIコメントの生成に失敗しました',
        'error',
      );
    } finally {
      setApprovalActionLoadingKey(null);
    }
  };

  const loadFinalEvaluationSubmissions = async () => {
    const requestSeq = ++adminApprovalLoadSeqRef.current;
    setIsAdminApprovalLoading(true);
    try {
      const result = await fetchFinalEvaluationSubmissions({
        onlyFinalized: false,
      });
      if (requestSeq !== adminApprovalLoadSeqRef.current) return;
      setSubmittedEvaluations(result.rows.map((row) => ({ ...row, hasSnapshot: true })));
    } catch (error) {
      if (requestSeq !== adminApprovalLoadSeqRef.current) return;
      showNotification(
        error instanceof Error ? error.message : '評価スナップショットの取得に失敗しました',
        'error',
      );
    } finally {
      if (requestSeq !== adminApprovalLoadSeqRef.current) return;
      setIsAdminApprovalLoading(false);
    }
  };

  const finalizeYearApproval = (year) => {
    openGenericConfirm({
      title: '年度確定の確認',
      message: `${year}年度の評価を確定しますか？`,
      confirmText: '確定する',
      tone: 'primary',
      onConfirm: async () => {
        setFinalizedYears((prev) => ({ ...prev, [year]: true }));
        showNotification(`${year}年度の評価を確定しました。`, 'success');
        try {
          const periodLabel = quarterLabelMap[adminApprovalPeriod] ?? '';
          await pushLineMessage({
            message: `第${activeTargetTermNo}期 ${periodLabel}の評価が確定しました。評価を確認してください。（テストです）`,
            onlyFollowing: true,
          });
          showNotification('LINE通知を送信しました。', 'success');
        } catch (error) {
          showNotification(
            error instanceof Error ? `LINE通知の送信に失敗しました: ${error.message}` : 'LINE通知の送信に失敗しました。',
            'error',
          );
        }
      },
    });
  };

  const getTodayString = () => new Date().toISOString().split('T')[0];

  const loadMyDailyReports = async () => {
    if (!authenticatedUserId) return;
    setIsDailyReportLoading(true);
    setDailyReportErrorMessage('');
    try {
      const reports = await fetchDailyReports(authenticatedUserId);
      setDailyReports(reports);
      setDailyReportHistoryVisibleCount(5);
      const today = getTodayString();
      const todayReport = reports.find((report) => report.reportDate === today);
      setDailyReportDraft({
        reportDate: today,
        todayWork: todayReport?.todayWork ?? '',
        nextTasks: todayReport?.nextTasks ?? '',
      });
    } catch (error) {
      setDailyReportErrorMessage(
        error instanceof Error ? error.message : '日報の取得に失敗しました',
      );
    } finally {
      setIsDailyReportLoading(false);
    }
  };

  const loadAdminDailyReports = async () => {
    setIsDailyReportLoading(true);
    setDailyReportErrorMessage('');
    try {
      const reports = await fetchAdminDailyReports();
      setDailyReports(reports);
    } catch (error) {
      setDailyReportErrorMessage(
        error instanceof Error ? error.message : '日報一覧の取得に失敗しました',
      );
    } finally {
      setIsDailyReportLoading(false);
    }
  };

  const applyDailyReportDate = (nextDate: string) => {
    const existingReport = dailyReports.find((report) => report.reportDate === nextDate);
    setDailyReportDraft((prev) => ({
      ...prev,
      reportDate: nextDate,
      todayWork: existingReport?.todayWork ?? '',
      nextTasks: existingReport?.nextTasks ?? '',
    }));
    if (/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) {
      setDailyReportCalendarMonth(nextDate.slice(0, 7));
    }
    setIsDailyReportCalendarOpen(false);
  };

  const applyAdminReportFilterDate = (nextDate: string) => {
    setReportFilterDate(nextDate);
    if (/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) {
      setAdminReportCalendarMonth(nextDate.slice(0, 7));
    }
    setIsAdminReportCalendarOpen(false);
  };

  const jumpToDailyReportInput = (reportDate: string) => {
    applyDailyReportDate(reportDate);
    requestAnimationFrame(() => {
      dailyReportInputCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const handleDailySubmit = async () => {
    if (!authenticatedUserId) return;
    if (!dailyReportDraft.todayWork.trim()) {
      showNotification('本日行ったことを入力してください。', 'error');
      return;
    }

    try {
      setIsDailyReportSaving(true);
      setDailyReportErrorMessage('');
      await upsertDailyReport({
        userId: authenticatedUserId,
        reportDate: dailyReportDraft.reportDate || getTodayString(),
        todayWork: dailyReportDraft.todayWork.trim(),
        nextTasks: dailyReportDraft.nextTasks.trim(),
      });
      await loadMyDailyReports();
      showNotification('日報を保存しました。', 'success');
    } catch (error) {
      setDailyReportErrorMessage(
        error instanceof Error ? error.message : '日報の保存に失敗しました',
      );
    } finally {
      setIsDailyReportSaving(false);
    }
  };

  const generateAIComment = async () => {
    setIsGeneratingAI(true);
    try {
      const targetUser = accountList.find((account) => account.id === selectedTargetUserId);
      const itemsText = behaviorData.map(item => `- ${item.item}: スコア ${item.evaluatorScore}/4\n  定義: ${item.def}\n  上長コメント: ${item.evaluatorComment || 'なし'}`).join('\n');
      const prompt = `あなたは人事評価のプロフェッショナルなマネージャーです。以下の部下の評価データを分析し、建設的なフィードバックコメントを作成してください。\n【評価対象者】${targetUser ? targetUser.name : '部下'}\n【評価データ】\n${itemsText}`;
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
      });
      if (!response.ok) throw new Error('API request failed');
      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text || "生成失敗";
      setOverallFeedback(prev => prev ? prev + "\n\n" + generatedText : generatedText);
    } catch (error) {
      console.error(error);
      showNotification('AI生成エラー', 'error');
    } finally { setIsGeneratingAI(false); }
  };

  // --- Render Functions ---
  const renderAdminSchedule = () => {
    const termTabs =
      scheduleTermOptions.length > 0
        ? scheduleTermOptions
        : [{ termNo: scheduleTermNo }];
    const currentScheduleTermIndex = termTabs.findIndex((term) => term.termNo === scheduleTermNo);
    const handleTermTabChange = async (termNo: number) => {
      if (termNo === scheduleTermNo || isScheduleLoading) return;
      await loadScheduleSettingData(termNo);
    };
    const currentAlerts = alertSettings[activeQuarter] ?? [];
    const currentQuarterItems = [...(scheduleData[activeQuarter] ?? [])].sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    const getAlertInfoForItem = (itemId: number, targetDate: string) => {
      const alert = currentAlerts.find((item) => item.itemId === itemId && item.enabled);
      if (!alert) return null;
      const base = new Date(`${targetDate}T00:00:00+09:00`);
      if (Number.isNaN(base.getTime())) return null;
      base.setUTCDate(base.getUTCDate() - alert.daysBefore);
      const y = base.getUTCFullYear();
      const m = String(base.getUTCMonth() + 1).padStart(2, '0');
      const d = String(base.getUTCDate()).padStart(2, '0');
      return {
        text: `${y}-${m}-${d} ${alert.sendTime} に通知`,
      };
    };

    return (
      <div className="-mx-4 sm:-mx-6 md:-mx-8">
        <div className="sticky top-0 z-[200] isolate relative bg-slate-50 border-b border-gray-200 pb-4 shadow-sm px-4 sm:px-6 md:px-8 before:content-[''] before:absolute before:left-0 before:right-0 before:-top-24 before:h-24 before:bg-slate-50 before:pointer-events-none">
          <div className="flex justify-between items-center mb-4">
            <SectionTitle isAdminMode>評価スケジュール設定</SectionTitle>
            {isScheduleSaving ? (
              <div className="text-xs font-bold text-indigo-600">保存中...</div>
            ) : <div />}
          </div>

          <div className="flex items-center justify-between gap-3 pb-0">
            <div className="flex flex-wrap items-center gap-2">
              {renderTermNavigator({
                termLabel: `第${scheduleTermNo}期`,
                onPrev: () => {
                  if (currentScheduleTermIndex > 0) {
                    void handleTermTabChange(termTabs[currentScheduleTermIndex - 1].termNo);
                  }
                },
                onNext: () => {
                  if (currentScheduleTermIndex >= 0 && currentScheduleTermIndex < termTabs.length - 1) {
                    void handleTermTabChange(termTabs[currentScheduleTermIndex + 1].termNo);
                  }
                },
                disablePrev: isScheduleLoading || currentScheduleTermIndex <= 0,
                disableNext:
                  isScheduleLoading || currentScheduleTermIndex < 0 || currentScheduleTermIndex >= termTabs.length - 1,
                size: 'compact',
              })}
              {renderQuarterToggle({
                activePeriod: activeQuarter,
                onChange: (period) => setActiveQuarter(period),
                size: 'compact',
              })}
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-6 md:px-8 pt-6 space-y-6">
        {scheduleErrorMessage && (
          <div className="text-red-500 text-sm font-bold">{scheduleErrorMessage}</div>
        )}

        <Card highlight>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold tracking-[0.08em] text-slate-400">現在表示中の期</div>
                  <h3 className="mt-1 flex items-center gap-2 text-base font-extrabold text-slate-700">
                    <Calendar size={18} className="text-indigo-500" />
                    設定期間
                  </h3>
                </div>
              </div>
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-extrabold text-indigo-600">
                    {quarterLabelMap[activeQuarter]}
                  </span>
                  <span className="text-[10px] font-bold text-indigo-500">表示中</span>
                </div>
                <div className="text-sm font-bold text-slate-700">
                  {activeScheduleBasePeriod?.fromDate ?? '-'}
                  <span className="mx-2 text-slate-400">〜</span>
                  {activeScheduleBasePeriod?.toDate ?? '-'}
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="text-xs font-bold tracking-[0.08em] text-slate-400">評価入力期間設定</div>
                <button
                  type="button"
                  onClick={handleOpenEvaluationPeriodModal}
                  disabled={isScheduleSaving || isScheduleLoading}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 shadow-sm transition-all hover:border-indigo-200 hover:text-indigo-600 disabled:opacity-60"
                >
                  <Edit3 size={12} />
                  編集
                </button>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-2.5">
                <div className="flex items-center justify-between gap-4 border-b border-slate-200/80 py-1.5">
                  <span className="text-sm font-bold text-slate-500">開始日</span>
                  <span className="text-base font-extrabold tracking-[0.01em] text-slate-700">
                    {evaluationInputPeriods[activeQuarter]?.fromDate ?? '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 py-1.5">
                  <span className="text-sm font-bold text-slate-500">終了日</span>
                  <span className="text-base font-extrabold tracking-[0.01em] text-slate-700">
                    {evaluationInputPeriods[activeQuarter]?.toDate ?? '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
        
        <Card highlight>
          <div className="flex items-center justify-between mb-4">
             <h3 className="font-bold text-gray-700 flex items-center gap-2"><Calendar size={18} className="text-indigo-500"/> {quarterLabelMap[activeQuarter]} スケジュール</h3>
             <button
               onClick={handleAddScheduleItem}
               disabled={isScheduleSaving || isScheduleLoading}
               className="flex items-center gap-1 bg-indigo-600 text-white px-3 py-1 rounded text-xs font-bold hover:opacity-90 shadow-sm transition-all disabled:opacity-60"
             >
               <Plus size={14} /> 項目追加
             </button>
          </div>
          <div className="overflow-x-auto overflow-y-visible border rounded-lg">
            <table className="w-full text-sm text-left border-collapse overflow-visible">
              <thead className="bg-gray-50 text-gray-600 font-bold border-b">
                <tr>
                  <th className="p-3 w-1/3">項目名</th>
                  <th className="p-3 w-40">日付</th>
                  <th className="p-3">備考</th>
                  <th className="p-3 w-28 text-center">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {currentQuarterItems.map((row) => (
                  <tr key={row.id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="p-3 text-gray-700">
                      <div className="inline-flex items-center gap-2">
                        <span>{row.item}</span>
                        {(() => {
                          const alertInfo = getAlertInfoForItem(row.id, row.date);
                          if (!alertInfo) return null;
                          return (
                            <span className="relative inline-flex items-center group">
                              <span className="inline-flex items-center text-amber-500">
                                <Bell size={14} />
                              </span>
                              <span className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-800 px-3 py-1.5 text-[11px] font-bold text-white shadow-lg group-hover:block">
                                {alertInfo.text}
                              </span>
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="p-3 text-gray-700">{row.date}</td>
                    <td className="p-3 text-gray-700">{row.note}</td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => handleOpenScheduleEditModal(row.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all"
                      >
                        <Edit3 size={12} />
                        編集
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {showEvaluationPeriodModal && (
          <div className="fixed inset-0 z-[300] bg-black/25 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-xl border border-gray-200 shadow-xl">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-extrabold text-gray-800">評価入力期間設定</h3>
                <button
                  onClick={() => setShowEvaluationPeriodModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="px-6 py-5">
                <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-2.5">
                  <div className="flex items-center justify-between gap-4 border-b border-slate-200/80 py-2">
                    <span className="text-sm font-bold text-slate-500">開始日</span>
                    <input
                      type="date"
                      value={evaluationPeriodForm.fromDate}
                      onChange={(e) =>
                        setEvaluationPeriodForm((prev) => ({ ...prev, fromDate: e.target.value }))
                      }
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 outline-none"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 py-2">
                    <span className="text-sm font-bold text-slate-500">終了日</span>
                    <input
                      type="date"
                      value={evaluationPeriodForm.toDate}
                      onChange={(e) =>
                        setEvaluationPeriodForm((prev) => ({ ...prev, toDate: e.target.value }))
                      }
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                <button
                  onClick={() => setShowEvaluationPeriodModal(false)}
                  className="px-4 py-2 rounded-md border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-100"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSaveEvaluationInputPeriod}
                  className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}

        {showScheduleItemModal && (
          <div className="fixed inset-0 z-[300] bg-black/25 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-white rounded-xl border border-gray-200 shadow-xl">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-extrabold text-gray-800">
                  {scheduleItemModalMode === 'create' ? 'スケジュール項目追加' : 'スケジュール項目編集'}
                </h3>
                <button
                  onClick={() => setShowScheduleItemModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">日付</label>
                    <input
                      type="date"
                      value={scheduleItemForm.date}
                      onChange={(e) => setScheduleItemForm((prev) => ({ ...prev, date: e.target.value }))}
                      className="w-full p-2 border border-gray-200 rounded text-gray-700 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">項目名</label>
                  <input
                    type="text"
                    value={scheduleItemForm.item}
                    onChange={(e) => setScheduleItemForm((prev) => ({ ...prev, item: e.target.value }))}
                    className="w-full p-2 border border-gray-200 rounded text-gray-700 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">備考</label>
                  <input
                    type="text"
                    value={scheduleItemForm.note}
                    onChange={(e) => setScheduleItemForm((prev) => ({ ...prev, note: e.target.value }))}
                    className="w-full p-2 border border-gray-200 rounded text-gray-700 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  />
                </div>
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={scheduleItemForm.alertEnabled}
                      onChange={(e) =>
                        setScheduleItemForm((prev) => ({ ...prev, alertEnabled: e.target.checked }))
                      }
                    />
                    この項目にアラートを設定する
                  </label>
                  {scheduleItemForm.alertEnabled && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1">何日前</label>
                          <input
                            type="number"
                            min={0}
                            max={30}
                            value={scheduleItemForm.daysBefore}
                            onChange={(e) =>
                              setScheduleItemForm((prev) => ({
                                ...prev,
                                daysBefore: Number(e.target.value || 0),
                              }))
                            }
                            className="w-full p-2 border border-gray-200 rounded text-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 mb-1">送信時刻</label>
                          <input
                            type="time"
                            value={scheduleItemForm.sendTime}
                            onChange={(e) =>
                              setScheduleItemForm((prev) => ({ ...prev, sendTime: e.target.value }))
                            }
                            className="w-full p-2 border border-gray-200 rounded text-gray-700"
                          />
                        </div>
                      </div>
                      <div className="text-xs font-bold text-indigo-600">
                        通知タイミング: 対象日の {scheduleItemForm.daysBefore} 日前 {scheduleItemForm.sendTime}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">通知メッセージ</label>
                        <textarea
                          value={scheduleItemForm.message}
                          onChange={(e) =>
                            setScheduleItemForm((prev) => ({ ...prev, message: e.target.value }))
                          }
                          placeholder="通知メッセージを入力"
                          rows={3}
                          className="w-full p-2 border border-gray-200 rounded text-gray-700 resize-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                {scheduleItemModalMode === 'edit' && (
                  <button
                    onClick={handleDeleteScheduleItem}
                    className="px-4 py-2 rounded-md border border-red-200 text-sm font-bold text-red-600 hover:bg-red-50 mr-auto"
                  >
                    削除
                  </button>
                )}
                <button
                  onClick={() => setShowScheduleItemModal(false)}
                  className="px-4 py-2 rounded-md border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-100"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSaveScheduleItemModal}
                  className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    );
  };
  
  // 新規追加: 店舗目標管理画面 (月次対応・12ヶ月一括表示)
  const renderAdminTargets = () => {
    const months = [11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const targetTermNos = targetTermOptions.map((term) => term.termNo);
    const currentTargetTermIndex = targetTermNos.findIndex(
      (termNo) => termNo === activeTargetTermNo,
    );
    const canMoveToPrevTargetTerm = currentTargetTermIndex > 0;
    const canMoveToNextTargetTerm =
      currentTargetTermIndex >= 0 && currentTargetTermIndex < targetTermNos.length - 1;
    const yearRows = storeTargets.filter((row) => row.termNo === activeTargetTermNo);
    const storeRows = Array.from(
      new Map(yearRows.map((row) => [row.storeId, { storeId: row.storeId, storeName: row.storeName }])).values(),
    ).sort((a, b) => a.storeId - b.storeId);
    const targetMap = new Map(
      yearRows.map((row) => [`${row.storeId}:${row.month}`, row]),
    );
    const getTarget = (storeId: number, month: number) => targetMap.get(`${storeId}:${month}`);
    const targetDataTabs: { id: AdminTargetDataTab; label: string; description: string }[] = [
      { id: 'actual', label: '実績データ', description: '売上・利益の月次データを管理' },
      { id: 'qsc', label: 'モニターチェック', description: '半年に1回の店舗別モニターチェックを管理' },
      { id: 'qsc_rate', label: 'QSCスコア', description: '半年に1回の店舗別QSC総合得点率を管理' },
      { id: 'crew_es', label: 'クルーESスコア', description: '半年に1回の店舗別クルーESスコアを管理' },
    ];
    const targetSectionTitle =
      adminTargetDataTab === 'actual'
        ? '店舗別 目標・実績データ管理 (月次)'
        : adminTargetDataTab === 'qsc'
          ? '店舗別 モニターチェック管理'
          : adminTargetDataTab === 'qsc_rate'
            ? '店舗別 QSCスコア管理'
          : '店舗別 クルーESスコア管理';
    const qscScoreMap = new Map(
      storeQscScores
        .filter((row) => row.termNo === activeTargetTermNo)
        .map((row) => [`${row.storeId}:${row.period}`, row]),
    );
    const crewEsScoreMap = new Map(
      storeCrewEsScores
        .filter((row) => row.termNo === activeTargetTermNo)
        .map((row) => [`${row.storeId}:${row.period}`, row]),
    );
    const qscRateScoreMap = new Map(
      storeQscRateScores
        .filter((row) => row.termNo === activeTargetTermNo)
        .map((row) => [`${row.storeId}:${row.period}`, row]),
    );
    const activeScoreType: 'qsc' | 'qsc_rate' | 'crew_es' =
      adminTargetDataTab === 'qsc'
        ? 'qsc'
        : adminTargetDataTab === 'qsc_rate'
          ? 'qsc_rate'
          : 'crew_es';
    const activeScoreMap =
      activeScoreType === 'qsc'
        ? qscScoreMap
        : activeScoreType === 'qsc_rate'
          ? qscRateScoreMap
          : crewEsScoreMap;
    const renderStoreScoreTab = (
      scoreType: 'qsc' | 'qsc_rate' | 'crew_es',
      scoreMap: Map<string, StoreHalfScoreRow>,
    ) => (
      <>
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-500">
          <Info size={18} className="mt-0.5 shrink-0 text-indigo-500" />
          <div className="flex-1">
            <p className="mb-1 font-bold text-gray-700">
              第{activeTargetTermNo}期 の{scoreType === 'qsc' ? 'モニターチェック' : scoreType === 'qsc_rate' ? 'QSCスコア' : 'クルーESスコア'}
            </p>
            <p>
              PDFを選択すると、店舗名と{scoreType === 'qsc' ? '得点' : scoreType === 'qsc_rate' ? '総合得点率' : '今回スコア'}を確認してから保存できます。
            </p>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-white text-gray-600 font-bold border-b border-gray-200">
              <tr>
                <th className="p-4 w-56">店舗名</th>
                <th className="p-4 text-center">上期</th>
                <th className="p-4 text-center">下期</th>
                <th className="p-4 text-center">更新日</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {storeRows.map((storeItem) => {
                const firstHalfScore = scoreMap.get(`${storeItem.storeId}:first_half`);
                const secondHalfScore = scoreMap.get(`${storeItem.storeId}:second_half`);
                const latestUpdatedAt =
                  secondHalfScore?.updatedAt || firstHalfScore?.updatedAt || '';

                return (
                  <tr key={`${scoreType}-${storeItem.storeId}`} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-4 font-bold text-slate-700">{storeItem.storeName}</td>
                    <td className="p-4 text-center">
                      <span className="inline-flex min-w-16 justify-center rounded-lg bg-slate-50 px-3 py-1.5 font-extrabold text-slate-700">
                        {typeof firstHalfScore?.score === 'number' ? `${firstHalfScore.score}${scoreType === 'qsc_rate' ? '%' : '点'}` : '-'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex min-w-16 justify-center rounded-lg bg-slate-50 px-3 py-1.5 font-extrabold text-slate-700">
                        {typeof secondHalfScore?.score === 'number' ? `${secondHalfScore.score}${scoreType === 'qsc_rate' ? '%' : '点'}` : '-'}
                      </span>
                    </td>
                    <td className="p-4 text-center text-sm font-medium text-slate-500">
                      {latestUpdatedAt ? latestUpdatedAt.slice(0, 10) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
    
    return (
      <div className="space-y-6">
        <Card highlight>
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <SectionTitle isAdminMode>{targetSectionTitle}</SectionTitle>
            
            <div className="flex flex-wrap items-center gap-4">
              {renderTermNavigator({
                termLabel: `第${activeTargetTermNo}期`,
                onPrev: () => {
                  if (currentTargetTermIndex > 0) {
                    setActiveTargetTermNo(targetTermNos[currentTargetTermIndex - 1]);
                  }
                },
                onNext: () => {
                  if (canMoveToNextTargetTerm) {
                    setActiveTargetTermNo(targetTermNos[currentTargetTermIndex + 1]);
                  }
                },
                disablePrev: !canMoveToPrevTargetTerm,
                disableNext: !canMoveToNextTargetTerm,
                size: 'compact',
              })}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (adminTargetDataTab === 'actual') {
                      handleOpenTargetPdfSelect();
                      return;
                    }
                    handleOpenStoreScorePdfSelect(
                      adminTargetDataTab === 'qsc'
                        ? 'qsc'
                        : adminTargetDataTab === 'qsc_rate'
                          ? 'qsc_rate'
                          : 'crew_es',
                    );
                  }}
                  disabled={
                    adminTargetDataTab === 'actual'
                      ? isStoreTargetImporting
                      : isStoreScoreImporting !== null
                  }
                  className="flex items-center gap-2 bg-amber-600 text-white px-3 py-2 rounded-lg shadow-md hover:bg-amber-700 disabled:opacity-60 font-bold transition-all text-xs"
                >
                  {(adminTargetDataTab === 'actual' ? isStoreTargetImporting : isStoreScoreImporting !== null) ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Upload size={14} />
                  )}
                  PDFインポート
                </button>
                {adminTargetDataTab === 'actual' ? (
                  <button
                    type="button"
                    onClick={() => {
                      void handleSaveStoreTargets();
                    }}
                    disabled={isStoreTargetSaving || isStoreTargetImporting}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-lg shadow-md hover:bg-indigo-700 disabled:opacity-60 font-bold transition-all text-xs"
                  >
                    {isStoreTargetSaving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    {isStoreTargetSaving ? '保存中...' : '保存'}
                  </button>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleImportStoreTargetPdf}
                  className="hidden"
                />
                <input
                  ref={qscFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  onChange={(event) => handleImportStoreScorePdf(event, 'qsc')}
                  className="hidden"
                />
                <input
                  ref={qscRateFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  onChange={(event) => handleImportStoreScorePdf(event, 'qsc_rate')}
                  className="hidden"
                />
                <input
                  ref={crewEsFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  onChange={(event) => handleImportStoreScorePdf(event, 'crew_es')}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
            {targetDataTabs.map((tab) => {
              const isActive = adminTargetDataTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setAdminTargetDataTab(tab.id)}
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    isActive
                      ? 'border-indigo-200 bg-indigo-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-indigo-100 hover:bg-slate-50'
                  }`}
                >
                  <div className={`text-sm font-extrabold ${isActive ? 'text-indigo-600' : 'text-slate-700'}`}>
                    {tab.label}
                  </div>
                  <div className="mt-1 text-xs font-medium text-slate-500">
                    {tab.description}
                  </div>
                </button>
              );
            })}
          </div>

          <div>
          <div className={adminTargetDataTab === 'actual' ? '' : 'hidden'} aria-hidden={adminTargetDataTab !== 'actual'}>
          <>
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-500">
            <Info size={18} className="mt-0.5 shrink-0 text-indigo-500" />
            <div>
              <p className="mb-1 font-bold text-slate-700">第{activeTargetTermNo}期 の売上・利益設定</p>
              <p>PDFを選択後、テンプレート判定OKなら取り込み月（複数可）を選択して反映できます。</p>
            </div>
          </div>
          {isStoreTargetLoading ? (
            <div className="py-10 flex items-center justify-center text-gray-500 gap-2">
              <Loader2 size={16} className="animate-spin" />
              読み込み中...
            </div>
          ) : null}

          {!isStoreTargetLoading ? (
          <>
          <div className="space-y-4 p-4 md:hidden">
            {storeRows.map((storeItem) => (
              <div key={storeItem.storeId} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="font-bold text-gray-700 mb-3">{storeItem.storeName}</div>
                {['売上', '利益'].map((itemType) => (
                  <div key={itemType} className="mb-3 last:mb-0">
                    <div className="text-xs font-bold text-gray-500 mb-2">{itemType}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      {months.map((m) => {
                        const target = getTarget(storeItem.storeId, m);
                        const value = itemType === '売上' ? target?.sales : target?.profit;
                        return (
                          <div key={m} className="bg-gray-50 border border-gray-200 rounded p-2">
                            <div className="text-[10px] text-gray-400">{m}月</div>
                            <div className="font-bold text-gray-700">{value ?? '-'}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="hidden max-h-[500px] overflow-x-auto border-t border-slate-200 shadow-sm md:block">
            <table className="w-full text-sm text-left border-collapse whitespace-nowrap">
              <thead className="bg-white text-gray-600 font-bold border-b border-gray-200 sticky top-0 z-30 shadow-sm">
                <tr>
                  <th className="p-4 w-48 sticky top-0 left-0 bg-white z-40 border-r border-gray-200">店舗名 / 項目</th>
                  {months.map(m => (
                    <th key={m} className="p-3 text-center w-32 min-w-[120px] text-xs text-gray-500 bg-white">{m}月</th>
                  ))}
                  <th className="p-3 text-center w-32 min-w-[120px] font-bold text-indigo-600 bg-white">合計</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {storeRows.map((storeItem) => {
                  let totalSales = 0;
                  let totalProfit = 0;

                  return (
                    <React.Fragment key={storeItem.storeId}>
                      {/* 売上行 */}
                      <tr className="hover:bg-indigo-50/10 transition-colors bg-white">
                        <td className="p-4 font-bold sticky left-0 bg-white border-r border-gray-200 text-gray-800 z-10 group-hover:bg-indigo-50/10 transition-colors border-b-0 pb-1">
                          <div className="flex flex-col">
                            <span>{storeItem.storeName}</span>
                            <span className="text-xs text-gray-400 font-normal mt-1">売上 (千円)</span>
                          </div>
                        </td>
                        {months.map(m => {
                          const targetData = getTarget(storeItem.storeId, m);
                          const value = targetData ? targetData.sales : 0;
                          totalSales += Number(value || 0);

                          return (
                            <td key={`sales-${m}`} className="p-2 text-center border-b-0 pb-1">
                              <input 
                                type="number" 
                                value={value} 
                                onChange={(e) => handleStoreTargetChange(storeItem.storeId, m, 'sales', e.target.value)}
                                className="w-full min-w-[110px] p-1.5 border border-gray-200 rounded text-right text-sm text-gray-700 font-medium focus:ring-2 focus:ring-indigo-200 outline-none transition-all hover:border-gray-300"
                              />
                            </td>
                          );
                        })}
                        <td className="p-3 text-right font-bold text-indigo-700 bg-indigo-50/10 border-l border-indigo-100 border-b-0 pb-1">
                          {totalSales.toLocaleString()}
                        </td>
                      </tr>
                      {/* 利益行 */}
                      <tr className="hover:bg-indigo-50/10 transition-colors bg-gray-50">
                        <td className="p-4 pt-1 font-bold sticky left-0 bg-white border-r border-gray-200 text-gray-800 z-10 group-hover:bg-indigo-50/10 transition-colors border-t-0">
                           <span className="text-xs text-gray-400 font-normal">利益 (千円)</span>
                        </td>
                        {months.map(m => {
                          const targetData = getTarget(storeItem.storeId, m);
                          const value = targetData ? targetData.profit : 0;
                          totalProfit += Number(value || 0);

                          return (
                            <td key={`profit-${m}`} className="p-2 text-center pt-1 border-t-0">
                              <input 
                                type="number" 
                                value={value} 
                                onChange={(e) => handleStoreTargetChange(storeItem.storeId, m, 'profit', e.target.value)}
                                className="w-full min-w-[110px] p-1.5 border border-gray-200 rounded text-right text-sm text-gray-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all hover:border-gray-300 bg-white"
                              />
                            </td>
                          );
                        })}
                         <td className="p-3 pt-1 text-right font-bold text-gray-600 bg-indigo-50/10 border-l border-indigo-100 border-t-0">
                          {totalProfit.toLocaleString()}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          ) : null}
          </>
          </div>
          <div className={adminTargetDataTab === 'actual' ? 'hidden' : ''} aria-hidden={adminTargetDataTab === 'actual'}>
            {renderStoreScoreTab(activeScoreType, activeScoreMap)}
          </div>
          </div>
        </Card>
        {showStoreScorePreviewModal && pendingStoreScorePreviews.length > 0 ? (
          <div className="fixed inset-0 z-[300] bg-black/25 flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-xl border border-gray-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-800">PDFインポート確認</h3>
                  <p className="mt-1 text-sm font-medium text-slate-500">
                    読み取り結果を確認して、保存先の期間を選択してください。
                  </p>
                </div>
                <button
                  onClick={handleCloseStoreScorePreviewModal}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4 px-6 py-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="mb-2 text-xs font-bold tracking-[0.08em] text-slate-400">選択ファイル数</div>
                  <div className="min-w-0 text-sm font-bold text-slate-700 truncate">
                    {pendingStoreScoreFiles.length}件
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="mb-2 text-xs font-bold tracking-[0.08em] text-slate-400">読み取り結果</div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="p-2 text-left">店舗名</th>
                          <th className="p-2 text-right">
                            {pendingStoreScorePreviews[0]?.type === 'qsc'
                              ? '点数'
                              : pendingStoreScorePreviews[0]?.type === 'qsc_rate'
                                ? '総合得点率'
                                : '今回スコア'}
                          </th>
                          <th className="p-2 text-right">PDF日付</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingStoreScorePreviews.map((preview, idx) => (
                          <tr key={`${preview.storeId}-${preview.reportDate}-${idx}`} className="border-t border-slate-100">
                            <td className="p-2 font-bold text-slate-700">{preview.storeName}</td>
                            <td className="p-2 text-right font-extrabold text-slate-800">
                              {preview.score}{preview.type === 'qsc_rate' ? '%' : '点'}
                            </td>
                            <td className="p-2 text-right text-slate-500">{preview.reportDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-bold tracking-[0.08em] text-slate-400">保存先</div>
                      <div className="mt-1 text-sm font-bold text-slate-700">
                        {typeof storeScoreImportOverrideTermNo === 'number' && storeScoreImportOverridePeriod
                          ? `第${storeScoreImportOverrideTermNo}期 ${quarterLabelMap[storeScoreImportOverridePeriod]}`
                          : storeScorePreviewSavePeriods.length > 0
                            ? storeScorePreviewSavePeriods.join(' / ')
                            : 'PDF日付 + term_master で初期設定'}
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-500">一括保存対象</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] font-bold tracking-[0.08em] text-slate-500">
                        期
                      </label>
                      <select
                        value={storeScoreImportOverrideTermNo === '' ? '' : String(storeScoreImportOverrideTermNo)}
                        onChange={(e) =>
                          setStoreScoreImportOverrideTermNo(
                            e.target.value === '' ? '' : Number(e.target.value),
                          )
                        }
                        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700"
                      >
                        {targetTermOptions.map((term) => (
                          <option key={`score-term-${term.termNo}`} value={term.termNo}>
                            第{term.termNo}期
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-bold tracking-[0.08em] text-slate-500">
                        半期
                      </label>
                      <select
                        value={storeScoreImportOverridePeriod}
                        onChange={(e) =>
                          setStoreScoreImportOverridePeriod(
                            e.target.value === '' ? '' : (e.target.value as ScheduleQuarter),
                          )
                        }
                        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700"
                      >
                        <option value="first_half">{quarterLabelMap.first_half}</option>
                        <option value="second_half">{quarterLabelMap.second_half}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t bg-gray-50 px-6 py-4">
                <button
                  onClick={handleCloseStoreScorePreviewModal}
                  className="rounded-md border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSaveStoreScorePreview}
                  disabled={isStoreScoreImporting !== null}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {isStoreScoreImporting !== null ? '保存中...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {showTargetImportModal && (
          <div className="fixed inset-0 z-[300] bg-black/25 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-800">PDFインポート</h3>
                  <p className="mt-1 text-sm font-medium text-slate-500">実績データ</p>
                </div>
                <button
                  onClick={handleCloseTargetImportModal}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4 px-6 py-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="mb-2 text-xs font-bold tracking-[0.08em] text-slate-400">選択ファイル</div>
                  <div className="min-w-0 text-sm font-bold text-slate-700 truncate">
                    {pendingImportFile?.name ?? '未選択'}
                  </div>
                  {pdfReportRef ? (
                    <div className="mt-2 text-xs font-medium text-slate-500">
                      PDF基準: 第{pdfReportRef.termNo}期 {pdfReportRef.month}月
                    </div>
                  ) : null}
                </div>

                {showImportMonthPicker && pendingImportFile ? (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                    <div className="mb-1 text-sm font-bold text-slate-800">取り込み月を選択（複数可）</div>
                    <div className="mb-3 min-w-0 text-xs text-slate-600">
                      <div className="truncate">ファイル: {pendingImportFile.name}</div>
                      <div>{pdfReportRef ? `PDF基準: 第${pdfReportRef.termNo}期 ${pdfReportRef.month}月` : ''}</div>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {importMonthOptions.map((month) => {
                        const selected = selectedImportMonths.includes(month);
                        return (
                          <button
                            key={month}
                            type="button"
                            onClick={() => toggleImportMonth(month)}
                            className={`rounded-md border px-3 py-1.5 text-xs font-bold ${
                              selected
                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                : 'border-gray-300 bg-white text-gray-700'
                            }`}
                          >
                            {month}月
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs font-medium text-slate-500">
                      保存すると選択した月のデータをDBへ即時反映します。
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="flex justify-end gap-2 border-t bg-gray-50 px-6 py-4">
                <button
                  onClick={handleCloseTargetImportModal}
                  className="rounded-md border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleExecuteStoreTargetPdfImport}
                  disabled={isStoreTargetImporting || !pendingImportFile || selectedImportMonths.length === 0}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {isStoreTargetImporting ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- 管理者 業績評価マスタ設定画面 (更新: 詳細展開機能のUI改善) ---
  const renderAdminPerformance = (options?: { embedded?: boolean }) => {
    const totalWeight = performanceData.reduce((sum, item) => sum + item.weight, 0);
    const isValidWeight = totalWeight === 100;
    const content = (
      <>
        <div className="flex justify-between items-end mb-6">
          <SectionTitle isAdminMode>業績項目</SectionTitle>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-gray-500 flex items-center justify-end gap-1">Total Weight <HelpCircle size={12}/></span>
              <div className={`text-2xl font-bold ${isValidWeight ? 'text-indigo-600' : 'text-red-500'}`}>{totalWeight}%</div>
            </div>
            <button
              onClick={handleSavePerformance}
              className={`flex items-center gap-2 text-white px-6 py-2 rounded-lg font-bold shadow-md transition-all text-sm ${isValidWeight ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`}
              disabled={!isValidWeight || isPerformanceConfigSaving}
              title={!isValidWeight ? "ウェイト合計が100%でないため保存できません" : ""}
            >
              <Save size={16} /> {isPerformanceConfigSaving ? '保存中...' : '設定を保存'}
            </button>
          </div>
        </div>
        {isPerformanceConfigLoading ? (
          <p className="text-xs text-gray-500 mb-4">業績項目設定を読み込み中...</p>
        ) : null}
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-8 relative shadow-inner"><div className={`h-full transition-all duration-500 ease-out ${isValidWeight ? 'bg-indigo-500' : 'bg-red-400'}`} style={{ width: `${Math.min(totalWeight, 100)}%` }} /></div>
        
        <div className="space-y-3 p-2">
          {performanceData.map((item) => (
            <div key={item.id} className={`bg-white border rounded-lg shadow-sm overflow-hidden transition-all duration-200 ${expandedPerformanceId === item.id ? 'border-indigo-300 ring-2 ring-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}>
              {(() => {
                const isFixedItem = resolvePerformanceFixedKey(item.fixedKey, item.item) !== null;
                return (
                  <>
              {/* メイン行 (4項目) */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start p-5">
                 <div className="col-span-12 md:col-span-10">
                   <label className="text-xs font-bold text-gray-500 mb-1.5 block">評価項目名</label>
                   <input type="text" value={item.item} onChange={(e) => handlePerformanceChange(item.id, 'item', e.target.value)} readOnly={isFixedItem} className={`w-full p-3 border border-gray-200 rounded-lg font-bold text-gray-700 outline-none transition-all ${isFixedItem ? 'bg-slate-50 cursor-default' : 'focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400'}`} placeholder="項目名を入力"/>
                 </div>
                 <div className="col-span-12 md:col-span-2">
                   <label className="text-xs font-bold text-gray-500 mb-1.5 block md:text-right">ウェイト</label>
                   <div className="flex items-center md:justify-end gap-2">
                     <input type="number" min="0" max="100" value={item.weight} onChange={(e) => handlePerformanceChange(item.id, 'weight', Number(e.target.value))} readOnly={isFixedItem} className={`w-20 p-3 border border-gray-200 rounded-lg font-bold text-right text-indigo-600 outline-none transition-all ${isFixedItem ? 'bg-slate-50 cursor-default' : 'focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400'}`}/><span className="text-gray-400 text-sm font-bold">%</span>
                   </div>
                 </div>
              </div>

              {/* 詳細設定トグルエリア */}
              {item.type !== 'manual' && (
                <div className="px-5 pb-3 flex justify-end">
                   <button 
                     onClick={() => setExpandedPerformanceId(expandedPerformanceId === item.id ? null : item.id)}
                     className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold transition-all border ${
                       expandedPerformanceId === item.id 
                         ? 'bg-gray-100 text-gray-600 border-gray-200' 
                         : 'bg-white text-indigo-600 border-indigo-100 hover:bg-indigo-50 hover:border-indigo-200'
                     }`}
                   >
                     {expandedPerformanceId === item.id ? (
                       <><ChevronUp size={14} /> 詳細設定を閉じる</>
                     ) : (
                       <><Settings size={14} /> 詳細な評価基準を設定する <ChevronDown size={14} /></>
                     )}
                   </button>
                </div>
              )}

              {/* 詳細パネル (評価レンジ設定) */}
              {expandedPerformanceId === item.id && item.type !== 'manual' && (
                <div className="bg-slate-50 p-6 pt-10 pb-6 border-t border-gray-200 animate-in slide-in-from-top-2">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      <TrendingUp size={16} className="text-indigo-500"/> 
                      評価レンジ設定 
                      <span className="text-xs font-normal text-gray-500 ml-2">※獲得ポイントは0〜4、基準値のみ変更できます</span>
                    </h4>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-100">
                        <tr>
                          <th className="p-3 pl-6 w-1/2">基準値 (以上)</th>
                          <th className="p-3 w-1/2">獲得ポイント</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                         {(item.ranges || []).map((range, idx) => (
                           <tr key={idx} className="group hover:bg-gray-50">
                             <td className="p-3 pl-6">
                               <div className="flex items-center gap-3">
                                 <input type="number" value={range.min} onChange={(e) => handleRangeChange(item.id, idx, e.target.value)} className="w-24 p-2 border border-gray-200 rounded-md text-right font-bold text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"/>
                                 <span className="text-gray-500 text-xs">{item.type === 'absolute' ? '' : '%'} 以上 〜</span>
                               </div>
                             </td>
                             <td className="p-3">
                               <div className="flex items-center gap-3">
                                 <span className="text-indigo-600 font-bold">➜</span>
                                 <input type="number" value={range.point} readOnly className="w-20 p-2 border border-gray-200 rounded-md text-right font-bold text-indigo-600 bg-gray-50 outline-none"/>
                                 <span className="text-gray-500 text-xs font-bold">pt</span>
                               </div>
                             </td>
                           </tr>
                         ))}
                         {(!item.ranges || item.ranges.length === 0) && (
                           <tr><td colSpan={2} className="p-8 text-center text-gray-400">評価基準が設定されていません</td></tr>
                         )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      </>
    );

    if (options?.embedded) {
      return <div className="space-y-6">{content}</div>;
    }
    return (
      <div className="space-y-6">
        <Card highlight>{content}</Card>
      </div>
    );
  };
  
  const renderAdminUsers = () => {
    const lineUsers = (evaluationLineUserList.length > 0
      ? evaluationLineUserList.map((item) => ({
          id: item.id,
          name: item.name,
          email: item.email,
          roleId: item.roleId,
          role: item.roleName || String(item.roleId),
          department: item.department || '',
        }))
      : accountList.map((account) => ({
          id: account.id,
          name: account.name,
          email: account.email,
          roleId: account.roleId,
          role: account.roleName,
          department: account.department || '',
        })))
      .filter((user) => !isSystemAdminUser(user.roleId, user.role));
    const evaluators = lineUsers.filter((u) => evaluatorIds.includes(u.id));
    const assignedUsers = new Set(Object.values(evalRelations).flat());
    const adminUserManagementTabs = [
      { id: 'store_assignment', label: '店舗・配属', icon: Building2 },
      { id: 'accounts', label: 'ユーザー', icon: User },
      { id: 'evaluation_lines', label: '評価ライン', icon: Network },
      { id: 'performance', label: '業績項目', icon: BarChart3 },
      { id: 'behavior', label: '行動項目', icon: Users },
      { id: 'adjustment', label: '加減点', icon: Calculator },
      { id: 'rank_master', label: 'ランク', icon: Target },
    ] as const;
    const isStoreAssignmentTab = adminUserManagementTab === 'store_assignment';
    const isAccountsTab = adminUserManagementTab === 'accounts';
    const isEvaluationLinesTab = adminUserManagementTab === 'evaluation_lines';
    const isPerformanceTab = adminUserManagementTab === 'performance';
    const isBehaviorTab = adminUserManagementTab === 'behavior';
    const isAdjustmentTab = adminUserManagementTab === 'adjustment';
    const isRankMasterTab = adminUserManagementTab === 'rank_master';
    const userById = new Map(storeAssignmentUserList.map((userItem) => [userItem.id, userItem]));
    const assignmentsByStoreId = storeAssignmentList.reduce<
      Record<number, StoreAssignmentBoardUserItem[]>
    >((acc, assignment) => {
      const assignedUser = userById.get(assignment.userId);
      if (!assignedUser) return acc;
      const current = acc[assignment.storeId] ?? [];
      acc[assignment.storeId] = [...current, assignedUser];
      return acc;
    }, {});

    return (
      <div className="space-y-8">
        {showUserModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800"><UserPlus size={20} className="text-indigo-500"/> 新規ユーザー作成</h3>
              <div className="space-y-4">
                <div><label className="block text-xs font-bold text-gray-500 mb-1">氏名</label><input className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} /></div>
                <div><label className="block text-xs font-bold text-gray-500 mb-1">メールアドレス</label><input className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} /></div>
                <div className="rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700">
                  初期パスワードはシステムが自動生成し、登録メールアドレスへ送信されます。
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">ロール</label>
                  <MobileSelect
                    value={String(newUser.roleId)}
                    onChange={(value) => setNewUser({ ...newUser, roleId: Number(value) })}
                    options={roleMasterList.map(role => ({ value: String(role.id), label: role.name }))}
                    selectClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    buttonClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    sheetTitle="ロールを選択"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">役職</label>
                  <MobileSelect
                    value={newUser.positionId ? String(newUser.positionId) : ''}
                    onChange={(value) => setNewUser({ ...newUser, positionId: value ? Number(value) : null })}
                    options={[
                      { value: '', label: '未設定' },
                      ...positionMasterList.map(position => ({
                        value: String(position.id),
                        label: position.positionName,
                      })),
                    ]}
                    selectClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    buttonClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    sheetTitle="役職を選択"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-8"><button onClick={() => setShowUserModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md font-bold text-sm transition-colors">キャンセル</button><button onClick={handleAddUser} className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold text-sm shadow-md transition-colors">作成</button></div>
            </div>
          </div>
        )}
        {showEditUserModal && editingUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
                <Edit3 size={20} className="text-indigo-500" /> ユーザー編集
              </h3>
              <div className="space-y-4">
                <div><label className="block text-xs font-bold text-gray-500 mb-1">氏名</label><input className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none" value={editingUser.name} onChange={e => setEditingUser({ ...editingUser, name: e.target.value })} /></div>
                <div><label className="block text-xs font-bold text-gray-500 mb-1">メールアドレス</label><input className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none" value={editingUser.email} onChange={e => setEditingUser({ ...editingUser, email: e.target.value })} /></div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">ロール</label>
                  <MobileSelect
                    value={String(editingUser.roleId)}
                    onChange={(value) => setEditingUser({ ...editingUser, roleId: Number(value) })}
                    options={roleMasterList.map(role => ({ value: String(role.id), label: role.name }))}
                    selectClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    buttonClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    sheetTitle="ロールを選択"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">役職</label>
                  <MobileSelect
                    value={editingUser.positionId ? String(editingUser.positionId) : ''}
                    onChange={(value) => setEditingUser({ ...editingUser, positionId: value ? Number(value) : null })}
                    options={[
                      { value: '', label: '未設定' },
                      ...positionMasterList.map(position => ({
                        value: String(position.id),
                        label: position.positionName,
                      })),
                    ]}
                    selectClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    buttonClassName="border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none"
                    sheetTitle="役職を選択"
                  />
                </div>
              </div>
              <div className="flex justify-between items-center mt-8">
                <button
                  type="button"
                  onClick={handleRequestDeleteEditingUser}
                  className="px-4 py-2 border border-red-200 text-red-500 rounded-md font-bold text-sm hover:bg-red-50"
                >
                  削除
                </button>
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setShowEditUserModal(false); setEditingUser(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md font-bold text-sm transition-colors">キャンセル</button>
                  <button onClick={handleUpdateUser} className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold text-sm shadow-md transition-colors">保存</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showAddDepartmentModal && (
           <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800"><Building2 size={20} className="text-indigo-500"/> 店舗追加</h3>
              <p className="text-xs text-gray-500 mb-4">新しい店舗を追加します。追加後、目標管理画面で数値を設定してください。</p>
              <div className="space-y-4">
                <div><label className="block text-xs font-bold text-gray-500 mb-1">店舗名</label><input className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-indigo-200 outline-none" value={newDepartmentName} onChange={e => setNewDepartmentName(e.target.value)} placeholder="例: 池袋西口店" /></div>
              </div>
              <div className="flex justify-end gap-3 mt-8"><button onClick={() => setShowAddDepartmentModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md font-bold text-sm transition-colors">キャンセル</button><button onClick={executeAddDepartment} className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold text-sm shadow-md transition-colors">追加</button></div>
            </div>
          </div>
        )}
        {showAddEvaluatorModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800"><Network size={20} className="text-indigo-500"/> 評価者追加</h3>
              <MobileSelect
                value={selectedNewEvaluator}
                onChange={(value) => setSelectedNewEvaluator(value)}
                options={[
                  { value: '', label: 'ユーザーを選択...' },
                  ...lineUsers.filter(u => !evaluatorIds.includes(u.id)).map(u => ({ value: u.id, label: `${u.name} (${u.role})` }))
                ]}
                selectClassName="border-gray-300 p-3 rounded-md bg-gray-50 font-bold text-gray-700 focus:ring-2 focus:ring-indigo-200 outline-none"
                buttonClassName="border-gray-300 p-3 rounded-md bg-gray-50 font-bold text-gray-700 focus:ring-2 focus:ring-indigo-200 outline-none"
                sheetTitle="ユーザーを選択"
              />
              <div className="flex justify-end gap-3 mt-6"><button onClick={() => setShowAddEvaluatorModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md font-bold text-sm transition-colors">キャンセル</button><button onClick={handleAddEvaluator} disabled={!selectedNewEvaluator} className="px-4 py-2 bg-blue-600 text-white rounded-md font-bold text-sm shadow-md hover:bg-indigo-700 disabled:opacity-50 transition-colors">追加</button></div>
            </div>
          </div>
        )}
        <StoreAssignmentBoardModal
          isOpen={showStoreAssignmentModal}
          selectedStore={selectedStoreForAssignment}
          users={storeAssignmentUserList}
          assignments={storeAssignmentList}
          isSaving={isStoreAssignmentSaving}
          errorMessage={storeAssignmentErrorMessage}
          canDeleteStore={
            selectedStoreForAssignment
              ? !storeAssignmentList.some(
                  (assignment) => assignment.storeId === selectedStoreForAssignment.id,
                )
              : false
          }
          onAssign={handleAssignUserToStoreBoard}
          onUnassign={handleUnassignUserFromStoreBoard}
          onDeleteStore={handleRequestDeleteStore}
          onClose={() => {
            setShowStoreAssignmentModal(false);
            setSelectedStoreForAssignment(null);
            setStoreDeleteTarget(null);
          }}
        />

        <div className="space-y-6">
          <Card highlight>
            <div className="flex flex-wrap items-center gap-2">
              {adminUserManagementTabs.map((tab) => {
                const isActive = adminUserManagementTab === tab.id;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setAdminUserManagementTab(tab.id);
                      syncUrlByAdminUsersTab(tab.id);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-bold border transition-all ${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">

          {isStoreAssignmentTab && (
            <div>
               <div className="flex justify-between items-center mb-6">
                 <SectionTitle isAdminMode>店舗・配属</SectionTitle>
                 <button onClick={handleOpenAddDepartmentModal} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-indigo-700 transition-all">
                   <Plus size={16} /> 新規追加
                 </button>
               </div>
               {isStoreLoading && (
                 <p className="text-xs text-gray-500 mb-4">店舗一覧を読み込み中...</p>
               )}
               
               {storeList.length === 0 && !isStoreLoading ? (
                 <div className="text-sm text-gray-500 border border-gray-200 rounded-lg p-6 bg-white text-center">
                   店舗データがありません。右上の「店舗を追加」から登録してください。
                 </div>
               ) : (
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   {storeList.map((storeItem) => (
                     <div
                       key={storeItem.id}
                       onClick={() => handleOpenStoreAssignmentModal(storeItem.id)}
                       className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 bg-white cursor-pointer hover:border-indigo-300"
                     >
                       <div className="bg-gray-50 p-4 border-b border-gray-200 font-bold text-gray-700 flex justify-between items-center">
                         <span className="flex items-center gap-2 text-sm"><Building2 size={18} className="text-indigo-500" /> {storeItem.storeName}</span>
                         <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-gray-200 text-gray-500 font-medium">{(assignmentsByStoreId[storeItem.id] ?? []).length}名</span>
                       </div>
                       <div className="max-h-48 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                         {(assignmentsByStoreId[storeItem.id] ?? []).length === 0 ? (
                           <div className="p-6 text-center text-xs text-gray-400">配属メンバーなし</div>
                         ) : (
                           (assignmentsByStoreId[storeItem.id] ?? []).map((assignedUser) => (
                             <div
                               key={assignedUser.id}
                               className="px-4 py-3 border-b border-gray-100 last:border-b-0"
                             >
                               <p className="text-sm font-bold text-gray-800">{assignedUser.name}</p>
                               <p className="text-xs text-gray-500">{assignedUser.email}</p>
                               <p className="text-[11px] text-gray-500 mt-1">
                                 {assignedUser.roleName}
                                 {assignedUser.positionName ? ` / ${assignedUser.positionName}` : ''}
                               </p>
                             </div>
                           ))
                         )}
                       </div>
                     </div>
                   ))}
                 </div>
               )}
            </div>
          )}

          {isAccountsTab && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <SectionTitle isAdminMode>ユーザー</SectionTitle>
                <button onClick={handleOpenUserModal} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-indigo-700 transition-all">
                  <Plus size={16} /> 新規追加
                </button>
              </div>
              <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 font-bold text-gray-600 border-b border-gray-200">
                    <tr>
                      <th className="p-4">氏名</th>
                      <th className="p-4">メール</th>
                      <th className="p-4">ロール</th>
                      <th className="p-4">役職</th>
                      <th className="p-4 text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isAccountLoading ? (
                      <tr><td colSpan={5} className="p-6 text-center text-gray-400">読み込み中...</td></tr>
                    ) : accountList.length === 0 ? (
                      <tr><td colSpan={5} className="p-6 text-center text-gray-400">アカウントがありません</td></tr>
                    ) : accountList.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4 font-bold text-gray-800">{u.name}</td>
                        <td className="p-4 text-gray-600">{u.email}</td>
                        <td className="p-4"><span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs border border-gray-200">{u.roleName || u.roleId}</span></td>
                        <td className="p-4"><span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs border border-gray-200">{u.positionName || '-'}</span></td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => handleOpenEditUserModal(u)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50"
                          >
                            <Edit3 size={13} />
                            編集
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {isEvaluationLinesTab && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <SectionTitle isAdminMode>評価ライン</SectionTitle>
                <button onClick={handleOpenAddEvaluatorModal} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-indigo-700 transition-all"><Plus size={16} /> 新規追加</button>
              </div>
              <p className="text-xs text-gray-500 mb-4">評価者カードをクリックすると、被評価者をドラッグ&ドロップで設定できます。</p>
              {isEvaluationLineLoading ? (
                <p className="text-xs text-gray-500 mb-4">評価ライン情報を読み込み中...</p>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {evaluators.length === 0 ? (
                  <div className="col-span-full border border-gray-200 rounded-xl bg-white p-6 text-center text-sm text-gray-400">
                    評価者が未設定です。「評価者追加」から登録してください。
                  </div>
                ) : null}
                {evaluators.map((evaluator) => {
                  const childIds = evalRelations[evaluator.id] || [];
                  return (
                    <div
                      key={evaluator.id}
                      onClick={() => onOpenEvaluatorLineModal(evaluator.id)}
                      className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
                    >
                      <div className="p-4 border-b border-gray-100 flex justify-between items-start">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-xs shadow-inner">評価</div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-800 truncate">{evaluator.name}</p>
                            <p className="text-xs text-gray-500 truncate">{evaluator.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRequestRemoveEvaluator(evaluator.id);
                          }}
                          className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-500">被評価者</span>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">{childIds.length}名</span>
                        </div>
                        {childIds.length === 0 ? (
                          <p className="text-xs text-gray-400">被評価者が未設定です</p>
                        ) : (
                          <div className="space-y-2">
                            {childIds.slice(0, 3).map((childId) => {
                              const child = lineUsers.find((userItem) => userItem.id === childId);
                              if (!child) return null;
                              return (
                                <div key={child.id} className="text-xs bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-gray-600 truncate">
                                  {child.name}
                                </div>
                              );
                            })}
                            {childIds.length > 3 ? (
                              <p className="text-[11px] text-gray-400">他 {childIds.length - 3} 名</p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isPerformanceTab && renderAdminPerformance({ embedded: true })}

          {isBehaviorTab && renderAdminBehavior({ embedded: true })}

          {isAdjustmentTab && renderAdminAdjustmentMaster({ embedded: true })}

          {isRankMasterTab && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <SectionTitle isAdminMode>ランク</SectionTitle>
                <button
                  type="button"
                  onClick={handleSaveRankMaster}
                  className="flex items-center gap-2 bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold shadow-md hover:bg-emerald-600 transition-all text-sm"
                >
                  <Save size={16} />
                  設定を保存
                </button>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <p className="text-sm text-gray-600 mb-4">
                  固定ランク（C / B / A / S）のしきい値を設定します。
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {[...rankThresholds]
                    .sort((left, right) => Number(right.minScore) - Number(left.minScore))
                    .map((item) => (
                    <div key={item.rank} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                      <div className="text-xs font-bold text-gray-500 mb-2">ランク {item.rank}</div>
                      <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                        しきい値
                        <input
                          type="number"
                          min="0"
                          value={item.minScore}
                          onChange={(event) =>
                            handleRankThresholdChange(item.rank, event.target.value)
                          }
                          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-right text-sm font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                        pt以上
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
            </div>
          </Card>
        </div>
        {selectedEvaluatorIdForLineModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                      <Network size={18} className="text-indigo-500" />
                    {lineUsers.find((userItem) => userItem.id === selectedEvaluatorIdForLineModal)?.name} 配下設定
                    </h3>
                  <p className="text-xs text-gray-500 mt-1">左のユーザー一覧から右へドラッグして被評価者に設定します。左へ戻すと解除されます。</p>
                </div>
                <button onClick={onCloseEvaluatorLineModal} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 sm:p-5 overflow-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <section onDragOver={onDragOver} onDrop={onDropToUnassignedFromSelectedEvaluator} className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 min-h-[420px]">
                    <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3"><Users size={15} className="text-indigo-500" /> ユーザー一覧</h4>
                    <p className="text-[11px] text-gray-500 mb-3">この枠に戻すと対象評価者から解除されます。</p>
                    <div className="space-y-2">
                    {lineUsers
                        .filter((userItem) => userItem.id !== selectedEvaluatorIdForLineModal)
                        .filter(
                          (userItem) =>
                            !(evalRelations[selectedEvaluatorIdForLineModal] || []).includes(
                              userItem.id,
                            ),
                        )
                        .map((userItem) => (
                          <div key={userItem.id} draggable onDragStart={(event) => onDragStart(event, userItem.id)} onDragEnd={() => setDraggingUserId(null)} className={`bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-move hover:border-indigo-400 hover:shadow-md transition-all flex items-center justify-between group ${draggingUserId === userItem.id ? 'opacity-60' : ''}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <GripVertical size={16} className="text-gray-300 group-hover:text-indigo-400 shrink-0" />
                              <div className="min-w-0">
                                <div className="font-bold text-sm text-gray-700 truncate">{userItem.name}</div>
                                <div className="text-[10px] text-gray-400 truncate">{userItem.role}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                  <section onDragOver={onDragOver} onDrop={onDropToSelectedEvaluator} className="border border-gray-200 rounded-xl overflow-hidden bg-white min-h-[420px]">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2"><UserCheck size={15} className="text-emerald-600" /> 被評価者</h4>
                      <span className="text-xs text-gray-500 px-2 py-0.5 rounded-full border border-gray-200 bg-white">{(evalRelations[selectedEvaluatorIdForLineModal] || []).length}名</span>
                    </div>
                    <div className="p-3 min-h-[360px]">
                      {(evalRelations[selectedEvaluatorIdForLineModal] || []).length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-8">ここにドロップ</p>
                      ) : (
                        <div className="space-y-2">
                          {(evalRelations[selectedEvaluatorIdForLineModal] || []).map((childId) => {
                            const child = lineUsers.find((userItem) => userItem.id === childId);
                            if (!child) return null;
                            return (
                              <div key={child.id} draggable onDragStart={(event) => onDragStart(event, child.id)} onDragEnd={() => setDraggingUserId(null)} className={`bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between group cursor-move ${draggingUserId === child.id ? 'opacity-60' : ''}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <GripVertical size={14} className="text-gray-300 group-hover:text-indigo-400 shrink-0" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-gray-700 truncate">{child.name}</div>
                                    <div className="text-[10px] text-gray-400 truncate">{child.role}</div>
                                  </div>
                                </div>
                                <button onClick={() => handleRequestRemoveEvaluatee(selectedEvaluatorIdForLineModal, child.id)} className="text-gray-300 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors">
                                  <X size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <p className="text-xs text-gray-500">変更は即時保存されます。</p>
                <button onClick={onCloseEvaluatorLineModal} className="px-4 py-2 text-sm font-bold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100">閉じる</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAdminBehavior = (options?: { embedded?: boolean }) => {
    const totalWeight = behaviorData.reduce((sum, item) => sum + Number(item.weight || 0), 0);
    const isValidWeight = totalWeight === 100;
    const content = (
      <>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div>
            <SectionTitle isAdminMode>行動項目</SectionTitle>
            <div className="mt-3 inline-flex rounded-lg bg-slate-100 p-1" role="tablist" aria-label="役職区分">
              {([
                ['general', '一般'],
                ['manager', '店長以上'],
              ] as const).map(([category, label]) => (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={behaviorItemPositionCategory === category}
                  onClick={() => setBehaviorItemPositionCategory(category)}
                  className={`rounded-md px-4 py-2 text-sm font-bold transition-colors ${
                    behaviorItemPositionCategory === category
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-gray-500 flex items-center justify-end gap-1">Total Weight <HelpCircle size={12}/></span>
              <div className={`text-2xl font-bold ${isValidWeight ? 'text-indigo-600' : 'text-red-500'}`}>{totalWeight}%</div>
            </div>
            <button
              onClick={handleSaveBehavior}
              disabled={isBehaviorConfigSaving || !isValidWeight}
              className={`flex items-center gap-2 text-white px-6 py-2 rounded-lg font-bold shadow-md transition-all text-sm ${isValidWeight ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`}
              title={!isValidWeight ? 'ウェイト合計が100%でないため保存できません' : ''}
            >
              <Save size={16} />
              {isBehaviorConfigSaving ? '保存中...' : '設定を保存'}
            </button>
            <button
              type="button"
              onClick={handleAddBehaviorItem}
              className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-bold text-indigo-600 transition-all hover:bg-indigo-50"
            >
              <Plus size={16} />
              項目を追加
            </button>
          </div>
        </div>
        {isBehaviorConfigLoading ? (
          <p className="text-xs text-gray-500 mb-4">行動項目設定を読み込み中...</p>
        ) : null}
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-8 relative shadow-inner">
          <div
            className={`h-full transition-all duration-500 ease-out ${isValidWeight ? 'bg-indigo-500' : 'bg-red-400'}`}
            style={{ width: `${Math.min(totalWeight, 100)}%` }}
          />
        </div>
        <div className="grid gap-6">
          {behaviorData.map((item) => (
            <div
              key={item.id}
              className="relative rounded-xl border border-gray-200 bg-white p-6 pr-28 shadow-sm transition-all hover:border-indigo-300 hover:shadow-md"
            >
              <button
                type="button"
                onClick={() => handleDeleteBehaviorItem(item.id)}
                className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-600 shadow-sm transition-colors hover:bg-red-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-200"
                aria-label={`${item.item}を削除`}
              >
                <Trash2 size={16} />
                削除
              </button>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-2 flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 font-bold mb-1.5 uppercase tracking-wider">ウェイト (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={item.weight}
                      onChange={(e) => handleBehaviorChange(item.id, 'weight', Number(e.target.value))}
                      className="w-full p-3 border border-gray-200 rounded-lg font-bold text-right text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="md:col-span-10 flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 font-bold mb-1.5 uppercase tracking-wider">評価項目名</label>
                    <input
                      type="text"
                      value={item.item}
                      onChange={(e) => handleBehaviorChange(item.id, 'item', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg font-bold text-gray-800 text-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 font-bold mb-1.5 uppercase tracking-wider">全体定義</label>
                    <textarea
                      value={item.def}
                      onChange={(e) => handleBehaviorChange(item.id, 'def', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg text-sm min-h-[80px] text-gray-600 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="md:col-span-12 mt-2 pt-6 border-t border-gray-100">
                  <p className="text-xs font-bold text-gray-400 mb-4 flex items-center gap-2">
                    <Settings size={14} />
                    評価レベル別基準定義
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {[0, 1, 2, 3, 4].map((level) => (
                      <div key={level} className="relative">
                        <label className="block text-[10px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider flex justify-between">
                          <span>LV.{level}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] ${level === 0 ? 'bg-red-50 text-red-500' : level === 4 ? 'bg-indigo-50 text-indigo-500' : 'bg-gray-100 text-gray-500'}`}>
                            {level === 0 ? '不足' : level === 2 ? '標準' : level === 4 ? '卓越' : ''}
                          </span>
                        </label>
                        <textarea
                          value={item.criteria?.[level] || ''}
                          onChange={(e) => handleBehaviorCriteriaChange(item.id, level, e.target.value)}
                          className="w-full p-3 border border-gray-200 rounded-lg text-xs h-28 resize-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all bg-gray-50/30 leading-relaxed"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
    if (options?.embedded) {
      return <div className="space-y-6">{content}</div>;
    }
    return (
      <div className="space-y-6">
        <Card highlight>{content}</Card>
      </div>
    );
  };

  const renderAdminAdjustmentMaster = (options?: { embedded?: boolean }) => {
    const content = (
      <>
      <div className="flex justify-between items-center mb-6">
        <SectionTitle isAdminMode>加減点</SectionTitle>
        <button onClick={handleSaveAdjustment} disabled={isAdjustmentConfigSaving} className="flex items-center gap-2 bg-emerald-500 text-white px-6 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-emerald-600 transition-all disabled:opacity-60"><Save size={16} /> {isAdjustmentConfigSaving ? '保存中...' : '設定を保存'}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div><div className="flex justify-between items-center mb-6"><h3 className="font-bold flex items-center text-gray-700 text-lg"><div className="w-1.5 h-6 bg-indigo-500 mr-3 rounded-full"></div>加点項目</h3><button onClick={() => handleAddAdjustmentItem('plus')} className="text-xs bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-indigo-700 transition-colors">追加</button></div><div className="space-y-4">{adjustments.plus.map((item) => (<div key={item.id} className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all group"><button onClick={() => handleDeleteAdjustmentItem('plus', item.id)} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"><Trash2 size={16} /></button><div className="space-y-4"><div><label className="block text-xs font-bold text-gray-400 mb-1">項目名</label><input type="text" value={item.item} onChange={(e) => handleAdjustMasterChange('plus', item.id, 'item', e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg font-bold text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"/></div><div><label className="block text-xs font-bold text-gray-400 mb-1">単価 (pt)</label><input type="number" value={item.unitPoint} onChange={(e) => handleAdjustMasterChange('plus', item.id, 'unitPoint', e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg font-bold text-right text-indigo-600 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"/></div></div></div>))}</div></div>
        <div><div className="flex justify-between items-center mb-6"><h3 className="font-bold flex items-center text-gray-700 text-lg"><div className="w-1.5 h-6 bg-red-500 mr-3 rounded-full"></div>減点項目</h3><button onClick={() => handleAddAdjustmentItem('minus')} className="text-xs bg-red-500 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-red-600 transition-colors">追加</button></div><div className="space-y-4">{adjustments.minus.map((item) => (<div key={item.id} className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all group"><button onClick={() => handleDeleteAdjustmentItem('minus', item.id)} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"><Trash2 size={16} /></button><div className="space-y-4"><div><label className="block text-xs font-bold text-gray-400 mb-1">項目名</label><input type="text" value={item.item} onChange={(e) => handleAdjustMasterChange('minus', item.id, 'item', e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg font-bold text-gray-700 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all"/></div><div><label className="block text-xs font-bold text-gray-400 mb-1">単価 (pt)</label><input type="number" value={item.unitPoint} onChange={(e) => handleAdjustMasterChange('minus', item.id, 'unitPoint', e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg font-bold text-right text-red-500 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all"/></div></div></div>))}</div></div>
      </div>
      </>
    );
    if (options?.embedded) {
      return <div className="space-y-6">{content}</div>;
    }
    return <Card highlight>{content}</Card>;
  };

  const handleAdminStoreAdjustmentCountChange = (itemId: number, value: string) => {
    const normalized = Number(value);
    const nextCount = Number.isFinite(normalized) ? Math.max(0, Math.trunc(normalized)) : 0;
    setAdminStoreAdjustmentDetail((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, count: nextCount, score: nextCount * item.unitPoint }
          : item,
      ),
    );
  };

  const handleSaveAdminStoreAdjustment = async () => {
    if (selectedAdminAdjustmentStoreId === '') {
      showNotification('店舗を選択してください。', 'error');
      return;
    }
    if (!isEvaluationInputWindowOpenForPeriod(adminStoreAdjustmentPeriod)) {
      showNotification('加点・減点は評価入力期間のみ編集できます。', 'error');
      return;
    }

    try {
      setIsAdminStoreAdjustmentSaving(true);
      const entries = adminStoreAdjustmentDetail.map((item) => ({
        adjustmentItemId: item.id,
        count: Math.max(0, Math.trunc(Number(item.count) || 0)),
      }));
      await saveStoreAdjustmentEvaluation({
        storeId: Number(selectedAdminAdjustmentStoreId),
        termNo: adminStoreAdjustmentTermNo,
        period: adminStoreAdjustmentPeriod,
        entries,
      });
      await loadAdminStoreAdjustmentDetail(
        Number(selectedAdminAdjustmentStoreId),
        adminStoreAdjustmentTermNo,
        adminStoreAdjustmentPeriod,
      );
      showNotification('店舗加減点を保存しました。', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '店舗加減点の保存に失敗しました',
        'error',
      );
    } finally {
      setIsAdminStoreAdjustmentSaving(false);
    }
  };

  const renderAdminStoreAdjustment = () => {
    const selectedStoreName =
      storeAssignmentStoreList.find((store) => store.id === selectedAdminAdjustmentStoreId)?.storeName ??
      '';
    const canEdit = isEvaluationInputWindowOpenForPeriod(adminStoreAdjustmentPeriod);
    const isAdminAdjustmentSelectionCurrent =
      adminStoreAdjustmentTermNo === currentTermNo && adminStoreAdjustmentPeriod === currentPeriodNow;
    const totalAdjustmentScore = adminStoreAdjustmentDetail.reduce((sum, item) => sum + item.score, 0);

    return (
      <Card highlight>
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <SectionTitle isAdminMode>店舗別 加点・減点入力</SectionTitle>
              <p className="text-xs text-gray-500 mt-1">
                評価期間中に、店舗単位の加減点件数を入力します。社員画面には所属店舗の値が表示されます。
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveAdminStoreAdjustment}
              disabled={
                selectedAdminAdjustmentStoreId === '' ||
                isAdminStoreAdjustmentSaving ||
                !canEdit
              }
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold shadow-md transition-all ${
                selectedAdminAdjustmentStoreId !== '' && canEdit
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
              }`}
            >
              <Save size={14} />
              {isAdminStoreAdjustmentSaving ? '保存中...' : '保存'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-xs font-bold text-gray-500 mb-1">対象期</div>
              <select
                value={adminStoreAdjustmentTermNo}
                onChange={(event) => setAdminStoreAdjustmentTermNo(Number(event.target.value))}
                className="w-full border border-gray-200 rounded-md p-2 text-sm font-bold text-slate-700 bg-white"
              >
                {(scheduleTermOptions.length > 0
                  ? scheduleTermOptions
                  : [{ termNo: currentTermNo, startDate: '', endDate: '' }]
                ).map((term) => (
                  <option key={`admin-adjustment-term-${term.termNo}`} value={term.termNo}>
                    第{term.termNo}期
                  </option>
                ))}
              </select>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-xs font-bold text-gray-500 mb-1">対象期間</div>
              <select
                value={adminStoreAdjustmentPeriod}
                onChange={(event) =>
                  setAdminStoreAdjustmentPeriod(event.target.value as ScheduleQuarter)
                }
                className="w-full border border-gray-200 rounded-md p-2 text-sm font-bold text-slate-700 bg-white"
              >
                <option value="first_half">{quarterLabelMap.first_half}</option>
                <option value="second_half">{quarterLabelMap.second_half}</option>
              </select>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-xs font-bold text-gray-500 mb-1">店舗</div>
              <MobileSelect
                value={selectedAdminAdjustmentStoreId === '' ? '' : String(selectedAdminAdjustmentStoreId)}
                onChange={(value) =>
                  setSelectedAdminAdjustmentStoreId(value ? Number(value) : '')
                }
                options={[
                  { value: '', label: '店舗を選択' },
                  ...storeAssignmentStoreList.map((store) => ({
                    value: String(store.id),
                    label: store.storeName,
                  })),
                ]}
                selectClassName="border border-gray-200 rounded-md p-2 text-sm font-bold text-gray-700 bg-white"
                buttonClassName="border border-gray-200 rounded-md p-2 text-sm font-bold text-gray-700 bg-white"
                sheetTitle="店舗を選択"
              />
            </div>
          </div>

          {!isAdminAdjustmentSelectionCurrent ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              現在の評価期間（第{currentTermNo}期 {quarterLabelMap[currentPeriodNow]}）ではありません。対象期間を確認してください。
            </div>
          ) : null}

          {!canEdit ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
              現在は評価入力期間外のため、閲覧のみ可能です。
            </div>
          ) : null}

          {selectedAdminAdjustmentStoreId === '' ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              店舗を選択してください。
            </div>
          ) : isAdminStoreAdjustmentLoading ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              店舗加減点を読み込み中です...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                店舗: <span className="font-bold text-gray-800">{selectedStoreName || '-'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold mb-3 flex items-center text-gray-700">
                    <div className="w-1 h-6 bg-[#80A0E0] mr-3"></div>加点項目
                  </h3>
                  <div className="space-y-3">
                    {adminStoreAdjustmentDetail
                      .filter((item) => item.category === 'plus')
                      .map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-sm border border-gray-200 shadow-sm">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-700">{item.item}</div>
                            <div className="text-xs text-gray-400 mt-1">単価: {item.unitPoint}pt</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">件数</span>
                            <input
                              type="number"
                              min="0"
                              disabled={!canEdit || isAdminStoreAdjustmentSaving}
                              value={item.count}
                              onChange={(event) =>
                                handleAdminStoreAdjustmentCountChange(item.id, event.target.value)
                              }
                              className="w-16 p-2 border rounded-sm text-right font-bold bg-gray-50 focus:bg-white focus:outline-none focus:border-[#80A0E0]"
                            />
                            <div className="w-14 text-right font-bold text-[#80A0E0]">
                              +{formatOneDecimal(Number(item.score ?? 0))}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold mb-3 flex items-center text-gray-700">
                    <div className="w-1 h-6 bg-[#D03020] mr-3"></div>減点項目
                  </h3>
                  <div className="space-y-3">
                    {adminStoreAdjustmentDetail
                      .filter((item) => item.category === 'minus')
                      .map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-sm border border-gray-200 shadow-sm">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-700">{item.item}</div>
                            <div className="text-xs text-gray-400 mt-1">単価: {item.unitPoint}pt</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">件数</span>
                            <input
                              type="number"
                              min="0"
                              disabled={!canEdit || isAdminStoreAdjustmentSaving}
                              value={item.count}
                              onChange={(event) =>
                                handleAdminStoreAdjustmentCountChange(item.id, event.target.value)
                              }
                              className="w-16 p-2 border rounded-sm text-right font-bold bg-gray-50 focus:bg-white focus:outline-none focus:border-[#D03020]"
                            />
                            <div className="w-14 text-right font-bold text-[#D03020]">
                              {formatOneDecimal(Number(item.score ?? 0))}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-4 border-t flex justify-between items-center bg-gray-50 p-4 rounded-sm border border-gray-200">
                <span className="font-bold text-gray-600">店舗加減点 合計</span>
                <span className={`text-2xl font-bold ${totalAdjustmentScore < 0 ? 'text-[#D03020]' : 'text-[#303030]'}`}>
                  {formatOneDecimal(totalAdjustmentScore)} pt
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const renderAdminApprovals = () => {
    const currentTermIndex = targetTermOptions.findIndex((term) => term.termNo === activeTargetTermNo);
    const filteredSubmissions = submittedEvaluations.filter(
      (row) => row.termNo === activeTargetTermNo && row.period === adminApprovalPeriod,
    );
    const approvalTargetAccounts = accountList.filter(
      (account) => !isSystemAdminUser(account.roleId, account.roleName),
    );
    const submissionByUserId = new Map(
      filteredSubmissions.map((row) => [row.userId, row] as const),
    );
    const approvalSubmissions = approvalTargetAccounts
      .map((account) => {
        const existingSubmission = submissionByUserId.get(account.id);
        if (existingSubmission) {
          const computed = computedApprovalScoreMap[existingSubmission.submissionKey];
          if (existingSubmission.hasSnapshot && existingSubmission.isFinalized) {
            return existingSubmission;
          }
          if (!computed) return existingSubmission;
          return {
            ...existingSubmission,
            performanceScore: computed.performanceScore,
            behaviorScore: computed.behaviorScore,
            adjustmentScore: computed.adjustmentScore,
            executiveAdjustmentScore: computed.executiveAdjustmentScore,
            totalScore: computed.totalScore,
          } satisfies SubmittedEvaluationSnapshot;
        }

        const submissionKey = getEvaluationSubmissionKey(
          account.id,
          activeTargetTermNo,
          adminApprovalPeriod,
        );
        const computed = computedApprovalScoreMap[submissionKey];
        return {
          submissionKey,
          userId: account.id,
          userName: account.name,
          department: account.department ?? '',
          role: account.positionName ?? account.roleName ?? '',
          termNo: activeTargetTermNo,
          period: adminApprovalPeriod,
          isFinalized: false,
          performanceScore: computed?.performanceScore ?? 0,
          behaviorScore: computed?.behaviorScore ?? 0,
          adjustmentScore: computed?.adjustmentScore ?? 0,
          executiveAdjustmentScore: computed?.executiveAdjustmentScore ?? 0,
          totalScore: computed?.totalScore ?? 0,
          submittedAt: '',
          hasSnapshot: false,
        } satisfies SubmittedEvaluationSnapshot;
      })
      .sort((a, b) => a.userName.localeCompare(b.userName, 'ja'));
    const totalCount = approvalSubmissions.length;
    const approvedCount = approvalSubmissions.filter((row) => isSubmissionApproved(row)).length;
    const snapshotCount = approvalSubmissions.filter((row) => row.hasSnapshot).length;
    const readinessLoadedForAll = approvalSubmissions.every(
      (row) => approvalReadinessMap[row.submissionKey] !== undefined,
    );
    const computedScoreLoadedForRequired = approvalSubmissions
      .filter(
        (row) =>
          !row.hasSnapshot && Boolean(approvalReadinessMap[row.submissionKey]),
      )
      .every((row) => computedApprovalScoreMap[row.submissionKey] !== undefined);
    const isApprovalHydrationPending =
      !readinessLoadedForAll || !computedScoreLoadedForRequired;
    const isAdminApprovalInputWindowOpen = isEvaluationInputWindowOpenForPeriod(adminApprovalPeriod);
    const canFinalize =
      ENABLE_APPROVAL_FINALIZE_TEST ||
      (totalCount > 0 &&
        snapshotCount === totalCount &&
        approvedCount === totalCount &&
        !finalizedYears[activeTargetYear]);

    return (
      <div className="space-y-6">
        <Card highlight>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <SectionTitle isAdminMode>評価承認</SectionTitle>
              <p className="text-xs text-gray-500">全社員の評価状況を確認し、必要項目が入力済みのスナップショットを承認してください。</p>
            </div>
            <div className="flex items-center gap-2">
              {renderTermNavigator({
                termLabel: `第${activeTargetTermNo}期`,
                onPrev: () => {
                  if (currentTermIndex > 0) {
                    setActiveTargetTermNo(targetTermOptions[currentTermIndex - 1].termNo);
                  }
                },
                onNext: () => {
                  if (currentTermIndex >= 0 && currentTermIndex < targetTermOptions.length - 1) {
                    setActiveTargetTermNo(targetTermOptions[currentTermIndex + 1].termNo);
                  }
                },
                disablePrev: currentTermIndex <= 0,
                disableNext: currentTermIndex < 0 || currentTermIndex >= targetTermOptions.length - 1,
                size: 'compact',
              })}
              {renderQuarterToggle({
                activePeriod: adminApprovalPeriod,
                onChange: (period) => setAdminApprovalPeriod(period),
                size: 'compact',
              })}
              <div className="text-[11px] bg-gray-100 px-2.5 py-1.5 rounded-lg border text-gray-600">
                承認: <span className="font-bold text-gray-800">{approvedCount}/{totalCount}</span>
              </div>
              <button
                onClick={() => finalizeYearApproval(activeTargetYear)}
                disabled={!canFinalize}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold shadow-md transition-all ${
                  canFinalize ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed border'
                }`}
              >
                <FileCheck size={12} /> 評価を確定
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-200">
                <tr>
                  <th className="p-4">氏名</th>
                  <th className="p-4 text-right">総合点</th>
                  <th className="p-4 text-center">AIコメント</th>
                  <th className="p-4 text-center">承認</th>
                  <th className="p-4 text-center">状態</th>
                  <th className="p-4 text-center">ランク</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isAdminApprovalLoading || isApprovalHydrationPending ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-sm text-gray-500">
                      評価状況を読み込んでいます。
                    </td>
                  </tr>
                ) : approvalSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-sm text-gray-500">
                      表示できる社員がいません。
                    </td>
                  </tr>
                ) : approvalSubmissions.map((submission) => {
                  const approved = isSubmissionApproved(submission);
                  const isApprovalActionLoading =
                    approvalActionLoadingKey === submission.submissionKey;
                  const canApproveSubmission = submission.hasSnapshot;
                  const isRequirementsReady = Boolean(
                    approvalReadinessMap[submission.submissionKey],
                  );
                  const approvalReady = canApproveSubmission || isRequirementsReady;
                  const statusLabel = approved
                    ? '承認済'
                    : approvalReady
                      ? '承認待ち'
                      : isAdminApprovalInputWindowOpen
                        ? '入力中'
                        : '期間外';
                  const displayedTotalScore = approvalReady
                    ? Number(submission.totalScore ?? 0)
                    : Number.NaN;
                  const rankLabel = approved
                    ? (submission.rank ?? resolveRankByScore(displayedTotalScore))
                    : null;
                  return (
                    <tr key={submission.submissionKey} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 font-bold text-gray-800">{submission.userName}</td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          disabled={!approvalReady}
                          onClick={() => {
                            if (!approvalReady) return;
                            setSelectedApprovalDetail(submission);
                            setApprovalDetailSection('summary');
                            setApprovalPerformanceDetail(null);
                            setApprovalBehaviorDetail(null);
                            setApprovalAdjustmentDetail(null);
                          }}
                          className={`inline-flex items-center rounded-md px-3 py-1.5 font-bold transition-colors ${
                            approvalReady
                              ? 'border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                              : 'border border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {approvalReady ? formatOneDecimal(Number(submission.totalScore ?? 0)) : '-'}
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          type="button"
                          disabled={!canApproveSubmission}
                          onClick={() => openApprovalAiCommentModal(submission)}
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${
                            canApproveSubmission
                              ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                              : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                          title="AIコメントを表示"
                          aria-label="AIコメントを表示"
                        >
                          <Bot size={15} />
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          type="button"
                          onClick={async () => {
                            if (approvalReady) {
                              if (approved) return;
                              setGenericConfirmState({
                                title: '承認の確認',
                                message:
                                  'この評価を承認します。承認後は解除できません。よろしいですか？',
                                confirmText: '承認する',
                                tone: 'primary',
                                onConfirm: async () => {
                                  await handleSubmissionApprovalAction(submission);
                                },
                              });
                              return;
                            }
                            setApprovalMissingRequirementModal({
                              isOpen: true,
                              userName: submission.userName,
                              items: approvalMissingItemsMap[submission.submissionKey] ?? [],
                              isLoading: true,
                              errorMessage: '',
                            });
                            try {
                              const result = await fetchFinalEvaluationMissingRequirements({
                                userId: submission.userId,
                                termNo: submission.termNo,
                                period: submission.period,
                              });
                              setApprovalMissingRequirementModal({
                                isOpen: true,
                                userName: submission.userName,
                                items: result.items,
                                isLoading: false,
                                errorMessage: '',
                              });
                            } catch (error) {
                              setApprovalMissingRequirementModal({
                                isOpen: true,
                                userName: submission.userName,
                                items: [],
                                isLoading: false,
                                errorMessage:
                                  error instanceof Error
                                    ? error.message
                                    : '不足項目の取得に失敗しました',
                              });
                            }
                          }}
                          className={`text-xs px-3 py-1.5 rounded-md font-bold transition-all ${
                            !approvalReady
                              ? 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                              : isApprovalActionLoading
                                ? 'bg-emerald-100 text-emerald-400 border border-emerald-200 cursor-wait'
                              : approved
                                ? 'bg-emerald-600 text-white cursor-not-allowed'
                                : 'bg-white border border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                          }`}
                          disabled={isApprovalActionLoading || approved}
                        >
                          {!approvalReady
                            ? '未入力'
                            : isApprovalActionLoading
                              ? '生成中...'
                              : approved
                                ? '承認済み'
                                : '承認する'}
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          approved
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : approvalReady
                              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                              : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        {rankLabel ? (
                          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                            {rankLabel}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {finalizedYears[activeTargetYear] && (
            <div className="mt-4 p-4 rounded-lg border border-indigo-200 bg-indigo-50 text-sm text-indigo-700 font-bold flex items-center gap-2">
              <FileCheck size={16} /> {activeTargetYear}年度の評価は確定済みです。
            </div>
          )}
        </Card>
      </div>
    );
  };

  const getApprovalDetailBreakdown = (submission: SubmittedEvaluationSnapshot) => {
    const computed = computedApprovalScoreMap[submission.submissionKey];
    const effectivePerformanceScore =
      (submission.hasSnapshot && submission.isFinalized) || !computed
        ? Number(submission.performanceScore ?? 0)
        : Number(computed.performanceScore ?? 0);
    const effectiveBehaviorScore =
      (submission.hasSnapshot && submission.isFinalized) || !computed
        ? Number(submission.behaviorScore ?? 0)
        : Number(computed.behaviorScore ?? 0);
    const effectiveAdjustmentScore =
      (submission.hasSnapshot && submission.isFinalized) || !computed
        ? Number(submission.adjustmentScore ?? 0)
        : Number(computed.adjustmentScore ?? 0);
    const effectiveExecutiveAdjustmentScore =
      (submission.hasSnapshot && submission.isFinalized) || !computed
        ? Number(submission.executiveAdjustmentScore ?? 0)
        : Number(computed.executiveAdjustmentScore ?? 0);
    const effectiveTotalScore =
      (submission.hasSnapshot && submission.isFinalized) || !computed
        ? Number(submission.totalScore ?? 0)
        : Number(computed.totalScore ?? 0);
    return {
      performanceScore: effectivePerformanceScore,
      behaviorScore: effectiveBehaviorScore,
      adjustmentScore: effectiveAdjustmentScore,
      executiveAdjustmentScore: effectiveExecutiveAdjustmentScore,
      totalScore: effectiveTotalScore,
    };
  };

  const buildPerformanceDetail = async (submission: SubmittedEvaluationSnapshot) => {
    const [configItems, assignmentBoard, targetRowsResult, previousTargetRowsResult, qscRowsResult, qscRateRowsResult, crewEsRowsResult] =
      await Promise.all([
        fetchPerformanceItemList(),
        fetchStoreAssignmentBoard(),
        fetchStoreTargetRows(submission.termNo),
        submission.termNo > 1 ? fetchStoreTargetRows(submission.termNo - 1) : Promise.resolve({ rows: [] }),
        fetchStoreQscScoreRows(submission.termNo),
        fetchStoreQscRateRows(submission.termNo),
        fetchStoreCrewEsScoreRows(submission.termNo),
      ]);

    const assignedStoreIds = Array.from(
      new Set(
        assignmentBoard.assignments
          .filter((assignment) => assignment.userId === submission.userId)
          .map((assignment) => assignment.storeId),
      ),
    );
    const targetMonths = quarterMonthOrderMap[submission.period];
    const allTargetRows = [
      ...targetRowsResult.rows,
      ...((previousTargetRowsResult as { rows: StoreTargetRow[] }).rows ?? []),
    ];
    const sumValueByTermAndStore = (storeId: number, termNo: number, field: 'sales' | 'profit') =>
      targetMonths.reduce((sum, month) => {
        const row = allTargetRows.find(
          (targetRow) =>
            targetRow.storeId === storeId &&
            targetRow.termNo === termNo &&
            targetRow.month === month,
        );
        return sum + Number(row?.[field] ?? 0);
      }, 0);
    const averageByStores = (resolver: (storeId: number) => number | null) => {
      if (assignedStoreIds.length === 0) return null;
      const total = assignedStoreIds.reduce(
        (sum, storeId) => sum + Number(resolver(storeId) ?? 0),
        0,
      );
      return Number((total / assignedStoreIds.length).toFixed(1));
    };
    const qscScore = averageByStores(
      (storeId) =>
        qscRowsResult.rows.find(
          (row) =>
            row.storeId === storeId &&
            row.termNo === submission.termNo &&
            row.period === submission.period,
        )?.score ?? null,
    );
    const qscRateScore = averageByStores(
      (storeId) =>
        qscRateRowsResult.rows.find(
          (row) =>
            row.storeId === storeId &&
            row.termNo === submission.termNo &&
            row.period === submission.period,
        )?.score ?? null,
    );
    const crewEsScore = averageByStores(
      (storeId) =>
        crewEsRowsResult.rows.find(
          (row) =>
            row.storeId === storeId &&
            row.termNo === submission.termNo &&
            row.period === submission.period,
        )?.score ?? null,
    );
    const averageGrowthRatio = (field: 'sales' | 'profit') => {
      if (assignedStoreIds.length === 0) return null;
      const totalRatio = assignedStoreIds.reduce((sum, storeId) => {
        const currentTotal = sumValueByTermAndStore(storeId, submission.termNo, field);
        const previousTotal =
          submission.termNo > 1 ? sumValueByTermAndStore(storeId, submission.termNo - 1, field) : 0;
        const ratio = previousTotal > 0 ? (currentTotal / previousTotal) * 100 : 0;
        return sum + ratio;
      }, 0);
      return Number((totalRatio / assignedStoreIds.length).toFixed(1));
    };
    const salesRatio = averageGrowthRatio('sales');
    const profitRatio = averageGrowthRatio('profit');

    const baseItems = configItems
      .filter((item) => item.item !== '営業利益 伸長率')
      .map((item) => ({
        id: item.id,
        item: item.item,
        fixedKey: resolvePerformanceFixedKey(item.fixedKey, item.item),
        type: item.type,
        actual: '',
        weight: item.weight,
        max: item.max,
        points: 0,
        ranges: normalizePerformanceRangesToFixedPoints(
          item.ranges.map((range) => ({
            min: Number(range.min),
            point: range.point,
          })),
        ),
      }));

    return baseItems.map((item) => {
      let actual = item.actual;
      const fixedKey = resolvePerformanceFixedKey(item.fixedKey, item.item);
      if (fixedKey === 'sales_growth') {
        actual = salesRatio !== null ? `${salesRatio}%` : '-';
      } else if (fixedKey === 'profit_growth') {
        actual = profitRatio !== null ? `${profitRatio}%` : '-';
      } else if (fixedKey === 'monitor_check') {
        actual = qscScore !== null ? `${qscScore}点` : '-';
      } else if (fixedKey === 'qsc_score') {
        actual = qscRateScore !== null ? `${qscRateScore}%` : '-';
      } else if (fixedKey === 'crew_es') {
        actual = crewEsScore !== null ? `${crewEsScore}点` : '-';
      }
      const nextItem = { ...item, actual };
      const point = calculatePerformancePoint(nextItem);
      return {
        ...nextItem,
        points: point,
        score: calculateWeightedScoreByMax(point, nextItem.weight, nextItem.max),
      };
    });
  };

  const buildBehaviorDetail = async (submission: SubmittedEvaluationSnapshot) => {
    const [configItems, overview] = await Promise.all([
      fetchBehaviorItemList({ targetUserId: submission.userId }),
      fetchBehaviorEvaluationOverview({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
      }),
    ]);

    return mergeBehaviorEvaluationToUiData(
      configItems,
      overview.selfEvaluation?.items ?? [],
      overview.supervisorEvaluation?.items ?? [],
    );
  };

  const buildAdjustmentDetail = async (submission: SubmittedEvaluationSnapshot) => {
    const [configItems, evaluation] = await Promise.all([
      adjustmentConfigItems.length > 0
        ? Promise.resolve(adjustmentConfigItems)
        : fetchAdjustmentItemList(),
      fetchAdjustmentEvaluation({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
      }),
    ]);

    const countByItemId = new Map(
      evaluation.entries.map((entry) => [entry.adjustmentItemId, Number(entry.count)]),
    );

    return configItems
      .map((item) => {
        const count = Number(countByItemId.get(item.id) ?? 0);
        return {
          id: item.id,
          category: item.category,
          item: item.item,
          unitPoint: item.unitPoint,
          count,
          score: count * item.unitPoint,
        };
      })
      .filter((item) => item.count > 0);
  };

  const computeApprovalScoresFromSource = async (
    submission: SubmittedEvaluationSnapshot,
  ): Promise<SubmittedEvaluationScoreBreakdown> => {
    const [performanceRows, behaviorRows, adjustmentRows, behaviorOverview] = await Promise.all([
      buildPerformanceDetail(submission),
      buildBehaviorDetail(submission),
      buildAdjustmentDetail(submission),
      fetchBehaviorEvaluationOverview({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
      }),
    ]);

    const performanceScore = performanceRows.reduce((sum, row) => sum + Number(row.score ?? 0), 0);
    const behaviorScore = behaviorRows.reduce(
      (sum, row) =>
        sum +
        calculateBehaviorWeightedScore(
          Number(row.evaluatorScore ?? 0),
          Number(row.weight ?? 0),
        ),
      0,
    );
    const adjustmentScore = adjustmentRows.reduce((sum, row) => sum + Number(row.score ?? 0), 0);
    const executiveAdjustmentScore = Number(behaviorOverview.supervisorEvaluation?.rolePoint ?? 0);
    const totalScore = roundToOneDecimal(
      performanceScore + behaviorScore + adjustmentScore + executiveAdjustmentScore,
    );

    return {
      performanceScore: roundToOneDecimal(performanceScore),
      behaviorScore: roundToOneDecimal(behaviorScore),
      adjustmentScore: roundToOneDecimal(adjustmentScore),
      executiveAdjustmentScore: roundToOneDecimal(executiveAdjustmentScore),
      totalScore,
    };
  };

  const hydrateApprovalComputedScores = async () => {
    if (activeTab !== 'admin_approvals') return;
    if (accountList.length === 0) return;
    const requestSeq = ++adminApprovalScoreLoadSeqRef.current;
    const approvalTargetAccounts = accountList.filter(
      (account) => !isSystemAdminUser(account.roleId, account.roleName),
    );
    const filteredSubmissions = submittedEvaluations.filter(
      (row) => row.termNo === activeTargetTermNo && row.period === adminApprovalPeriod,
    );
    const submissionByUserId = new Map(filteredSubmissions.map((row) => [row.userId, row] as const));

    const candidates = approvalTargetAccounts
      .map((account) => {
        const existing = submissionByUserId.get(account.id);
        return (
          existing ?? {
            submissionKey: getEvaluationSubmissionKey(
              account.id,
              activeTargetTermNo,
              adminApprovalPeriod,
            ),
            userId: account.id,
            userName: account.name,
            department: account.department ?? '',
            role: account.positionName ?? account.roleName ?? '',
            termNo: activeTargetTermNo,
            period: adminApprovalPeriod,
            isFinalized: false,
            performanceScore: 0,
            behaviorScore: 0,
            adjustmentScore: 0,
            executiveAdjustmentScore: 0,
            totalScore: 0,
            submittedAt: '',
            hasSnapshot: false,
          }
        );
      })
      .filter(
        (submission) =>
          !submission.hasSnapshot && Boolean(approvalReadinessMap[submission.submissionKey]),
      );

    const computedEntries = await Promise.all(
      candidates.map(async (submission) => {
        try {
          const score = await computeApprovalScoresFromSource(submission);
          return [submission.submissionKey, score] as const;
        } catch {
          return null;
        }
      }),
    );

    if (requestSeq !== adminApprovalScoreLoadSeqRef.current) return;

    const nextMap: Record<string, SubmittedEvaluationScoreBreakdown> = {};
    computedEntries.forEach((entry) => {
      if (!entry) return;
      nextMap[entry[0]] = entry[1];
    });
    setComputedApprovalScoreMap((prev) => ({ ...prev, ...nextMap }));
  };

  const hydrateApprovalReadiness = async () => {
    if (activeTab !== 'admin_approvals') return;
    if (accountList.length === 0) return;
    const requestSeq = ++adminApprovalReadinessSeqRef.current;
    const approvalTargetAccounts = accountList.filter(
      (account) => !isSystemAdminUser(account.roleId, account.roleName),
    );
    const filteredSubmissions = submittedEvaluations.filter(
      (row) => row.termNo === activeTargetTermNo && row.period === adminApprovalPeriod,
    );
    const submissionByUserId = new Map(filteredSubmissions.map((row) => [row.userId, row] as const));

    const candidates = approvalTargetAccounts.map((account) => {
      const existing = submissionByUserId.get(account.id);
      return (
        existing ?? {
          submissionKey: getEvaluationSubmissionKey(
            account.id,
            activeTargetTermNo,
            adminApprovalPeriod,
          ),
          userId: account.id,
          userName: account.name,
          department: account.department ?? '',
          role: account.positionName ?? account.roleName ?? '',
          termNo: activeTargetTermNo,
          period: adminApprovalPeriod,
          isFinalized: false,
          performanceScore: 0,
          behaviorScore: 0,
          adjustmentScore: 0,
          executiveAdjustmentScore: 0,
          totalScore: 0,
          submittedAt: '',
          hasSnapshot: false,
        }
      );
    });

    const result = await fetchFinalEvaluationMissingRequirementsBatch({
      userIds: candidates.map((submission) => submission.userId),
      termNo: activeTargetTermNo,
      period: adminApprovalPeriod,
    });
    if (requestSeq !== adminApprovalReadinessSeqRef.current) return;

    const readiness: Record<string, boolean> = {};
    const missingItems: Record<string, string[]> = {};
    const itemByUserId = new Map(result.rows.map((row) => [row.userId, row.items] as const));
    candidates.forEach((submission) => {
      const key = submission.submissionKey;
      const items = itemByUserId.get(submission.userId) ?? ['判定エラー'];
      missingItems[key] = items;
      readiness[key] = items.length === 0;
    });
    setApprovalMissingItemsMap(missingItems);
    setApprovalReadinessMap(readiness);
  };

  const openApprovalDetailSection = async (
    section: ApprovalDetailSection,
    submission: SubmittedEvaluationSnapshot,
  ) => {
    setApprovalDetailSection(section);
    if (section === 'summary' || section === 'executive') {
      return;
    }

    setIsApprovalSectionLoading(true);
    try {
      if (section === 'performance') {
        const detail = await buildPerformanceDetail(submission);
        setApprovalPerformanceDetail(detail);
      }
      if (section === 'behavior') {
        const detail = await buildBehaviorDetail(submission);
        setApprovalBehaviorDetail(detail);
      }
      if (section === 'adjustment') {
        const detail = await buildAdjustmentDetail(submission);
        setApprovalAdjustmentDetail(detail);
      }
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '詳細データの取得に失敗しました',
        'error',
      );
      setApprovalDetailSection('summary');
    } finally {
      setIsApprovalSectionLoading(false);
    }
  };

  const mapAdminStoreAdjustmentDetail = (
    entries: { adjustmentItemId: number; count: number }[],
  ): ApprovalAdjustmentDetailItem[] =>
    adjustmentConfigItems
      .map((item) => {
        const matched = entries.find((entry) => entry.adjustmentItemId === item.id);
        const count = Number(matched?.count ?? 0);
        return {
          id: item.id,
          category: item.category,
          item: item.item,
          unitPoint: item.unitPoint,
          count,
          score: count * item.unitPoint,
        };
      });

  const loadAdminStoreAdjustmentDetail = async (
    storeId: number,
    termNo: number,
    period: ScheduleQuarter,
  ) => {
    if (adjustmentConfigItems.length === 0) return;
    setIsAdminStoreAdjustmentLoading(true);
    try {
      const result = await fetchStoreAdjustmentEvaluation({
        storeId,
        termNo,
        period,
      });
      setAdminStoreAdjustmentDetail(mapAdminStoreAdjustmentDetail(result.entries));
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : '店舗加減点データの取得に失敗しました',
        'error',
      );
      setAdminStoreAdjustmentDetail(mapAdminStoreAdjustmentDetail([]));
    } finally {
      setIsAdminStoreAdjustmentLoading(false);
    }
  };

  const openApprovalAiCommentModal = async (submission: SubmittedEvaluationSnapshot) => {
    if (!submission.hasSnapshot) return;
    setSelectedApprovalAiTarget(submission);
    setIsApprovalAiCommentLoading(true);
    setApprovalAiComment({ summary: '', strength: '', improvement: '' });
    try {
      const result = await fetchFinalEvaluation({
        userId: submission.userId,
        termNo: submission.termNo,
        period: submission.period,
      });
      setApprovalAiComment({
        summary: result.aiSummary || '',
        strength: result.aiStrength || '',
        improvement: result.aiImprovement || '',
      });
    } catch (error) {
      setApprovalAiComment({
        summary: error instanceof Error ? error.message : 'AIコメントの取得に失敗しました。',
        strength: '',
        improvement: '',
      });
    } finally {
      setIsApprovalAiCommentLoading(false);
    }
  };

  const openDashboardPeriodModal = () => {
    const defaultTermNo = currentPeriodNow === 'second_half'
      ? currentTermNo
      : (pastTermOptions.at(-1)?.termNo ?? '');
    const defaultPeriods = getSelectablePastPeriodsForTerm(defaultTermNo);
    setDashboardTermDraft(defaultTermNo);
    setDashboardPeriodDraft(defaultPeriods[0] ?? 'first_half');
    setDashboardHistoryErrorMessage('');
    setDashboardHistoryPreview(null);
    setDashboardHistoryPerformanceRows([]);
    setDashboardHistoryBehaviorRows([]);
    setShowDashboardPeriodModal(true);
  };

  const applyDashboardPeriodSelection = async (
    termNoInput: number | '',
    periodInput: ScheduleQuarter,
  ) => {
    if (termNoInput === '') {
      showNotification('期を選択してください。', 'error');
      return;
    }
    if (!currentUserId) {
      showNotification('ユーザー情報を取得できませんでした。', 'error');
      return;
    }
    const isPastSelection =
      termNoInput < currentTermNo ||
      (termNoInput === currentTermNo &&
        currentPeriodNow === 'second_half' &&
        periodInput === 'first_half');
    const allowedPeriods = getSelectablePastPeriodsForTerm(termNoInput);
    if (!isPastSelection || !allowedPeriods.includes(periodInput)) {
      showNotification('過去分のみ表示できます。期・区分を見直してください。', 'error');
      return;
    }
    setIsDashboardHistoryLoading(true);
    setDashboardHistoryErrorMessage('');
    try {
      const result = await fetchFinalEvaluation({
        userId: currentUserId,
        termNo: termNoInput,
        period: periodInput,
      });
      if (!result.exists) {
        setDashboardHistoryPreview(null);
        setDashboardHistoryPerformanceRows([]);
        setDashboardHistoryBehaviorRows([]);
        setDashboardHistoryErrorMessage('この期間の最終評価データはありません。');
        return;
      }
      const performanceRows = await buildPerformanceDetail({
        submissionKey: `dashboard-history:${currentUserId}:${termNoInput}:${periodInput}`,
        userId: currentUserId,
        userName: activeDisplayUserName,
        department: '',
        role: activeDisplayRoleName,
        termNo: termNoInput,
        period: periodInput,
        approved: false,
        hasSnapshot: false,
        performanceScore: result.performanceScore,
        behaviorScore: result.behaviorScore,
        adjustmentScore: result.adjustmentScore,
        executiveAdjustmentScore: result.executiveAdjustmentScore,
        totalScore: result.totalScore,
        submittedAt: null,
      });
      const behaviorRows = await buildBehaviorDetail({
        submissionKey: `dashboard-history:${currentUserId}:${termNoInput}:${periodInput}`,
        userId: currentUserId,
        userName: activeDisplayUserName,
        department: '',
        role: activeDisplayRoleName,
        termNo: termNoInput,
        period: periodInput,
        approved: false,
        hasSnapshot: false,
        performanceScore: result.performanceScore,
        behaviorScore: result.behaviorScore,
        adjustmentScore: result.adjustmentScore,
        executiveAdjustmentScore: result.executiveAdjustmentScore,
        totalScore: result.totalScore,
        submittedAt: null,
      });
      setDashboardHistoryPreview({
        termNo: termNoInput,
        period: periodInput,
        performanceScore: result.performanceScore,
        behaviorScore: result.behaviorScore,
        adjustmentScore: result.adjustmentScore,
        executiveAdjustmentScore: result.executiveAdjustmentScore,
        totalScore: result.totalScore,
        rank: result.rank ?? null,
        aiSummary: result.aiSummary || '',
        aiStrength: result.aiStrength || '',
        aiImprovement: result.aiImprovement || '',
      });
      setDashboardHistoryPerformanceRows(
        performanceRows.map((row) => ({
          id: row.id,
          item: row.item,
          score: Number(row.score ?? 0),
        })),
      );
      setDashboardHistoryBehaviorRows(
        behaviorRows.map((row) => ({
          id: row.id,
          item: row.item,
          selfScore: Number(row.selfScore ?? 0),
          evaluatorScore: Number(row.evaluatorScore ?? 0),
          weight: Number(row.weight ?? 1),
        })),
      );
    } catch (error) {
      setDashboardHistoryErrorMessage(
        error instanceof Error ? error.message : '過去期間のダッシュボード取得に失敗しました',
      );
      setDashboardHistoryPreview(null);
      setDashboardHistoryPerformanceRows([]);
      setDashboardHistoryBehaviorRows([]);
    } finally {
      setIsDashboardHistoryLoading(false);
    }
  };

  const renderDashboard = () => {
    const dashboardQuarterLabel = quarterLabelMap[activeEmployeePeriod] ?? '';
    const dashboardPeriodRangeLabel =
      activeEmployeePeriod === 'first_half' ? '(11月〜4月)' : '(5月〜10月)';
    const currentEvaluationInputPeriod = evaluationInputPeriods[activeEmployeePeriod];
    const hasInputPeriodWindow =
      !!currentEvaluationInputPeriod?.fromDate && !!currentEvaluationInputPeriod?.toDate;
    const isWithinInputPeriod =
      hasInputPeriodWindow &&
      currentEvaluationInputPeriod!.fromDate <= todayYmd &&
      todayYmd <= currentEvaluationInputPeriod!.toDate;
    const isBeforeInputPeriod =
      hasInputPeriodWindow && todayYmd < currentEvaluationInputPeriod!.fromDate;
    const dashboardStatus = isFinalized
      ? '承認済み'
      : isWithinInputPeriod
        ? '入力中'
        : isBeforeInputPeriod
          ? '未開始'
          : '期間外';
    const dashboardStatusDetail =
      !isFinalized && hasInputPeriodWindow
        ? isWithinInputPeriod
          ? `${currentEvaluationInputPeriod!.fromDate}〜${currentEvaluationInputPeriod!.toDate} 入力可能`
          : `${currentEvaluationInputPeriod!.fromDate}〜${currentEvaluationInputPeriod!.toDate} 対象期間`
        : undefined;
    const performanceChartData = performanceData.map((row) => ({
      label: row.item,
      score: calculateWeightedScoreByMax(row.points, row.weight, row.max),
      weight: row.weight,
      points: row.points,
    }));
    const performanceMax = Math.max(...performanceChartData.map((d) => d.score), 1);

    const radarItems = behaviorData.map((item) => ({
      label: item.item,
      self: Number(item.selfScore || 0),
      evaluator: Number(item.evaluatorScore || 0),
      weight: Number(item.weight || 1),
    }));
    const radarCount = radarItems.length || 1;
    const radarMax = 4;
    const radarCenter = 110;
    const radarRadius = 90;
    const radarAngle = (index) => (Math.PI * 2 * index) / radarCount - Math.PI / 2;
    const radarPoint = (value, index) => {
      const ratio = value / radarMax;
      const angle = radarAngle(index);
      return [
        radarCenter + Math.cos(angle) * radarRadius * ratio,
        radarCenter + Math.sin(angle) * radarRadius * ratio,
      ];
    };
    const radarPath = (key) =>
      radarItems
        .map((item, idx) => radarPoint(item[key], idx).join(','))
        .join(' ');
    const radarCenterPath = Array.from({ length: radarCount }, () => `${radarCenter},${radarCenter}`).join(' ');
    const dashboardTotalScore =
      isFinalized && finalizedScoreSnapshot
        ? Number(finalizedScoreSnapshot.totalScore ?? 0)
        : Number(totalScore ?? 0);
    const dashboardComputedRank = [...rankThresholds]
      .sort((a, b) => Number(b.minScore) - Number(a.minScore))
      .find((item) => dashboardTotalScore >= Number(item.minScore))?.rank ?? null;
    const dashboardRankLabel = isFinalized ? (finalizedRank ?? dashboardComputedRank) : dashboardComputedRank;

    return (
      <div className="space-y-6">
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <button
            type="button"
            onClick={openDashboardPeriodModal}
            className="hidden md:inline-flex absolute -top-9 right-0 px-3 py-1.5 text-xs font-bold rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
          >
            過去分を見る
          </button>
          <Card className="col-span-2">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">評価対象期間</p>
              {isFinalized ? (
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight" style={{ color: COLORS.text }}>
                  <span className="block">第{activeEmployeeTermNo}期 {dashboardQuarterLabel}</span>
                  <span className="block text-base sm:text-lg text-gray-600">{dashboardPeriodRangeLabel}</span>
                </h3>
              ) : (
                <h3 className="text-[15px] sm:text-2xl font-bold tracking-tight whitespace-nowrap" style={{ color: COLORS.text }}>
                  第{activeEmployeeTermNo}期 {dashboardQuarterLabel} {dashboardPeriodRangeLabel}
                </h3>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={dashboardStatus} detail={dashboardStatusDetail} />
            </div>
          </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 text-center">
              <div className="p-4 bg-gray-50 border rounded-sm">
                <p className="text-xs font-bold text-gray-500 mb-2">店舗業績</p>
                <p className="text-2xl font-bold">
                  {formatOneDecimal(perfScore)} <span className="text-sm font-normal text-gray-400">/ {formatOneDecimal(perfMaxScore)}</span>
                </p>
              </div>
              <div className="p-4 bg-gray-50 border rounded-sm">
                <p className="text-xs font-bold text-gray-500 mb-2">行動評価</p>
                <p className="text-2xl font-bold">{formatOneDecimal(behaviorScore)}</p>
              </div>
              <div className="p-4 bg-gray-50 border rounded-sm">
                <p className="text-xs font-bold text-gray-500 mb-2">加減点</p>
                <p className={`text-2xl font-bold ${adjustScore < 0 ? 'text-[#D03020]' : ''}`}>
                  {formatOneDecimal(adjustScore)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 border rounded-sm">
                <p className="text-xs font-bold text-gray-500 mb-2">役職ポイント</p>
                <p className={`text-2xl font-bold ${effectiveRolePoint < 0 ? 'text-[#D03020]' : ''}`}>
                  {formatOneDecimal(effectiveRolePoint)}
                </p>
              </div>
            </div>
          </Card>
          <button
            type="button"
            onClick={openDashboardPeriodModal}
            className="md:hidden inline-flex w-fit justify-self-end px-3 py-1.5 text-xs font-bold rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
          >
            過去分を見る
          </button>
          <Card className="flex flex-col justify-center items-center">
            <p className="text-sm text-gray-500 mb-2 font-medium">総合評価スコア</p>
            <div className="text-6xl font-bold mb-2 tracking-tighter" style={{ color: COLORS.primary }}>
              {formatOneDecimal(dashboardTotalScore)}
            </div>
            {dashboardRankLabel ? (
              <div className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700">
                ランク {dashboardRankLabel}
              </div>
            ) : null}
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionTitle>店舗業績管理</SectionTitle>
            </div>
            <div className="bg-gray-50 border rounded-lg p-4">
              <svg viewBox="0 0 520 220" className="w-full h-[220px]">
                <line x1="40" y1="190" x2="500" y2="190" stroke="#e2e8f0" strokeWidth="2" />
                {performanceChartData.map((item, idx) => {
                  const barWidth = 60;
                  const gap = 22;
                  const x = 55 + idx * (barWidth + gap);
                  const height = (item.score / performanceMax) * 130;
                  const y = 190 - height;
                  return (
                    <g key={item.label}>
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={height}
                        rx="6"
                        fill="#6366f1"
                        opacity={0.85}
                        style={{
                          transform: `scaleY(${isDashboardPerfChartAnimated ? 1 : 0})`,
                          transformOrigin: 'center bottom',
                          transformBox: 'fill-box',
                          transition: `transform 550ms cubic-bezier(0.22, 1, 0.36, 1) ${idx * 80}ms`,
                        }}
                      />
                      <text
                        x={x + barWidth / 2}
                        y={y - 8}
                        textAnchor="middle"
                        fontSize="11"
                        fill="#475569"
                        style={{
                          opacity: isDashboardPerfChartAnimated ? 1 : 0,
                          transition: `opacity 300ms ease ${200 + idx * 80}ms`,
                        }}
                      >
                        {formatOneDecimal(Number(item.score ?? 0))}
                      </text>
                      <text x={x + barWidth / 2} y="208" textAnchor="middle" fontSize="10" fill="#64748b">{item.label}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionTitle>行動・技能・知識評価シート</SectionTitle>
            </div>
            <div className="bg-gray-50 border rounded-lg p-3">
              <svg viewBox="0 0 220 220" className="w-full h-[260px]">
                {[0.25, 0.5, 0.75, 1].map((ratio, idx) => (
                  <circle key={ratio} cx={radarCenter} cy={radarCenter} r={radarRadius * ratio} fill="none" stroke="#e2e8f0" strokeWidth={idx === 3 ? 2 : 1} />
                ))}
                {radarItems.map((item, idx) => {
                  const [x, y] = radarPoint(radarMax, idx);
                  return <line key={item.label} x1={radarCenter} y1={radarCenter} x2={x} y2={y} stroke="#e2e8f0" />;
                })}
                <polygon points={radarPath('self')} fill="rgba(59,130,246,0.35)" stroke="#3b82f6" strokeWidth="2" fillOpacity="0" strokeOpacity="0">
                  <animate
                    attributeName="points"
                    from={radarCenterPath}
                    to={radarPath('self')}
                    dur="0.45s"
                    begin="0.1s"
                    fill="freeze"
                  />
                  <animate attributeName="fill-opacity" from="0" to="0.35" dur="0.45s" begin="0.15s" fill="freeze" />
                  <animate attributeName="stroke-opacity" from="0" to="1" dur="0.45s" begin="0.15s" fill="freeze" />
                </polygon>
                <polygon points={radarPath('evaluator')} fill="rgba(16,185,129,0.25)" stroke="#10b981" strokeWidth="2" fillOpacity="0" strokeOpacity="0">
                  <animate
                    attributeName="points"
                    from={radarCenterPath}
                    to={radarPath('evaluator')}
                    dur="0.5s"
                    begin="0.24s"
                    fill="freeze"
                  />
                  <animate attributeName="fill-opacity" from="0" to="0.25" dur="0.5s" begin="0.28s" fill="freeze" />
                  <animate attributeName="stroke-opacity" from="0" to="1" dur="0.5s" begin="0.28s" fill="freeze" />
                </polygon>
                {radarItems.map((item, idx) => {
                  const [x, y] = radarPoint(item.self, idx);
                  return (
                    <circle key={`${item.label}-self`} cx={x} cy={y} r="0" fill="#3b82f6">
                      <animate
                        attributeName="r"
                        from="0"
                        to="3.5"
                        dur="0.24s"
                        begin={`${0.2 + idx * 0.05}s`}
                        fill="freeze"
                      />
                    </circle>
                  );
                })}
                {radarItems.map((item, idx) => {
                  const [x, y] = radarPoint(item.evaluator, idx);
                  return (
                    <circle key={`${item.label}-eval`} cx={x} cy={y} r="0" fill="#10b981">
                      <animate
                        attributeName="r"
                        from="0"
                        to="3.5"
                        dur="0.24s"
                        begin={`${0.35 + idx * 0.05}s`}
                        fill="freeze"
                      />
                    </circle>
                  );
                })}
                {radarItems.map((item, idx) => {
                  const [x, y] = radarPoint(radarMax + 0.2, idx);
                  const anchor = x < radarCenter - 10 ? 'end' : x > radarCenter + 10 ? 'start' : 'middle';
                  const dy = y < radarCenter ? -2 : 10;
                  return (
                    <text
                      key={`${item.label}-axis`}
                      x={x}
                      y={y + dy}
                      textAnchor={anchor}
                      fontSize="9"
                      fill="#475569"
                    >
                      {idx + 1}. {item.label}
                    </text>
                  );
                })}
              </svg>
            </div>
          </Card>
        </div>

        <Card>
          <div ref={dashboardAiCommentRef} />
          <div className="flex items-center justify-between mb-4">
            <SectionTitle>総合評価 AI分析コメント</SectionTitle>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
              <h4 className="text-lg font-bold text-slate-800 mb-2">総合所見</h4>
              <div className="text-slate-700">
                {renderAiCommentWithBold(
                  finalAiComment.summary || 'AIコメントはまだ生成されていません。',
                  'text-slate-900',
                )}
              </div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4">
              <h4 className="text-lg font-bold text-emerald-800 mb-2">強み</h4>
              <div className="text-emerald-900">
                {renderAiCommentWithBold(
                  finalAiComment.strength || 'AIコメントはまだ生成されていません。',
                  'text-emerald-950',
                )}
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
              <h4 className="text-lg font-bold text-amber-800 mb-2">改善提案</h4>
              <div className="text-amber-900">
                {renderAiCommentWithBold(
                  finalAiComment.improvement || 'AIコメントはまだ生成されていません。',
                  'text-amber-950',
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderFinalizedReadOnlyNotice = () =>
    !isAdmin && isFinalized ? (
      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
        評価確定済みのため、この画面は確認のみ可能です。
      </div>
    ) : null;

  const renderPerformance = () => (
    <Card>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <SectionTitle>店舗業績評価</SectionTitle>
        {employeeAssignedStores.length > 1 ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-xs font-bold text-gray-500">表示店舗</span>
            <MobileSelect
              value={String(selectedPerformanceStoreId)}
              onChange={(value) =>
                setSelectedPerformanceStoreId(value === 'average' ? 'average' : Number(value))
              }
              options={[
                { value: 'average', label: '掛け持ち平均（評価反映）' },
                ...employeeAssignedStores.map((store) => ({
                  value: String(store.storeId),
                  label: store.storeName,
                })),
              ]}
              selectClassName="border border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-700 bg-white"
              buttonClassName="border border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-700 bg-white min-w-[220px] text-left"
              sheetTitle="表示店舗を選択"
            />
          </div>
        ) : null}
      </div>
      {renderFinalizedReadOnlyNotice()}
      {employeeAssignedStores.length > 1 && selectedPerformanceStoreId !== 'average' ? (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          現在は店舗別表示です。最終評価に使う業績スコアは「掛け持ち平均（評価反映）」で計算されます。
        </div>
      ) : null}
      <div className="md:hidden space-y-3">
        {performanceViewData.map((row) => (
          <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-800 text-sm">{row.item}</span>
              <span className={`text-xs font-bold px-2 py-1 rounded ${row.points < 3 ? 'bg-red-50 text-[#D03020]' : 'bg-blue-50 text-[#80A0E0]'}`}>
                評価 {row.points}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 leading-tight">
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-400">実績</div>
                <div className={`font-bold ${row.points < 3 ? 'text-[#D03020]' : 'text-gray-800'}`}>{row.actual}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-400">ウェイト</div>
                <div className="font-bold text-gray-700">× {row.weight}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-400">加重点</div>
                <div className="font-bold text-indigo-600">
                  {calculateWeightedScoreByMax(row.points, row.weight, row.max)}
                </div>
              </div>
            </div>
          </div>
        ))}
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm font-bold flex justify-between">
          <span className="text-gray-600">業績評価 合計</span>
          <span style={{ color: COLORS.primary }}>{formatOneDecimal(performanceViewScore)}</span>
        </div>
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-100 text-gray-600 font-bold border-b">
            <tr>
              <th className="p-3">評価項目</th>
              <th className="p-3 text-right">実績</th>
              <th className="p-3 text-right">評価(0-4)</th>
              <th className="p-3 text-right">ウェイト</th>
              <th className="p-3 text-right bg-gray-50">加重点</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {performanceViewData.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="p-3 font-medium">{row.item}</td>
                <td className={`p-3 text-right font-bold ${row.points < 3 ? 'text-[#D03020]' : ''}`}>{row.actual}</td>
                <td className="p-3 text-right">
                  <span className={`inline-block px-2 py-1 rounded-sm text-xs font-bold ${row.points < 3 ? 'bg-red-50 text-[#D03020]' : 'bg-blue-50 text-[#80A0E0]'}`}>
                    {row.points}
                  </span>
                </td>
                <td className="p-3 text-right text-gray-500">× {row.weight}</td>
                <td className="p-3 text-right font-bold bg-gray-50">
                  {calculateWeightedScoreByMax(row.points, row.weight, row.max)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100 font-bold border-t-2">
            <tr>
              <td colSpan={5} className="p-3 text-right">業績評価 合計</td>
              <td className="p-3 text-right text-lg" style={{ color: COLORS.primary }}>{formatOneDecimal(performanceViewScore)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );

  const renderAdjustment = () => (
    <Card>
      <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
        <SectionTitle>加点・減点（店舗管理者入力）</SectionTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-bold text-gray-500">表示店舗</label>
            {employeeAssignedStores.length > 1 ? (
              <select
                value={String(selectedAdjustmentStoreId)}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedAdjustmentStoreId(value === 'average' ? 'average' : Number(value));
                }}
                className="w-64 rounded-xl border-2 border-[#1565C0] bg-white px-4 py-2 text-base font-semibold text-slate-700 focus:outline-none focus:ring-0"
              >
                <option value="average">掛け持ち平均（評価反映）</option>
                {employeeAssignedStores.map((store) => (
                  <option key={store.storeId} value={store.storeId}>
                    {store.storeName}
                  </option>
                ))}
              </select>
            ) : (
              <div
                className="w-80 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-base font-semibold text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis"
                title={employeeAssignedStores[0]?.storeName ?? '-'}
              >
                {employeeAssignedStores[0]?.storeName ?? '-'}
              </div>
            )}
          </div>
        </div>
      </div>
      {renderFinalizedReadOnlyNotice()}
      {employeeAssignedStores.length > 1 && selectedAdjustmentStoreId !== 'average' ? (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          現在は店舗別表示です。最終評価に使う加減点は「掛け持ち平均（評価反映）」で計算されます。
        </div>
      ) : null}
      {isAdjustmentStoreViewLoading ? (
        <div className="mb-6 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
          店舗別の加減点データを読み込み中です...
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="font-bold mb-4 flex items-center text-gray-700"><div className="w-1 h-6 bg-[#80A0E0] mr-3"></div>加点項目</h3>
          <div className="space-y-3">
            {adjustmentViewData.plus.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-sm border border-gray-200 shadow-sm">
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-700">{item.item}</div>
                  <div className="text-xs text-gray-400 mt-1">単価: {item.unitPoint}pt</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">件数</span>
                  <input type="number" min="0" disabled value={item.count} className="w-16 p-2 border rounded-sm text-right font-bold bg-gray-50 text-gray-700"/>
                  <div className="w-12 text-right font-bold text-[#80A0E0]">+{Number(item.count || 0) * item.unitPoint}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-bold mb-4 flex items-center text-gray-700"><div className="w-1 h-6 bg-[#D03020] mr-3"></div>減点項目</h3>
          <div className="space-y-3">
            {adjustmentViewData.minus.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-sm border border-gray-200 shadow-sm">
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-700">{item.item}</div>
                  <div className="text-xs text-gray-400 mt-1">単価: {item.unitPoint}pt</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">件数</span>
                  <input type="number" min="0" disabled value={item.count} className="w-16 p-2 border rounded-sm text-right font-bold bg-gray-50 text-gray-700"/>
                  <div className="w-12 text-right font-bold text-[#D03020]">{Number(item.count || 0) * item.unitPoint}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-8 pt-4 border-t flex justify-between items-center bg-gray-50 p-4 rounded-sm border" style={{ borderColor: COLORS.border }}>
        <span className="font-bold text-gray-600">加減点 合計</span>
        <span className={`text-2xl font-bold ${adjustmentViewScore < 0 ? 'text-[#D03020]' : 'text-[#303030]'}`}>{formatOneDecimal(adjustmentViewScore)} pt</span>
      </div>
    </Card>
  );

  const renderDailyReport = () => {
    const myReports = dailyReports.slice();
    const sortedMyReports = myReports
      .slice()
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    const visibleMyReports = sortedMyReports.slice(0, dailyReportHistoryVisibleCount);
    const canShowMoreMyReports = visibleMyReports.length < sortedMyReports.length;
    const enteredDateSet = new Set(myReports.map((report) => report.reportDate));
    const [calendarYear, calendarMonth] = dailyReportCalendarMonth
      .split('-')
      .map((value) => Number(value));
    const calendarMonthDate = new Date(calendarYear, (calendarMonth || 1) - 1, 1);
    const calendarStartWeekday = calendarMonthDate.getDay();
    const calendarDaysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
    const calendarPrevMonthDays = new Date(calendarYear, calendarMonth - 1, 0).getDate();
    const calendarCellDates = Array.from({ length: 42 }, (_, index) => {
      const dayOffset = index - calendarStartWeekday + 1;
      const cellDate = new Date(calendarYear, calendarMonth - 1, dayOffset);
      return {
        ymd: formatDateYmdLocal(cellDate),
        isCurrentMonth: dayOffset >= 1 && dayOffset <= calendarDaysInMonth,
        displayDay:
          dayOffset < 1
            ? calendarPrevMonthDays + dayOffset
            : dayOffset > calendarDaysInMonth
              ? dayOffset - calendarDaysInMonth
              : dayOffset,
      };
    });
    const moveDailyReportCalendarMonth = (delta: number) => {
      const [baseYear, baseMonth] = dailyReportCalendarMonth
        .split('-')
        .map((value) => Number(value));
      const baseDate = new Date(baseYear, (baseMonth || 1) - 1 + delta, 1);
      setDailyReportCalendarMonth(
        `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}`,
      );
    };

    return (
      <div className="space-y-6">
        <div ref={dailyReportInputCardRef}>
          <Card highlight>
          <SectionTitle>日報入力</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="md:col-span-1">
              <label className="block text-xs font-bold text-gray-500 mb-1">日付</label>
              <div ref={dailyReportCalendarRef} className="relative">
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={dailyReportDraft.reportDate ? dailyReportDraft.reportDate.replace(/-/g, '/') : ''}
                    onClick={() => setIsDailyReportCalendarOpen(true)}
                    placeholder="YYYY/MM/DD"
                    className="w-full p-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-bold text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setIsDailyReportCalendarOpen((prev) => !prev)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 hover:bg-gray-100"
                    aria-label="カレンダーを開く"
                  >
                    <Calendar size={16} />
                  </button>
                </div>
                {isDailyReportCalendarOpen ? (
                  <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                    <div className="mb-2 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => moveDailyReportCalendarMonth(-1)}
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-50"
                      >
                        前月
                      </button>
                      <div className="text-xs font-bold text-gray-700">
                        {calendarYear}年{calendarMonth}月
                      </div>
                      <button
                        type="button"
                        onClick={() => moveDailyReportCalendarMonth(1)}
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-50"
                      >
                        翌月
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-gray-400 mb-1">
                      {['日', '月', '火', '水', '木', '金', '土'].map((weekday) => (
                        <div key={weekday}>{weekday}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {calendarCellDates.map((cell) => {
                        const isSelectedDate = dailyReportDraft.reportDate === cell.ymd;
                        const isEntered = enteredDateSet.has(cell.ymd);
                        return (
                          <button
                            key={cell.ymd}
                            type="button"
                            onClick={() => applyDailyReportDate(cell.ymd)}
                            className={`relative h-8 rounded-md border text-xs font-bold transition-colors ${
                              isSelectedDate
                                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                : cell.isCurrentMonth
                                  ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                  : 'border-gray-100 bg-gray-50 text-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            <span>{cell.displayDay}</span>
                            {isEntered ? (
                              <span className="absolute right-1 top-0.5 text-[10px] text-emerald-600">✓</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-xs font-bold text-gray-500 mb-1">本日行ったこと・気付き</label>
            <textarea
              value={dailyReportDraft.todayWork}
              onChange={(e) => setDailyReportDraft((prev) => ({ ...prev, todayWork: e.target.value }))}
              className="w-full p-3 border border-gray-200 rounded-lg text-sm min-h-[120px] text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none"
              placeholder="例: 発注状況の確認 / 新人教育のフォロー / 週次売上の集計"
            />
          </div>

          <div className="mb-6">
            <label className="block text-xs font-bold text-gray-500 mb-1">次回の出勤で行うこと</label>
            <textarea
              value={dailyReportDraft.nextTasks}
              onChange={(e) => setDailyReportDraft((prev) => ({ ...prev, nextTasks: e.target.value }))}
              className="w-full p-3 border border-gray-200 rounded-lg text-sm min-h-[120px] text-gray-700 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none"
              placeholder="例: 在庫確認 / 重点声かけ / 人員配置の再確認"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleDailySubmit}
              disabled={isDailyReportSaving}
              className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-md hover:bg-indigo-700 transition-all disabled:opacity-60"
            >
              <Save size={16} /> {isDailyReportSaving ? '保存中...' : '日報を保存'}
            </button>
          </div>
          </Card>
        </div>

        <Card>
          <SectionTitle>過去の日報</SectionTitle>
          {isDailyReportLoading ? (
            <div className="text-sm text-gray-400 text-center py-10">読み込み中...</div>
          ) : null}
          {visibleMyReports.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-10">まだ日報がありません。</div>
          ) : (
            <div className="space-y-4">
              {visibleMyReports.map(report => (
                <div key={report.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => jumpToDailyReportInput(report.reportDate)}
                      className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-sm font-extrabold text-slate-700 transition-colors hover:bg-slate-200"
                    >
                      {report.reportDate}
                    </button>
                    <span className="text-xs font-medium text-slate-400">
                      更新: {new Date(report.updatedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-1 text-[11px] font-bold tracking-wide text-slate-500">
                      本日行ったこと・気付き
                    </div>
                    <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                      {report.todayWork}
                    </div>
                  </div>
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                    <div className="mb-1 text-[11px] font-bold tracking-wide text-indigo-500">
                      次回の出勤で行うこと
                    </div>
                    <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                      {report.nextTasks || '記載なし'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {canShowMoreMyReports ? (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setDailyReportHistoryVisibleCount((prev) => prev + 5)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                次の5件を表示
              </button>
            </div>
          ) : null}
        </Card>
      </div>
    );
  };

  const renderAdminReports = () => {
    const filteredByUser = reportFilterUserId === 'all'
      ? dailyReports
      : dailyReports.filter((r) => r.userId === reportFilterUserId);
    const enteredAdminReportDateSet = new Set(filteredByUser.map((report) => report.reportDate));
    const filtered = reportFilterDate
      ? filteredByUser.filter((r) => r.reportDate === reportFilterDate)
      : filteredByUser;
    const sorted = filtered
      .slice()
      .sort((a, b) => {
        if (a.reportDate !== b.reportDate) {
          return b.reportDate.localeCompare(a.reportDate);
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    const [adminCalendarYear, adminCalendarMonth] = adminReportCalendarMonth
      .split('-')
      .map((value) => Number(value));
    const adminCalendarMonthDate = new Date(adminCalendarYear, (adminCalendarMonth || 1) - 1, 1);
    const adminCalendarStartWeekday = adminCalendarMonthDate.getDay();
    const adminCalendarDaysInMonth = new Date(adminCalendarYear, adminCalendarMonth, 0).getDate();
    const adminCalendarPrevMonthDays = new Date(adminCalendarYear, adminCalendarMonth - 1, 0).getDate();
    const adminCalendarCellDates = Array.from({ length: 42 }, (_, index) => {
      const dayOffset = index - adminCalendarStartWeekday + 1;
      const cellDate = new Date(adminCalendarYear, adminCalendarMonth - 1, dayOffset);
      return {
        ymd: formatDateYmdLocal(cellDate),
        isCurrentMonth: dayOffset >= 1 && dayOffset <= adminCalendarDaysInMonth,
        displayDay:
          dayOffset < 1
            ? adminCalendarPrevMonthDays + dayOffset
            : dayOffset > adminCalendarDaysInMonth
              ? dayOffset - adminCalendarDaysInMonth
              : dayOffset,
      };
    });
    const moveAdminReportCalendarMonth = (delta: number) => {
      const [baseYear, baseMonth] = adminReportCalendarMonth
        .split('-')
        .map((value) => Number(value));
      const baseDate = new Date(baseYear, (baseMonth || 1) - 1 + delta, 1);
      setAdminReportCalendarMonth(
        `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}`,
      );
    };
    const reportSelectableUsers = accountList
      .filter(
        (account) =>
          !isSystemAdminUser(account.roleId, account.roleName) &&
          (Number(account.roleId) === 2 || account.roleName === '社員'),
      )
      .map((account) => ({ value: account.id, label: account.name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));

    return (
      <div className="space-y-6">
        <Card highlight>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <SectionTitle isAdminMode>日報管理</SectionTitle>
              <p className="text-xs text-gray-500">各社員の日報を確認できます。</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-full md:w-44">
                <div ref={adminReportCalendarRef} className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={reportFilterDate ? reportFilterDate.replace(/-/g, '/') : ''}
                      onClick={() => setIsAdminReportCalendarOpen(true)}
                      placeholder="YYYY/MM/DD"
                      className="w-full rounded-lg border border-gray-200 bg-white p-2 pr-9 text-sm font-bold text-gray-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setIsAdminReportCalendarOpen((prev) => !prev)}
                      className="absolute right-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-gray-600 hover:bg-gray-100"
                      aria-label="日付カレンダーを開く"
                    >
                      <Calendar size={14} />
                    </button>
                  </div>
                  {isAdminReportCalendarOpen ? (
                    <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                      <div className="mb-2 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => moveAdminReportCalendarMonth(-1)}
                          className="rounded-md border border-gray-200 px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-50"
                        >
                          前月
                        </button>
                        <div className="text-xs font-bold text-gray-700">
                          {adminCalendarYear}年{adminCalendarMonth}月
                        </div>
                        <button
                          type="button"
                          onClick={() => moveAdminReportCalendarMonth(1)}
                          className="rounded-md border border-gray-200 px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-50"
                        >
                          翌月
                        </button>
                      </div>
                      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-gray-400">
                        {['日', '月', '火', '水', '木', '金', '土'].map((weekday) => (
                          <div key={weekday}>{weekday}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {adminCalendarCellDates.map((cell) => {
                          const isSelectedDate = reportFilterDate === cell.ymd;
                          const isEntered = enteredAdminReportDateSet.has(cell.ymd);
                          return (
                            <button
                              key={cell.ymd}
                              type="button"
                              onClick={() => applyAdminReportFilterDate(cell.ymd)}
                              className={`relative h-8 rounded-md border text-xs font-bold transition-colors ${
                                isSelectedDate
                                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                  : cell.isCurrentMonth
                                    ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                    : 'border-gray-100 bg-gray-50 text-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              <span>{cell.displayDay}</span>
                              {isEntered ? (
                                <span className="absolute right-1 top-0.5 text-[10px] text-emerald-600">✓</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="w-full md:w-44">
                <MobileSelect
                  value={reportFilterUserId}
                  onChange={(value) => setReportFilterUserId(value)}
                  options={[
                    { value: 'all', label: '全ユーザー' },
                    ...reportSelectableUsers,
                  ]}
                  selectClassName="border border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-700 bg-white shadow-sm"
                  buttonClassName="border border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-700 bg-white shadow-sm"
                  sheetTitle="ユーザーを選択"
                />
              </div>
              <div className="text-xs bg-gray-100 px-3 py-2 rounded-lg border text-gray-600">
                件数: <span className="font-bold text-gray-800">{sorted.length}</span>
              </div>
            </div>
          </div>

          {sorted.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-10">日報がまだ提出されていません。</div>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {sorted.map(report => {
                  return (
                    <button
                      key={report.id}
                      type="button"
                      onClick={() => setSelectedAdminReport(report)}
                      className="w-full border border-gray-200 rounded-lg p-3 bg-white text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-gray-700">{report.reportDate}</span>
                        <span className="text-[10px] text-gray-400">{report.userName || '不明'}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 mb-2">
                        更新: {new Date(report.updatedAt).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-700 mb-3 whitespace-pre-wrap">
                        {report.todayWork}
                      </div>
                      <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">
                        <span className="font-bold text-gray-500">次回:</span> {report.nextTasks || '記載なし'}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                <table className="w-full table-fixed text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-200">
                    <tr>
                      <th className="p-4 w-[140px]">日付</th>
                      <th className="p-4 w-[140px]">氏名</th>
                      <th className="p-4 w-[190px]">更新日</th>
                      <th className="p-4 w-[260px]">本日行ったこと・気付き</th>
                      <th className="p-4 w-[260px]">次回の出勤で行うこと</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {sorted.map(report => {
                      const todayWorkText = report.todayWork || '記載なし';
                      const nextTasksText = report.nextTasks || '記載なし';
                      const userNameText = report.userName || '不明';
                      const updatedAtText = new Date(report.updatedAt).toLocaleString();
                      return (
                        <tr
                          key={report.id}
                          onClick={() => setSelectedAdminReport(report)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedAdminReport(report);
                            }
                          }}
                          tabIndex={0}
                          className="cursor-pointer hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                        >
                          <td className="p-4 text-gray-600" title={report.reportDate}>
                            <div className="truncate">{report.reportDate}</div>
                          </td>
                          <td className="p-4 font-bold text-gray-800" title={userNameText}>
                            <div className="truncate">{userNameText}</div>
                          </td>
                          <td className="p-4 text-gray-600 text-xs" title={updatedAtText}>
                            <div className="truncate">{updatedAtText}</div>
                          </td>
                          <td className="p-4 text-gray-700 text-xs" title={todayWorkText}>
                            <div className="truncate">{todayWorkText}</div>
                          </td>
                          <td className="p-4 text-gray-600 text-xs" title={nextTasksText}>
                            <div className="truncate">{nextTasksText}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>
    );
  };

  const renderFinal = () => {
    if (isFinalEvaluationDataLoading) {
      return (
        <div className="max-w-3xl mx-auto">
          <Card>
            <SectionTitle>最終評価確定</SectionTitle>
            <div className="py-12 flex items-center justify-center gap-3 text-gray-500">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm font-medium">評価データを読み込み中です...</span>
            </div>
          </Card>
        </div>
      );
    }

    const finalizeBlockedReasons: string[] = [];
    if (
      isStoreScoreLoading ||
      isEmployeePerformanceTargetLoading ||
      !hasStoreScoreLoaded ||
      !hasEmployeePerformanceTargetLoaded
    ) {
      finalizeBlockedReasons.push('店舗業績データを読み込み中です。');
    }
    if (!hasPerformanceEvaluation) {
      finalizeBlockedReasons.push('店舗業績評価が未入力です。');
    }
    if (!hasSelfBehaviorEvaluation) {
      finalizeBlockedReasons.push('自己の行動評価が未入力です。');
    }
    if (!behaviorSupervisorUserId) {
      finalizeBlockedReasons.push('上長が未設定です。');
    } else if (!hasSupervisorBehaviorEvaluation) {
      finalizeBlockedReasons.push('上長の行動評価が未入力です。');
    }
    if (!hasAdjustmentEvaluation) {
      finalizeBlockedReasons.push('加点・減点の件数が未入力です。');
    }
    const isFinalizeDisabled = finalizeBlockedReasons.length > 0;
    const displayPerformanceScore =
      isFinalized && finalizedScoreSnapshot
        ? finalizedScoreSnapshot.performanceScore
        : perfScore;
    const displayBehaviorScore =
      isFinalized && finalizedScoreSnapshot ? finalizedScoreSnapshot.behaviorScore : behaviorScore;
    const displayAdjustmentScore =
      isFinalized && finalizedScoreSnapshot
        ? finalizedScoreSnapshot.adjustmentScore
        : adjustScore;
    const displayExecutiveAdjustmentScore =
      isFinalized && finalizedScoreSnapshot
        ? finalizedScoreSnapshot.executiveAdjustmentScore
        : effectiveRolePoint;
    const displayTotalScore =
      isFinalized && finalizedScoreSnapshot ? finalizedScoreSnapshot.totalScore : totalScore;

    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <SectionTitle>最終評価確定</SectionTitle>
          <div
            className="space-y-2 mb-8 bg-gray-50 p-6 rounded-sm border"
            style={{ borderColor: COLORS.border }}
          >
          <div className="flex justify-between items-center p-3 border-b border-gray-200">
            <span className="text-gray-600 font-medium">店舗業績スコア</span>
            <span className="font-bold text-lg">{formatOneDecimal(displayPerformanceScore)}</span>
          </div>
          <div className="flex justify-between items-center p-3 border-b border-gray-200">
            <span className="text-gray-600 font-medium">行動評価スコア</span>
            <span className="font-bold text-lg">{formatOneDecimal(displayBehaviorScore)}</span>
          </div>
          <div className="flex justify-between items-center p-3">
            <span className="text-gray-600 font-medium">加点・減点</span>
            <span
              className={`font-bold text-lg ${displayAdjustmentScore < 0 ? 'text-[#D03020]' : ''}`}
            >
              {formatOneDecimal(displayAdjustmentScore)}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 border-t border-gray-200">
            <span className="text-gray-600 font-medium">役職ポイント</span>
            <span className="font-bold text-lg">{formatOneDecimal(displayExecutiveAdjustmentScore)}</span>
          </div>
        </div>

        <div className="bg-[#303030] text-white p-6 rounded-sm shadow-md flex justify-between items-center mb-8">
          <div>
            <span className="block text-sm text-gray-400 mb-1">総合評価スコア</span>
            <span className="text-xs text-gray-500 uppercase tracking-widest">Total Score</span>
          </div>
          <div className="text-5xl font-bold tracking-tight">
            {formatOneDecimal(displayTotalScore)} <span className="text-lg font-normal text-gray-400">pt</span>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          {isFinalized ? (
            <div className="w-full bg-gray-100 text-gray-500 font-bold py-4 rounded-sm text-center flex items-center justify-center gap-2 border">
              <Lock size={20} /> 評価確定済み
            </div>
          ) : (
            <div className="w-full">
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={isFinalizeDisabled}
                className={`w-full text-white font-bold py-4 rounded-sm shadow-md transition-all flex items-center justify-center gap-2 ${
                  isFinalizeDisabled
                    ? 'bg-gray-300 cursor-not-allowed shadow-none'
                    : 'bg-[#80A0E0] hover:bg-[#6c8cc9] active:transform active:scale-[0.99]'
                }`}
              >
                <FileCheck size={20} /> 評価を確定する
              </button>
              {isFinalizeDisabled ? (
                <p className="mt-2 text-sm text-[#D03020] font-medium">
                  {finalizeBlockedReasons[0]}
                </p>
              ) : null}
            </div>
          )}
        </div>
        </Card>
      </div>
    );
  };

  const renderBehavior = (options?: { evaluatorOnly?: boolean; showPeriodOnly?: boolean }) => {
    const evaluatorOnly = options?.evaluatorOnly ?? false;
    const showPeriodOnly = options?.showPeriodOnly ?? false;
    const behaviorUsers =
      evaluationLineUserList.length > 0
        ? evaluationLineUserList.map((user) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            roleId: user.roleId,
            role: user.roleName,
            department: user.department ?? '',
          }))
        : accountList.map((account) => ({
            id: account.id,
            name: account.name,
            email: account.email,
            roleId: account.roleId,
            role: account.roleName,
            department: account.department ?? '',
          }));
    const filteredBehaviorUsers = behaviorUsers.filter(
      (user) => !isSystemAdminUser(user.roleId, user.role),
    );
    const mySubordinates = evalRelations[currentUserId] || [];
    const evaluatorTargets =
      evaluatorOnly && isAdmin && isSystemAdminMode
        ? filteredBehaviorUsers
            .filter((user) => user.id !== currentUserId)
            .map((user) => user.id)
            .filter((id) => isUuid(id))
        : mySubordinates.filter((id) => isUuid(id));
    const isEvaluator = evaluatorTargets.length > 0;
    const hasSupervisor = Object.entries(evalRelations).some(
      ([evaluatorUserId, evaluateeUserIds]) =>
        evaluatorUserId !== currentUserId && evaluateeUserIds.includes(currentUserId),
    );
    const showSelf = evaluatorOnly ? true : behaviorViewMode === 'both' || behaviorViewMode === 'self';
    const showEvaluator = evaluatorOnly
      ? true
      : behaviorViewMode === 'evaluator' || (behaviorViewMode === 'both' && hasSupervisor);
    const isThreeColumnLayout = showSelf && showEvaluator;
    const definitionColClass = isThreeColumnLayout ? 'col-span-4' : 'col-span-6';
    const selfColClass = isThreeColumnLayout ? 'col-span-4' : 'col-span-6';
    const evaluatorColClass = isThreeColumnLayout ? 'col-span-4' : 'col-span-6';
    const targetUser = filteredBehaviorUsers.find((u) => u.id === selectedTargetUserId);
    const isEvaluatorTargetLocked =
      (behaviorViewMode === 'evaluator' || evaluatorOnly) && selectedTargetIsFinalized;
    const isAdminBehaviorSelectionCurrent =
      adminBehaviorEvalTermNo === currentTermNo && adminBehaviorEvalPeriod === currentPeriodNow;

    if ((behaviorViewMode === 'evaluator' || evaluatorOnly) && !isEvaluator) {
      return (
        <div className="space-y-6">
          <Card>
            <SectionTitle>行動・技能・知識評価シート</SectionTitle>
            <p className="text-sm text-gray-500 mt-2">部下を持つ評価者のみ上長評価を入力できます。</p>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-6 pb-28">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1 w-full">
            <SectionTitle>行動・技能・知識評価シート</SectionTitle>
            {showPeriodOnly ? (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-xs text-gray-500">評価期間:</div>
                  <select
                    value={adminBehaviorEvalTermNo}
                    onChange={(event) => setAdminBehaviorEvalTermNo(Number(event.target.value))}
                    className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-bold text-slate-700"
                  >
                    {(scheduleTermOptions.length > 0
                      ? scheduleTermOptions
                      : [{ termNo: currentTermNo, startDate: '', endDate: '' }]
                    ).map((term) => (
                      <option key={`admin-behavior-term-${term.termNo}`} value={term.termNo}>
                        第{term.termNo}期
                      </option>
                    ))}
                  </select>
                  <select
                    value={adminBehaviorEvalPeriod}
                    onChange={(event) =>
                      setAdminBehaviorEvalPeriod(event.target.value as ScheduleQuarter)
                    }
                    className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-bold text-slate-700"
                  >
                    <option value="first_half">{quarterLabelMap.first_half}</option>
                    <option value="second_half">{quarterLabelMap.second_half}</option>
                  </select>
                  <div className="text-xs text-slate-500">
                    {adminBehaviorEvalLockedPeriodRange?.fromDate ?? '-'}〜
                    {adminBehaviorEvalLockedPeriodRange?.toDate ?? '-'}
                  </div>
                </div>
                {!isAdminBehaviorSelectionCurrent ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                    現在の評価期間（第{currentTermNo}期 {quarterLabelMap[currentPeriodNow]}）ではありません。対象期間を確認してください。
                  </div>
                ) : null}
              </div>
            ) : (behaviorViewMode === 'evaluator' || evaluatorOnly) &&
              Boolean(selectedTargetUserId) &&
              selectedTargetIsFinalized ? (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                この部下は承認済みのため、上長評価は確認のみ可能です。
              </div>
            ) : !isAdmin && behaviorViewMode !== 'evaluator' && isFinalized ? (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                評価確定済みのため、この画面は確認のみ可能です。
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-1">左の評価基準を直接クリックしてスコアを選択してください。</p>
            )}
          </div>
          {!evaluatorOnly ? (
          <div className="w-full md:w-auto flex flex-wrap items-center gap-2 bg-gray-100 p-1 rounded-md">
            <button onClick={() => setBehaviorViewMode('both')} className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-1 transition-all ${behaviorViewMode === 'both' ? 'bg-white text-[#80A0E0] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><Layout size={14} /> 全体比較</button>
            <button onClick={() => setBehaviorViewMode('self')} className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-1 transition-all ${behaviorViewMode === 'self' ? 'bg-white text-[#80A0E0] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><User size={14} /> 本人評価</button>
            {isEvaluator && (
              <button onClick={() => setBehaviorViewMode('evaluator')} className={`px-3 py-1.5 text-xs font-bold rounded-md flex items-center gap-1 transition-all ${behaviorViewMode === 'evaluator' ? 'bg-white text-[#80A0E0] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><UserCheck size={14} /> 上長評価</button>
            )}
          </div>
          ) : null}
        </div>

        {(behaviorViewMode === 'evaluator' || evaluatorOnly) && (
          <Card>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="text-sm font-bold text-gray-700">評価対象者</div>
              <select
                value={selectedTargetUserId}
                onChange={(event) => setSelectedTargetUserId(event.target.value)}
                className="w-full sm:w-auto border border-gray-200 rounded-md px-3 py-2 text-sm"
              >
                {evaluatorTargets.length === 0 ? (
                  <option value="">評価対象なし</option>
                ) : null}
                {evaluatorTargets.map((userId) => {
                  const user = filteredBehaviorUsers.find((u) => u.id === userId);
                  if (!user) return null;
                  return (
                    <option key={userId} value={userId}>
                      {user.name}
                    </option>
                  );
                })}
              </select>
            </div>
            {evaluatorTargets.length === 0 ? (
              <p className="text-xs text-gray-500">評価対象の部下が未設定です。評価ライン設定後に表示されます。</p>
            ) : null}
            {!targetUser ? (
              <p className="text-xs text-amber-600">評価対象者を選択してください。</p>
            ) : null}
            {targetUser && isEvaluatorTargetLocked ? (
              <p className="text-xs text-amber-600 mt-1">
                この部下は承認済みのため、上長評価は編集できません。
              </p>
            ) : null}
          </Card>
        )}

        <div className="md:hidden space-y-3">
          {behaviorData.map((item) => {
            const isSelfDisabled = behaviorViewMode !== 'self' || isFinalized;
            const isEvaluatorDisabled =
              (!evaluatorOnly && behaviorViewMode !== 'evaluator') || isEvaluatorTargetLocked || !selectedTargetUserId;

            return (
              <div
                key={item.id}
                className="bg-white border rounded-sm shadow-sm p-3 space-y-3"
                style={{ borderColor: COLORS.border }}
              >
                <div className="space-y-2">
                  <h4 className="font-bold text-gray-800 text-sm">{item.item}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 px-2 py-1.5 rounded border border-gray-100">
                    {item.def}
                  </p>
                </div>
                {item.criteria ? (
                  <div className="bg-white border border-gray-200 rounded p-2">
                    <p className="text-[10px] font-bold text-gray-500 mb-1">評価レベル基準</p>
                    <div className="space-y-1">
                      {[4, 3, 2, 1, 0].map((level) => {
                        const isSelfSelected = Number(item.selfScore) === level;
                        const isEvaluatorSelected = Number(item.evaluatorScore) === level;
                        let isSelected = isSelfSelected;
                        let isClickable = false;
                        let target: 'self' | 'evaluator' = 'self';
                        if (!evaluatorOnly && behaviorViewMode === 'self') {
                          isSelected = isSelfSelected;
                          isClickable = !isSelfDisabled;
                          target = 'self';
                        } else if (behaviorViewMode === 'evaluator' || evaluatorOnly) {
                          isSelected = isEvaluatorSelected;
                          isClickable = !isEvaluatorDisabled;
                          target = 'evaluator';
                        }
                        return item.criteria[level] ? (
                          <button
                            key={level}
                            type="button"
                            disabled={!isClickable}
                            onClick={() => handleSelectBehaviorLevel(item.id, level, target)}
                            className={`w-full text-left text-xs flex gap-2 items-start px-2 py-1.5 rounded transition-colors ${
                              isSelected ? 'bg-blue-600 text-white font-bold' : 'text-gray-600 bg-gray-50'
                            } ${isClickable ? 'cursor-pointer hover:bg-blue-100' : 'cursor-default'}`}
                          >
                            <span className="font-bold w-4 shrink-0">{level}:</span>
                            <span className="leading-tight flex-1">{item.criteria[level]}</span>
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                ) : null}
                {showSelf ? (
                  <div className="p-2 border rounded bg-blue-50/20">
                    <div className="text-xs font-bold text-gray-500 mb-1">本人評価</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Score</span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-white text-[#80A0E0] border border-blue-200">
                        {item.selfScore}
                      </span>
                    </div>
                    <div className="mt-2">
                      <div className="text-xs font-bold text-gray-500 mb-1">本人コメント</div>
                      {!evaluatorOnly && behaviorViewMode === 'self' ? (
                        <textarea
                          disabled={isSelfDisabled}
                          value={item.selfComment}
                          onChange={(e) => handleBehaviorChange(item.id, 'selfComment', e.target.value)}
                          placeholder="コメントを入力してください"
                          className={`w-full p-2 text-sm border rounded resize-none min-h-[72px] ${
                            isSelfDisabled
                              ? 'bg-gray-50 text-gray-600'
                              : 'bg-white focus:ring-1 focus:ring-gray-400 focus:border-gray-400'
                          }`}
                        />
                      ) : (
                        <div className="w-full p-2 text-sm border rounded bg-white text-gray-700 min-h-[72px] whitespace-pre-wrap break-words">
                          {item.selfComment}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
                {showEvaluator ? (
                  <div className="p-2 border rounded bg-gray-50/40 space-y-2">
                    <div className="text-xs font-bold text-gray-500">上長評価</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Score</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold ${isEvaluatorDisabled ? 'bg-gray-100 text-gray-500 border border-gray-200' : 'bg-white text-slate-700 border border-slate-200'}`}>
                        {item.evaluatorScore}
                      </span>
                    </div>
                    <textarea
                      disabled={isEvaluatorDisabled}
                      value={item.evaluatorComment}
                      onChange={(e) => handleBehaviorChange(item.id, 'evaluatorComment', e.target.value)}
                      placeholder={(behaviorViewMode !== 'evaluator' && !evaluatorOnly) ? '（上長評価入力待ち）' : 'コメントを入力してください'}
                      className={`w-full p-2 text-sm border rounded resize-none min-h-[88px] ${isEvaluatorDisabled ? 'bg-gray-50 text-gray-600' : 'bg-white focus:ring-1 focus:ring-gray-400 focus:border-gray-400'}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="hidden md:block bg-white border rounded-sm shadow-sm overflow-x-auto md:overflow-hidden" style={{ borderColor: COLORS.border }}>
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 bg-gray-100 border-b text-sm font-bold text-gray-700" style={{ borderColor: COLORS.border }}>
              <div className={`${definitionColClass} p-3 border-b md:border-b-0 md:border-r flex items-center`}>評価項目 / 基準定義</div>
              {showSelf && (<div className={`${selfColClass} p-3 border-b md:border-b-0 md:border-r bg-blue-50/30`}><span className="flex items-center gap-2 text-[#80A0E0]"><Users size={16}/> 本人評価</span></div>)}
              {showEvaluator && (<div className={`${evaluatorColClass} p-3 bg-gray-200/50`}><span className="flex items-center gap-2 text-gray-600"><UserCheck size={16}/> 上長評価</span></div>)}
            </div>
            {behaviorData.map((item) => {
            const isSelfDisabled = behaviorViewMode !== 'self' || isFinalized;
            const isEvaluatorDisabled =
              (!evaluatorOnly && behaviorViewMode !== 'evaluator') || isEvaluatorTargetLocked || !selectedTargetUserId;

            return (
              <div key={item.id} className="grid grid-cols-12 border-b last:border-b-0" style={{ borderColor: COLORS.border }}>
                <div className={`${definitionColClass} p-3 border-b md:border-b-0 md:border-r bg-gray-50/30`}>
                  <h4 className="font-bold text-gray-800 mb-1.5">{item.item}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed bg-white px-2 py-1.5 rounded border border-gray-100 mb-2">{item.def}</p>
                  {item.criteria && (
                    <div className="bg-white/50 border border-gray-200 rounded p-1.5">
                      <p className="text-[10px] font-bold text-gray-400 mb-1 border-b border-gray-100 pb-1">評価レベル基準</p>
                      <div className="space-y-0.5">
                        {[4, 3, 2, 1, 0].map((level) => {
                          const isSelfSelected = Number(item.selfScore) === level;
                          const isEvaluatorSelected = Number(item.evaluatorScore) === level;
                          let isSelected = isSelfSelected;
                          let isClickable = false;
                          let target: 'self' | 'evaluator' = 'self';
                          if (!evaluatorOnly && behaviorViewMode === 'self') {
                            isSelected = isSelfSelected;
                            isClickable = !isSelfDisabled;
                            target = 'self';
                          } else if (behaviorViewMode === 'evaluator' || evaluatorOnly) {
                            isSelected = isEvaluatorSelected;
                            isClickable = !isEvaluatorDisabled;
                            target = 'evaluator';
                          }
                          return item.criteria[level] ? (
                            <button
                              key={level}
                              type="button"
                              disabled={!isClickable}
                              onClick={() => handleSelectBehaviorLevel(item.id, level, target)}
                              className={`w-full text-left text-[10px] flex gap-1.5 items-start px-1.5 py-1 rounded transition-colors duration-200 ${
                                isSelected ? 'bg-blue-600 text-white shadow-md transform scale-105 font-bold border-l-4 border-yellow-400' : level === 2 ? 'bg-blue-50/50 text-gray-500' : 'text-gray-500'
                              } ${isClickable ? 'cursor-pointer hover:bg-blue-100/80' : 'cursor-default'}`}
                            >
                              <span className={`font-bold w-3 shrink-0 ${isSelected ? 'text-white' : (level >= 3 ? 'text-blue-400' : 'text-gray-400')}`}>{level}:</span>
                              <span className={`leading-tight flex-1 ${isSelected ? 'text-white' : 'text-gray-600'}`}>{item.criteria[level]}</span>
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {showSelf && (
                  <div className={`${selfColClass} p-3 border-b md:border-b-0 md:border-r bg-blue-50/10 flex flex-col gap-2`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500">Score</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold ${isSelfDisabled ? 'bg-gray-100 text-gray-500 border border-gray-200' : 'bg-white text-[#80A0E0] border border-blue-200'}`}>
                        {item.selfScore}
                      </span>
                    </div>
                    <div className="text-xs bg-blue-100 border border-blue-200 text-blue-800 px-2 py-1.5 rounded relative arrow-bottom">
                      <span className="font-bold mr-1">評価{item.selfScore}の目安:</span>{item.criteria?.[item.selfScore] || '基準未設定'}
                    </div>
                    <div className="text-xs font-bold text-gray-500">本人コメント</div>
                    {!evaluatorOnly && behaviorViewMode === 'self' ? (
                      <textarea
                        disabled={isSelfDisabled}
                        value={item.selfComment}
                        onChange={(e) => handleBehaviorChange(item.id, 'selfComment', e.target.value)}
                        placeholder="コメントを入力してください"
                        className={`w-full p-2 text-sm border rounded resize-none min-h-[64px] ${
                          isSelfDisabled
                            ? 'bg-gray-50 text-gray-600'
                            : 'bg-white focus:ring-1 focus:ring-gray-400 focus:border-gray-400'
                        }`}
                      />
                    ) : (
                      <div className="w-full p-2 text-sm border rounded bg-white text-gray-700 min-h-[64px] whitespace-pre-wrap break-words">
                        {item.selfComment}
                      </div>
                    )}
                  </div>
                )}
                {showEvaluator && (
                  <div className={`${evaluatorColClass} p-3 bg-gray-50/50 flex flex-col gap-2`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500">Score</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold ${isEvaluatorDisabled ? 'bg-gray-100 text-gray-500 border border-gray-200' : 'bg-white text-slate-700 border border-slate-200'}`}>
                        {item.evaluatorScore}
                      </span>
                    </div>
                    <div className="text-xs bg-slate-100 border border-slate-200 text-slate-700 px-2 py-1.5 rounded">
                      <span className="font-bold mr-1">評価{item.evaluatorScore}の目安:</span>{item.criteria?.[item.evaluatorScore] || '基準未設定'}
                    </div>
                    <textarea
                      disabled={isEvaluatorDisabled}
                      value={item.evaluatorComment}
                      onChange={(e) => handleBehaviorChange(item.id, 'evaluatorComment', e.target.value)}
                      placeholder={(behaviorViewMode !== 'evaluator' && !evaluatorOnly) ? '（上長評価入力待ち）' : 'コメントを入力してください'}
                      className={`w-full p-2 text-sm border rounded resize-none flex-1 min-h-[64px] ${isEvaluatorDisabled ? 'bg-gray-50 text-gray-600' : 'bg-white focus:ring-1 focus:ring-gray-400 focus:border-gray-400'}`}
                    />
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>

        {(behaviorViewMode === 'evaluator' || evaluatorOnly) && (
          <Card>
            <div className="mb-2 text-sm font-bold text-gray-700">上長所見</div>
            <textarea
              disabled={isEvaluatorTargetLocked}
              value={overallFeedback}
              onChange={(e) => setOverallFeedback(e.target.value)}
              placeholder="上長所見を入力してください。"
              className={`w-full p-3 border rounded text-sm min-h-[120px] outline-none ${
                isEvaluatorTargetLocked
                  ? 'bg-gray-50 text-gray-600'
                  : 'focus:ring-2 focus:ring-indigo-200'
              }`}
            />
            <div className="mt-4">
              <div className="mb-2 text-sm font-bold text-gray-700">役職ポイント</div>
              <input
                type="number"
                min={0}
                max={30}
                disabled={isEvaluatorTargetLocked}
                value={evaluatorRolePoint}
                onChange={(e) => setEvaluatorRolePoint(Number(e.target.value || 0))}
                className={`w-full rounded border p-3 text-sm outline-none md:w-48 ${
                  isEvaluatorTargetLocked
                    ? 'bg-gray-50 text-gray-600'
                    : 'focus:ring-2 focus:ring-indigo-200'
                }`}
              />
            </div>
          </Card>
        )}

        <div className="fixed inset-x-4 bottom-4 z-40 flex flex-col gap-3 border bg-white p-3 shadow-lg sm:left-6 sm:right-6 sm:flex-row sm:items-center sm:justify-end sm:gap-6 sm:p-4 md:left-[calc(15rem+2rem)] md:right-8 lg:left-[calc(18rem+2rem)]">
          {showEvaluator && (
            <div className="sm:mr-auto sm:max-w-xl text-left">
              <span className="block text-xs text-gray-400 mb-1">上長所見</span>
              <p className="max-h-16 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap break-words">
                {overallFeedback?.trim() ? overallFeedback : '未入力'}
              </p>
            </div>
          )}
          {showSelf && (
            <div className="text-right"><span className="block text-xs text-gray-400">本人評価 合計</span><span className="text-lg font-bold text-[#80A0E0]">{formatOneDecimal(behaviorData.reduce((acc, curr) => acc + calculateBehaviorWeightedScore(Number(curr.selfScore || 0), Number(curr.weight || 0)), 0))}</span></div>
          )}
          {showEvaluator && (
            <div className="text-right"><span className="block text-xs text-gray-400">上長評価 合計</span><span className="text-lg font-bold text-slate-700">{formatOneDecimal(behaviorData.reduce((acc, curr) => acc + calculateBehaviorWeightedScore(Number(curr.evaluatorScore || 0), Number(curr.weight || 0)), 0))}</span></div>
          )}
          {behaviorViewMode === 'self' && (
            <button
              type="button"
              onClick={handleSaveSelfBehaviorEvaluation}
              disabled={isFinalized || isBehaviorEvaluationSaving}
              className="w-full sm:w-auto px-5 py-2 bg-indigo-600 text-white rounded-md text-sm font-bold shadow-md hover:bg-indigo-700 disabled:opacity-50"
            >
              保存
            </button>
          )}
          {(behaviorViewMode === 'evaluator' || evaluatorOnly) && (
            <button
              type="button"
              onClick={handleSaveEvaluatorBehaviorEvaluation}
              disabled={isEvaluatorTargetLocked || isBehaviorEvaluationSaving || !selectedTargetUserId}
              className="w-full sm:w-auto px-5 py-2 bg-indigo-600 text-white rounded-md text-sm font-bold shadow-md hover:bg-indigo-700 disabled:opacity-50"
            >
              保存
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isAdmin) {
      switch (activeTab) {
        case 'admin_schedule': return renderAdminSchedule();
        case 'admin_users': return renderAdminUsers();
        case 'admin_targets': return renderAdminTargets(); 
        case 'admin_performance': return renderAdminPerformance();
        case 'admin_behavior_eval': return renderBehavior({ evaluatorOnly: true, showPeriodOnly: true });
        case 'admin_behavior': return renderAdminBehavior();
        case 'admin_adjustment': return renderAdminStoreAdjustment();
        case 'admin_approvals': return renderAdminApprovals();
        case 'admin_reports': return renderAdminReports();
        default: return renderAdminPerformance();
      }
    } else {
      switch (activeTab) {
        case 'dashboard': return renderDashboard();
        case 'performance': return renderPerformance();
        case 'behavior': return renderBehavior();
        case 'adjustment': return renderAdjustment();
        case 'daily_report': return renderDailyReport();
        case 'final': return renderFinal();
        default: return renderDashboard();
      }
    }
  };

  const tabsToRender = isAdmin
    ? ADMIN_TABS
    : EMPLOYEE_TABS.filter(
        (tab) =>
          !PERIOD_LIMITED_EMPLOYEE_TAB_IDS.has(tab.id) || isEvaluationInputWindowOpen,
      );
  const shouldUseFullWidthContent = isAdmin && activeTab === 'admin_behavior_eval';
  const approvalLoadingSubmission = approvalActionLoadingKey
    ? submittedEvaluations.find((row) => row.submissionKey === approvalActionLoadingKey) ?? null
    : null;
  const renderUserActionPanel = () => (
    <div data-user-action-menu="true" className="relative border-t border-gray-100 pt-4">
      {showUserActionMenu ? (
        <div className="absolute bottom-full left-0 right-0 mb-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          {canOpenSafrieSso ? (
            <button
              type="button"
              onClick={() => {
                void handleOpenSafrieSso();
              }}
              disabled={isSafrieSsoLoading}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSafrieSsoLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ExternalLink size={14} />
              )}
              {isSafrieSsoLoading ? '遷移準備中...' : '修正依頼を出す'}
            </button>
          ) : null}
          {showRoleToggle ? (
            <button
              type="button"
              onClick={() => {
                setShowUserActionMenu(false);
                toggleRole();
              }}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-bold text-gray-700 hover:bg-gray-50"
            >
              {isAdmin ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              {isAdmin ? '社員モードへ' : '管理モードへ'}
            </button>
          ) : null}
          {onLogout ? (
            <button
              type="button"
              onClick={() => {
                setShowUserActionMenu(false);
                setShowLogoutConfirmModal(true);
              }}
              className="flex w-full items-center gap-2 bg-red-50 px-4 py-3 text-left text-sm font-bold text-red-600 hover:bg-red-100"
            >
              <Lock size={14} />
              ログアウト
            </button>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setShowUserActionMenu((prev) => !prev)}
        className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-gray-50"
        aria-expanded={showUserActionMenu}
      >
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-700">{activeDisplayUserName}</span>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${activeDisplayRoleBadgeClass}`}>
          {activeDisplayRoleBadgeName}
        </span>
      </button>
    </div>
  );

  return (
    <div className="h-screen overflow-hidden font-sans flex flex-col" style={{ backgroundColor: COLORS.bg, color: COLORS.text }}>
      <AppConfirmDialog
        isOpen={showConfirmModal}
        title="最終確定の確認"
        message={
          <>
            評価を確定すると、すべての項目がロックされ修正できなくなります。
            <br />
            入力内容に間違いがないか、もう一度確認してください。
          </>
        }
        confirmText="確定して送信"
        cancelText="キャンセル"
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleFinalize}
      >
        <div className="bg-gray-50 p-4 rounded-lg text-sm border border-gray-100">
          <div className="flex justify-between mb-1">
            <span className="text-gray-500 font-bold">最終スコア:</span>
            <span className="font-bold text-xl text-indigo-600">{formatOneDecimal(totalScore)} pt</span>
          </div>
        </div>
      </AppConfirmDialog>
      <AppConfirmDialog
        isOpen={alertDeleteTargetId !== null}
        title="アラート削除の確認"
        message="このアラートを削除します。よろしいですか？"
        confirmText="削除する"
        cancelText="キャンセル"
        tone="danger"
        onCancel={handleCancelDeleteAlertSetting}
        onConfirm={handleConfirmDeleteAlertSetting}
      />
      <AppConfirmDialog
        isOpen={showEditDeleteConfirmModal}
        title="ユーザー削除の確認"
        message="このユーザーを削除します。よろしいですか？"
        confirmText="削除する"
        cancelText="キャンセル"
        tone="danger"
        onCancel={handleCancelDeleteEditingUser}
        onConfirm={handleConfirmDeleteEditingUser}
      />
      <AppConfirmDialog
        isOpen={evaluatorDeleteTargetId !== null}
        title="評価者解除の確認"
        message="この評価者設定を解除します。よろしいですか？"
        confirmText="解除する"
        cancelText="キャンセル"
        tone="danger"
        onCancel={handleCancelRemoveEvaluator}
        onConfirm={handleConfirmRemoveEvaluator}
      />
      <AppConfirmDialog
        isOpen={storeDeleteTarget !== null}
        title="店舗削除の確認"
        message="この店舗を削除します。よろしいですか？"
        confirmText="削除する"
        cancelText="キャンセル"
        tone="danger"
        onCancel={handleCancelDeleteStore}
        onConfirm={handleConfirmDeleteStore}
      />
      <AppConfirmDialog
        isOpen={genericConfirmState !== null}
        title={genericConfirmState?.title ?? ''}
        message={genericConfirmState?.message ?? ''}
        confirmText={genericConfirmState?.confirmText ?? '実行する'}
        cancelText="キャンセル"
        tone={genericConfirmState?.tone ?? 'danger'}
        onCancel={() => setGenericConfirmState(null)}
        onConfirm={async () => {
          const currentConfirm = genericConfirmState;
          setGenericConfirmState(null);
          if (!currentConfirm) return;
          await currentConfirm.onConfirm();
          setGenericConfirmState(null);
        }}
      />
      <AppConfirmDialog
        isOpen={showLogoutConfirmModal}
        title="ログアウト確認"
        message="ログアウトします。よろしいですか？"
        confirmText="ログアウト"
        cancelText="キャンセル"
        onCancel={() => setShowLogoutConfirmModal(false)}
        onConfirm={async () => {
          setShowLogoutConfirmModal(false);
          await onLogout?.();
        }}
      />
      {approvalActionLoadingKey ? (
        <div className="fixed inset-0 z-[365] bg-black/45 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-2xl">
            <div className="px-6 py-5">
              <h3 className="text-base font-bold text-gray-800 mb-2">AIコメントを生成しています</h3>
              <p className="text-xs text-gray-500 mb-3">
                {approvalLoadingSubmission?.userName ?? '対象ユーザー'} の承認データを作成中です。
              </p>
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <Loader2 size={18} className="animate-spin" />
                <span>完了までお待ちください...</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showDashboardPeriodModal ? (
        <div className="fixed inset-0 z-[350] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-xl bg-white border border-gray-200 shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800">表示期間を選択</h3>
              <p className="mt-1 text-sm text-gray-500">
                ダッシュボードに表示する期と上期/下期を選択してください。
              </p>
            </div>
            {!dashboardHistoryPreview ? (
              <div className="px-5 py-4 space-y-3">
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-xs text-indigo-800 font-bold">
                  1. 期を選択 → 2. 区分を選択 → 3. 「表示する」で過去分を表示
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block rounded-lg border border-gray-200 bg-white p-3">
                    <span className="text-xs font-bold text-gray-600">期</span>
                    <select
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      value={dashboardTermDraft}
                      onChange={(event) => {
                        const nextTermNo = event.target.value ? Number(event.target.value) : '';
                        setDashboardTermDraft(nextTermNo);
                        if (nextTermNo === '') {
                          setDashboardPeriodDraft('first_half');
                          return;
                        }
                        const nextPeriods = getSelectablePastPeriodsForTerm(nextTermNo);
                        if (nextPeriods.length === 0) {
                          setDashboardPeriodDraft('first_half');
                        } else if (!nextPeriods.includes(dashboardPeriodDraft)) {
                          setDashboardPeriodDraft(nextPeriods[0]);
                        }
                      }}
                    >
                      <option value="">選択してください</option>
                      {pastTermOptions.map((term) => (
                        <option key={`dashboard-term-${term.termNo}`} value={term.termNo}>
                          第{term.termNo}期
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block rounded-lg border border-gray-200 bg-white p-3">
                    <span className="text-xs font-bold text-gray-600">区分</span>
                    <select
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100 disabled:text-gray-400"
                      value={dashboardPeriodDraft}
                      disabled={dashboardTermDraft === '' || selectablePastPeriods.length === 0}
                      onChange={(event) => {
                        const nextPeriod = event.target.value as ScheduleQuarter;
                        setDashboardPeriodDraft(nextPeriod);
                      }}
                    >
                      {dashboardTermDraft === '' ? (
                        <option value="first_half">先に期を選択してください</option>
                      ) : selectablePastPeriods.length === 0 ? (
                        <option value="first_half">選択可能な過去区分がありません</option>
                      ) : (
                        selectablePastPeriods.map((period) => (
                          <option key={`dashboard-period-${period}`} value={period}>
                            {quarterLabelMap[period]}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                </div>
              </div>
            ) : null}
            <div className="px-5 pb-4">
              {isDashboardHistoryLoading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                  ダッシュボードを読み込んでいます...
                </div>
              ) : dashboardHistoryErrorMessage ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {dashboardHistoryErrorMessage}
                </div>
              ) : dashboardHistoryPreview ? (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
                  <div className="text-sm font-bold text-indigo-800">
                    第{dashboardHistoryPreview.termNo}期 {quarterLabelMap[dashboardHistoryPreview.period]} の結果
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-[11px] text-gray-500 font-bold">店舗業績</div>
                      <div className="text-xl font-bold text-gray-800">{formatOneDecimal(Number(dashboardHistoryPreview.performanceScore ?? 0))}</div>
                    </div>
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-[11px] text-gray-500 font-bold">行動評価</div>
                      <div className="text-xl font-bold text-gray-800">{formatOneDecimal(Number(dashboardHistoryPreview.behaviorScore ?? 0))}</div>
                    </div>
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-[11px] text-gray-500 font-bold">加減点</div>
                      <div className="text-xl font-bold text-gray-800">{formatOneDecimal(Number(dashboardHistoryPreview.adjustmentScore ?? 0))}</div>
                    </div>
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-[11px] text-gray-500 font-bold">役職ポイント</div>
                      <div className="text-xl font-bold text-gray-800">{formatOneDecimal(Number(dashboardHistoryPreview.executiveAdjustmentScore ?? 0))}</div>
                    </div>
                  </div>
                  <div className="rounded-md border border-indigo-200 bg-white p-3 text-center">
                    <div className="text-xs text-indigo-700 font-bold">総合評価スコア</div>
                    <div className="text-3xl font-black text-indigo-700">{formatOneDecimal(Number(dashboardHistoryPreview.totalScore ?? 0))}</div>
                    {(() => {
                      const computedHistoryRank = [...rankThresholds]
                        .sort((a, b) => Number(b.minScore) - Number(a.minScore))
                        .find((item) => Number(dashboardHistoryPreview.totalScore ?? 0) >= Number(item.minScore))?.rank ?? null;
                      const historyRankLabel = dashboardHistoryPreview.rank ?? computedHistoryRank;
                      return historyRankLabel ? (
                        <div className="mt-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700">
                          ランク {historyRankLabel}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-600 font-bold mb-2">店舗業績管理</div>
                      {dashboardHistoryPerformanceRows.length > 0 ? (
                        <svg viewBox="0 0 520 220" className="w-full h-[220px]">
                          <line x1="40" y1="190" x2="500" y2="190" stroke="#e2e8f0" strokeWidth="2" />
                          {dashboardHistoryPerformanceRows.map((item, idx) => {
                            const barWidth = 60;
                            const gap = 22;
                            const x = 55 + idx * (barWidth + gap);
                            const maxScore = Math.max(
                              ...dashboardHistoryPerformanceRows.map((d) => d.score),
                              1,
                            );
                            const height = (item.score / maxScore) * 130;
                            const y = 190 - height;
                            return (
                              <g key={`history-perf-svg-${item.id}`}>
                                <rect x={x} y={y} width={barWidth} height={height} rx="6" fill="#6366f1" opacity={0.85} />
                                <text x={x + barWidth / 2} y={y - 8} textAnchor="middle" fontSize="11" fill="#475569">
                                  {formatOneDecimal(Number(item.score ?? 0))}
                                </text>
                                <text x={x + barWidth / 2} y="208" textAnchor="middle" fontSize="10" fill="#64748b">
                                  {item.item}
                                </text>
                              </g>
                            );
                          })}
                        </svg>
                      ) : (
                        <div className="text-xs text-gray-500">店舗業績データがありません。</div>
                      )}
                    </div>
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                      <div className="text-xs text-gray-600 font-bold mb-2">行動・技能・知識評価シート</div>
                      {dashboardHistoryBehaviorRows.length > 0 ? (
                        <svg viewBox="0 0 220 220" className="w-full h-[220px]">
                          {[0.25, 0.5, 0.75, 1].map((ratio, idx) => (
                            <circle key={ratio} cx={110} cy={110} r={90 * ratio} fill="none" stroke="#e2e8f0" strokeWidth={idx === 3 ? 2 : 1} />
                          ))}
                          {dashboardHistoryBehaviorRows.map((item, idx) => {
                            const angle = (Math.PI * 2 * idx) / dashboardHistoryBehaviorRows.length - Math.PI / 2;
                            const x = 110 + Math.cos(angle) * 90;
                            const y = 110 + Math.sin(angle) * 90;
                            return <line key={`history-radar-axis-${item.id}`} x1={110} y1={110} x2={x} y2={y} stroke="#e2e8f0" />;
                          })}
                          <polygon
                            points={dashboardHistoryBehaviorRows
                              .map((item, idx) => {
                                const angle =
                                  (Math.PI * 2 * idx) / dashboardHistoryBehaviorRows.length - Math.PI / 2;
                                const ratio = Number(item.selfScore || 0) / 4;
                                return `${110 + Math.cos(angle) * 90 * ratio},${110 + Math.sin(angle) * 90 * ratio}`;
                              })
                              .join(' ')}
                            fill="rgba(59,130,246,0.35)"
                            stroke="#3b82f6"
                            strokeWidth="2"
                          />
                          <polygon
                            points={dashboardHistoryBehaviorRows
                              .map((item, idx) => {
                                const angle =
                                  (Math.PI * 2 * idx) / dashboardHistoryBehaviorRows.length - Math.PI / 2;
                                const ratio = Number(item.evaluatorScore || 0) / 4;
                                return `${110 + Math.cos(angle) * 90 * ratio},${110 + Math.sin(angle) * 90 * ratio}`;
                              })
                              .join(' ')}
                            fill="rgba(16,185,129,0.25)"
                            stroke="#10b981"
                            strokeWidth="2"
                          />
                        </svg>
                      ) : (
                        <div className="text-xs text-gray-500">行動評価データがありません。</div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="text-xs text-gray-600 font-bold mb-2">
                      総合評価 AI分析コメント（第{dashboardHistoryPreview.termNo}期 {quarterLabelMap[dashboardHistoryPreview.period]}）
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                        <h4 className="text-lg font-bold text-slate-800 mb-2">総合所見</h4>
                        <div className="text-slate-700">
                          {renderAiCommentWithBold(
                            dashboardHistoryPreview.aiSummary || 'AIコメントはまだ生成されていません。',
                            'text-slate-900',
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4">
                        <h4 className="text-lg font-bold text-emerald-800 mb-2">強み</h4>
                        <div className="text-emerald-900">
                          {renderAiCommentWithBold(
                            dashboardHistoryPreview.aiStrength || 'AIコメントはまだ生成されていません。',
                            'text-emerald-950',
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
                        <h4 className="text-lg font-bold text-amber-800 mb-2">改善提案</h4>
                        <div className="text-amber-900">
                          {renderAiCommentWithBold(
                            dashboardHistoryPreview.aiImprovement || 'AIコメントはまだ生成されていません。',
                            'text-amber-950',
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                  期と区分を選択して「表示する」を押すと、ここに結果を表示します。
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              {dashboardHistoryPreview ? (
                <button
                  type="button"
                  onClick={() => setDashboardHistoryPreview(null)}
                  className="px-4 py-2 text-sm font-bold rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                >
                  条件を変更
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setShowDashboardPeriodModal(false)}
                className="px-4 py-2 text-sm font-bold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
              >
                キャンセル
              </button>
              {!dashboardHistoryPreview ? (
                <button
                  type="button"
                  onClick={() =>
                    void applyDashboardPeriodSelection(dashboardTermDraft, dashboardPeriodDraft)
                  }
                  disabled={
                    isDashboardHistoryLoading ||
                    dashboardTermDraft === '' ||
                    selectablePastPeriods.length === 0
                  }
                  className="px-4 py-2 text-sm font-bold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
                >
                  表示する
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {approvalMissingRequirementModal.isOpen ? (
        <div className="fixed inset-0 z-[360] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-800">承認できません</h3>
              <p className="mt-1 text-sm text-gray-500">
                {approvalMissingRequirementModal.userName} さんの評価で、以下の入力が不足しています。
              </p>
            </div>
            <div className="px-5 py-4">
              {approvalMissingRequirementModal.isLoading ? (
                <div className="text-sm text-gray-500">不足項目を確認しています...</div>
              ) : approvalMissingRequirementModal.errorMessage ? (
                <div className="text-sm text-red-600">{approvalMissingRequirementModal.errorMessage}</div>
              ) : approvalMissingRequirementModal.items.length === 0 ? (
                <div className="text-sm text-gray-600">
                  不足項目は見つかりませんでした。画面を再読み込みして再度お試しください。
                </div>
              ) : (
                <ul className="space-y-2 text-sm text-gray-700">
                  {approvalMissingRequirementModal.items.map((item) => (
                    <li key={item} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() =>
                  setApprovalMissingRequirementModal({
                    isOpen: false,
                    userName: '',
                    items: [],
                    isLoading: false,
                    errorMessage: '',
                  })
                }
                className="px-4 py-2 text-sm font-bold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showFinalAiCommentModal ? (
        <div className="fixed inset-0 z-[260] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-2xl">
            <div className="px-6 py-5">
              <h3 className="text-base font-bold text-gray-800 mb-3">AIコメントを生成しています</h3>
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <Loader2 size={18} className="animate-spin" />
                <span>完了後にダッシュボードへ移動します...</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {selectedApprovalAiTarget ? (
        <div className="fixed inset-0 z-[360] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white border border-gray-200 shadow-2xl max-h-[80vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-800">総合評価 AI分析コメント</h3>
                <p className="mt-1 text-sm text-gray-500">{selectedApprovalAiTarget.userName}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedApprovalAiTarget(null)}
                className="text-gray-400 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-5 overflow-y-auto">
              {isApprovalAiCommentLoading ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 size={16} className="animate-spin" />
                  <span>AIコメントを読み込み中...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                    <h4 className="text-lg font-bold text-slate-800 mb-2">総合所見</h4>
                    <div className="text-slate-700">
                      {renderAiCommentWithBold(
                        approvalAiComment.summary || 'コメントはありません。',
                        'text-slate-900',
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4">
                    <h4 className="text-lg font-bold text-emerald-800 mb-2">強み</h4>
                    <div className="text-emerald-900">
                      {renderAiCommentWithBold(
                        approvalAiComment.strength || 'コメントはありません。',
                        'text-emerald-950',
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4">
                    <h4 className="text-lg font-bold text-amber-800 mb-2">改善提案</h4>
                    <div className="text-amber-900">
                      {renderAiCommentWithBold(
                        approvalAiComment.improvement || 'コメントはありません。',
                        'text-amber-950',
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedApprovalAiTarget(null)}
                className="px-4 py-2 text-sm font-bold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {selectedAdminReport ? (
        <div
          className="fixed inset-0 z-[340] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSelectedAdminReport(null)}
        >
          <div
            className="w-full max-w-2xl rounded-xl bg-white border border-gray-200 shadow-2xl max-h-[85vh] flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-800">日報詳細</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedAdminReport.reportDate} / {selectedAdminReport.userName || '不明'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAdminReport(null)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="日報詳細を閉じる"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-5 overflow-y-auto space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="text-xs font-bold text-slate-500 mb-2">更新日</div>
                <div className="text-sm text-slate-700">
                  {new Date(selectedAdminReport.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="text-xs font-bold text-slate-500 mb-2">本日行ったこと・気付き</div>
                <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {selectedAdminReport.todayWork || '記載なし'}
                </div>
              </div>
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-4">
                <div className="text-xs font-bold text-indigo-500 mb-2">次回の出勤で行うこと</div>
                <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {selectedAdminReport.nextTasks || '記載なし'}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {selectedApprovalDetail ? (
        (() => {
          const breakdown = getApprovalDetailBreakdown(selectedApprovalDetail);
          const performanceChartData =
            approvalDetailSection === 'performance' && approvalPerformanceDetail
              ? approvalPerformanceDetail.map((row) => ({
                  label: row.item,
                  score: row.score,
                  weight: row.weight,
                  points: row.points,
                  actual: row.actual,
                }))
              : [];
          const performanceMax = Math.max(...performanceChartData.map((d) => d.score), 1);
          const radarItems =
            approvalDetailSection === 'behavior' && approvalBehaviorDetail
              ? approvalBehaviorDetail.map((item) => ({
                  label: item.item,
                  self: Number(item.selfScore || 0),
                  evaluator: Number(item.evaluatorScore || 0),
                  weight: Number(item.weight || 1),
                  def: item.def,
                }))
              : [];
          const radarCount = radarItems.length || 1;
          const radarMax = 4;
          const radarCenter = 110;
          const radarRadius = 90;
          const radarAngle = (index: number) => (Math.PI * 2 * index) / radarCount - Math.PI / 2;
          const radarPoint = (value: number, index: number) => {
            const ratio = value / radarMax;
            const angle = radarAngle(index);
            return [
              radarCenter + Math.cos(angle) * radarRadius * ratio,
              radarCenter + Math.sin(angle) * radarRadius * ratio,
            ];
          };
          const radarPath = (key: 'self' | 'evaluator') =>
            radarItems.map((item, idx) => radarPoint(item[key], idx).join(',')).join(' ');
          const detailScoreSummary =
            approvalDetailSection === 'performance'
              ? {
                  label: '店舗業績スコア',
                  sublabel: 'Performance Score',
                  value:
                    !selectedApprovalDetail.hasSnapshot &&
                    approvalDetailSection === 'performance' &&
                    approvalPerformanceDetail
                      ? approvalPerformanceDetail.reduce(
                          (sum, row) => sum + Number(row.score ?? 0),
                          0,
                        )
                      : breakdown.performanceScore,
                }
              : approvalDetailSection === 'behavior'
                ? { label: '行動評価スコア', sublabel: 'Behavior Score', value: breakdown.behaviorScore }
                : approvalDetailSection === 'adjustment'
                  ? { label: '加点・減点', sublabel: 'Adjustment Score', value: breakdown.adjustmentScore }
                  : approvalDetailSection === 'executive'
                    ? { label: '役員調整ポイント', sublabel: 'Executive Adjustment', value: breakdown.executiveAdjustmentScore }
                    : { label: '総合評価スコア', sublabel: 'Total Score', value: breakdown.totalScore };
          return (
        <div className="fixed inset-0 z-[410] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="flex h-[min(760px,calc(100vh-32px))] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800">評価内訳</h3>
                <p className="mt-1 text-sm text-gray-500">{selectedApprovalDetail.userName}</p>
              </div>
              <div className="flex items-center gap-2">
                {approvalDetailSection !== 'summary' ? (
                  <button
                    type="button"
                    onClick={() => setApprovalDetailSection('summary')}
                    className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-100"
                  >
                    戻る
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedApprovalDetail(null);
                    setApprovalDetailSection('summary');
                    setApprovalPerformanceDetail(null);
                    setApprovalBehaviorDetail(null);
                  }}
                  className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  aria-label="閉じる"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-8">
              {approvalDetailSection === 'summary' ? (
                <div
                  className="space-y-2 rounded-md border bg-gray-50 p-6"
                  style={{ borderColor: COLORS.border }}
                >
                  <button
                    type="button"
                    onClick={() => openApprovalDetailSection('performance', selectedApprovalDetail)}
                    className="flex w-full items-center justify-between border-b border-gray-200 p-3 text-left transition-colors hover:bg-white"
                  >
                    <span className="font-medium text-gray-600">店舗業績スコア</span>
                    <span className="text-lg font-bold">{formatOneDecimal(Number(breakdown.performanceScore ?? 0))}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openApprovalDetailSection('behavior', selectedApprovalDetail)}
                    className="flex w-full items-center justify-between border-b border-gray-200 p-3 text-left transition-colors hover:bg-white"
                  >
                    <span className="font-medium text-gray-600">行動評価スコア</span>
                    <span className="text-lg font-bold">{formatOneDecimal(Number(breakdown.behaviorScore ?? 0))}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openApprovalDetailSection('adjustment', selectedApprovalDetail)}
                    className="flex w-full items-center justify-between p-3 text-left transition-colors hover:bg-white"
                  >
                    <span className="font-medium text-gray-600">加点・減点</span>
                    <span
                      className={`text-lg font-bold ${
                        breakdown.adjustmentScore < 0 ? 'text-[#D03020]' : ''
                      }`}
                    >
                      {formatOneDecimal(Number(breakdown.adjustmentScore ?? 0))}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openApprovalDetailSection('executive', selectedApprovalDetail)}
                    className="flex w-full items-center justify-between border-t border-gray-200 p-3 text-left transition-colors hover:bg-white"
                  >
                    <span className="font-medium text-gray-600">役員調整ポイント</span>
                    <span className="text-lg font-bold">
                      {formatOneDecimal(Number(breakdown.executiveAdjustmentScore ?? 0))}
                    </span>
                  </button>
                </div>
              ) : null}
              {approvalDetailSection === 'performance' ? (
                isApprovalSectionLoading ? (
                  <div className="rounded-md border bg-gray-50 p-8 text-sm text-gray-500">店舗業績の詳細を読み込んでいます。</div>
                ) : (
                  <div className="space-y-6">
                    <div className="rounded-md border bg-gray-50 p-4">
                      <svg viewBox="0 0 520 220" className="h-[220px] w-full">
                        <line x1="40" y1="190" x2="500" y2="190" stroke="#e2e8f0" strokeWidth="2" />
                        {performanceChartData.map((item, idx) => {
                          const barWidth = 60;
                          const gap = 22;
                          const x = 55 + idx * (barWidth + gap);
                          const height = (item.score / performanceMax) * 130;
                          const y = 190 - height;
                          return (
                            <g key={item.label}>
                              <rect x={x} y={y} width={barWidth} height={height} rx="6" fill="#6366f1" opacity={0.85} />
                              <text x={x + barWidth / 2} y={y - 8} textAnchor="middle" fontSize="11" fill="#475569">{formatOneDecimal(Number(item.score ?? 0))}</text>
                              <text x={x + barWidth / 2} y="208" textAnchor="middle" fontSize="10" fill="#64748b">{item.label}</text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 text-xs text-gray-600">
                      {performanceChartData.map((item) => (
                        <div key={item.label} className="rounded-md border bg-white p-3">
                          <div className="font-bold text-gray-700">{item.label}</div>
                          <div className="text-gray-500">実績: {item.actual}</div>
                          <div className="mt-2 flex items-center justify-between">
                            <span>評価 {item.points}</span>
                            <span className="font-bold text-indigo-600">加重点 {formatOneDecimal(Number(item.score ?? 0))}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ) : null}
              {approvalDetailSection === 'behavior' ? (
                isApprovalSectionLoading ? (
                  <div className="rounded-md border bg-gray-50 p-8 text-sm text-gray-500">行動・技能評価の詳細を読み込んでいます。</div>
                ) : (
                  <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-[240px_1fr]">
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <svg viewBox="0 0 220 220" className="h-[220px] w-full">
                        {[0.25, 0.5, 0.75, 1].map((ratio, idx) => (
                          <circle key={ratio} cx={radarCenter} cy={radarCenter} r={radarRadius * ratio} fill="none" stroke="#e2e8f0" strokeWidth={idx === 3 ? 2 : 1} />
                        ))}
                        {radarItems.map((item, idx) => {
                          const [x, y] = radarPoint(radarMax, idx);
                          return <line key={item.label} x1={radarCenter} y1={radarCenter} x2={x} y2={y} stroke="#e2e8f0" />;
                        })}
                        <polygon points={radarPath('self')} fill="rgba(59,130,246,0.35)" stroke="#3b82f6" strokeWidth="2" />
                        <polygon points={radarPath('evaluator')} fill="rgba(16,185,129,0.25)" stroke="#10b981" strokeWidth="2" />
                        {radarItems.map((item, idx) => {
                          const [x, y] = radarPoint(item.self, idx);
                          return <circle key={`${item.label}-self`} cx={x} cy={y} r="3.5" fill="#3b82f6" />;
                        })}
                        {radarItems.map((item, idx) => {
                          const [x, y] = radarPoint(item.evaluator, idx);
                          return <circle key={`${item.label}-eval`} cx={x} cy={y} r="3.5" fill="#10b981" />;
                        })}
                      </svg>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-blue-500"></span>本人自己評価</span>
                        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-500"></span>評価者評価</span>
                      </div>
                      <div className="rounded-md border bg-gray-50 p-2 text-[11px] text-gray-500">
                        内訳の順番: {radarItems.map((item, idx) => `${idx + 1}.${item.label}`).join(' / ')}
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-xs text-gray-600">
                        {radarItems.map((item) => (
                          <div key={item.label} className="rounded-md border bg-white p-3">
                            <div className="font-bold text-gray-700">{item.label}</div>
                            <div className="mt-1 text-[11px] text-gray-500">{item.def}</div>
                            <div className="mt-2 flex items-center justify-between">
                              <span className="font-bold text-blue-600">自己 {item.self}</span>
                              <span className="font-bold text-emerald-600">評価 {item.evaluator}</span>
                            </div>
                            <div className="mt-1 text-[10px] text-gray-500">ウェイト {item.weight}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              ) : null}
              {approvalDetailSection === 'adjustment' ? (
                isApprovalSectionLoading ? (
                  <div className="rounded-md border bg-gray-50 p-8 text-sm text-gray-500">加点・減点の詳細を読み込んでいます。</div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-md border bg-gray-50 p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="text-sm font-bold text-gray-700">加点・減点の詳細</div>
                      </div>
                      {approvalAdjustmentDetail && approvalAdjustmentDetail.length > 0 ? (
                        <div className="space-y-3">
                          {approvalAdjustmentDetail.map((item) => (
                            <div key={item.id} className="rounded-md border bg-white p-3 text-sm">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="font-bold text-gray-800">{item.item}</div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    {item.category === 'plus' ? '加点' : '減点'} / 単価 {item.unitPoint}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-2 text-xs text-gray-500">
                                    件数
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.count}
                                      readOnly
                                      disabled
                                      className="w-16 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-right text-sm font-bold text-gray-700"
                                    />
                                  </label>
                                  <div className={`min-w-12 text-right text-lg font-bold ${item.score < 0 ? 'text-[#D03020]' : 'text-indigo-600'}`}>
                                    {formatOneDecimal(Number(item.score ?? 0))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">加点・減点の明細はありません。</div>
                      )}
                    </div>
                  </div>
                )
              ) : null}
              {approvalDetailSection === 'executive' ? (
                <div className="rounded-md border bg-gray-50 p-8 text-center">
                  <div className="text-sm text-gray-500">役員調整ポイントの詳細</div>
                  <div className="mt-3 text-4xl font-bold text-gray-800">
                    {formatOneDecimal(Number(breakdown.executiveAdjustmentScore ?? 0))}
                  </div>
                </div>
              ) : null}
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-5">
              <div className="flex items-center justify-between rounded-md bg-[#303030] p-6 text-white shadow-md">
                <div>
                  <span className="mb-1 block text-sm text-gray-400">{detailScoreSummary.label}</span>
                  <span className="text-xs uppercase tracking-widest text-gray-500">
                    {detailScoreSummary.sublabel}
                  </span>
                </div>
                <div className="text-5xl font-bold tracking-tight">
                  {formatOneDecimal(Number(detailScoreSummary.value ?? 0))}{' '}
                  <span className="text-lg font-normal text-gray-400">pt</span>
                </div>
              </div>
            </div>
          </div>
        </div>
          );
        })()
      ) : null}
      <AppToast notification={appNotification} visible={isAppNotificationVisible} />
      <header className="sticky top-0 z-30 shadow-sm transition-colors duration-300 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><div className="h-9 w-auto flex items-center"><img src="/assets/fipro-logo.png" alt="Logo" className="h-full object-contain" /></div><div className="h-6 w-px bg-gray-200 hidden sm:block"></div><h1 className="text-sm font-bold tracking-wide text-gray-700 hidden sm:block">人事評価システム</h1></div>
          <div className="flex items-center gap-4">
            {showNavigation && !isAdmin && (
              <button
                type="button"
                onClick={() => setShowMobileMenu(true)}
                className="md:hidden p-2 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
              >
                <img
                  src="https://web-de-asobo.net/wp-content/uploads/2022/06/ham-menu-image01.png"
                  alt="Menu"
                  className="w-[18px] h-[18px] object-contain"
                />
              </button>
            )}
          </div>
        </div>
      </header>
      {showNavigation && isAdmin ? (
        <div className="md:hidden max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-2">
          <nav className="flex space-x-7 overflow-x-auto no-scrollbar">
            {tabsToRender.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isDisabled = isEmployeeTabDisabledByRule(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  disabled={isDisabled}
                  className={`group flex items-center gap-2 py-3.5 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${isActive ? 'text-indigo-600 border-indigo-600' : 'text-gray-500 border-transparent hover:text-gray-800 hover:border-gray-300'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Icon
                    size={17}
                    className={`transition-colors ${isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          {renderUserActionPanel()}
        </div>
      ) : null}

      {showNavigation && !isAdmin && showMobileMenu && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileMenu(false)} />
          <div className="absolute right-0 top-0 flex h-full w-72 flex-col bg-white shadow-xl border-l border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-gray-700">メニュー</span>
              <button type="button" onClick={() => setShowMobileMenu(false)} className="p-2 rounded-md hover:bg-gray-100 text-gray-500">
                <X size={18} />
              </button>
            </div>
            <nav className="min-h-0 flex-1 space-y-2 overflow-y-auto">
              {tabsToRender.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isDisabled = isEmployeeTabDisabledByRule(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => { if (!isDisabled) handleTabChange(tab.id); setShowMobileMenu(false); }}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-sm font-bold transition-all ${
                      isActive ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Icon size={18} className={`${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
            {renderUserActionPanel()}
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 w-full flex overflow-hidden">
        {showNavigation && (
          <aside className="hidden md:flex w-60 lg:w-72 h-full flex-col border-r border-gray-200 bg-white/70 backdrop-blur-sm">
            <nav className="min-h-0 flex-1 overflow-y-auto p-5 space-y-2">
              {tabsToRender.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isDisabled = isEmployeeTabDisabledByRule(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 rounded-md px-3 py-3 text-base font-bold transition-all ${isActive ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Icon size={18} className={`${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
            <div className="p-5">
              {renderUserActionPanel()}
            </div>
          </aside>
        )}
        <main ref={mainScrollContainerRef} className={`flex-1 h-full overflow-y-auto w-full px-4 sm:px-6 md:px-8 pt-8 sm:pt-10 flex items-start ${isAdmin ? 'pb-16 sm:pb-20' : 'pb-16 sm:pb-20'}`}>
          <div className={shouldUseFullWidthContent ? 'w-full' : 'max-w-7xl mx-auto w-full'}>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}
